import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  ImageRun,
  ExternalHyperlink,
  WidthType,
  BorderStyle,
  Tab,
  TabStopType,
  TabStopPosition,
  Table,
  TableRow,
  TableCell,
  VerticalAlign,
} from "docx";
import { saveAs } from "file-saver";

const StimFormReportDocx = async (data) => {
  const {
    firstName,
    lastName,
    age,
    ageGroup,
    gender,
    program,
    isReevaluation,
    isVirtual,
    evaluationDate,
    sections,
  } = data;

  const genderSubjectPronoun = gender === "Male" ? "he" : "she";
  const genderAdjective = gender === "Male" ? "his" : "her";
  const genderObjectAdjective = gender === "Male" ? "him" : "her";

  const doc = new Document({
    styles: {
      paragraphStyles: [
        {
          id: "Normal",
          name: "Normal",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: {
            font: "Calibri",
            size: 22, // 11pt - slightly smaller for better density
            color: "2C3E50", // Professional dark blue-gray
          },
          paragraph: {
            spacing: {
              line: 300, // Improved line spacing for readability
              after: 120, // Consistent spacing after paragraphs
            },
            indent: {
              left: 0,
              right: 0,
            },
          },
        },
        {
          id: "Heading1",
          name: "Heading 1",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: {
            font: "Calibri",
            size: 36, // 18pt - more balanced size
            bold: true,
            color: "1B365D", // Professional dark blue
            allCaps: true,
          },
          paragraph: {
            spacing: {
              before: 200, // Reduced for better flow
              after: 160, // Increased for better separation
              line: 360, // Better line spacing
            },
            alignment: AlignmentType.CENTER,
            border: {
              top: {
                color: "1B365D",
                space: 15,
                style: BorderStyle.SINGLE,
                size: 6,
              },
              bottom: {
                color: "1B365D",
                space: 15,
                style: BorderStyle.SINGLE,
                size: 6,
              },
            },
            shading: {
              fill: "F8F9FA", // Subtle light background
            },
          },
        },
        {
          id: "Heading2",
          name: "Heading 2",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: {
            font: "Calibri",
            size: 30, // 15pt - more proportional
            bold: true,
            color: "2980B9", // Professional blue
          },
          paragraph: {
            spacing: {
              before: 280, // Better spacing
              after: 140, // Improved separation
              line: 340, // Better line spacing
            },
            border: {
              left: {
                color: "2980B9",
                space: 20,
                style: BorderStyle.SINGLE,
                size: 8, // Slightly thinner for elegance
              },
            },
            indent: {
              left: 200, // Reduced for better alignment
            },
            shading: {
              fill: "FBFCFD", // Subtle light blue background
            },
          },
        },
        {
          id: "Heading3",
          name: "Heading 3",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: {
            font: "Calibri",
            size: 26, // 13pt - more proportional
            bold: true,
            color: "34495E", // Professional dark gray-blue
          },
          paragraph: {
            spacing: {
              before: 200, // Better spacing
              after: 100, // Improved separation
              line: 300, // Better line spacing
            },
            indent: {
              left: 300, // Better alignment
            },
            border: {
              bottom: {
                color: "BDC3C7",
                space: 8,
                style: BorderStyle.SINGLE,
                size: 3, // Slightly thicker for better visibility
              },
            },
          },
        },
        {
          id: "BodyText",
          name: "Body Text",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: {
            font: "Calibri",
            size: 22, // 11pt - consistent with Normal
            color: "2C3E50", // Professional dark blue-gray
          },
          paragraph: {
            spacing: {
              before: 80, // Better spacing
              after: 80, // Consistent spacing
              line: 280, // Improved line spacing for readability
            },
            indent: {
              left: 300, // Better alignment with headings
              right: 200, // Consistent margins
            },
            alignment: AlignmentType.JUSTIFIED,
          },
        },
        {
          id: "PatientInfo",
          name: "Patient Info",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: {
            font: "Calibri",
            size: 24, // 12pt - more readable
            color: "2C3E50", // Professional color
          },
          paragraph: {
            spacing: {
              before: 100, // Better spacing
              after: 100, // Consistent spacing
              line: 320, // Improved line spacing
            },
            indent: {
              left: 150, // Better alignment
              right: 150, // Consistent margins
            },
            alignment: AlignmentType.LEFT,
            border: {
              left: {
                color: "E8E8E8",
                space: 15,
                style: BorderStyle.SINGLE,
                size: 6, // Slightly thicker for better visibility
              },
            },
            shading: {
              fill: "FAFAFA", // Subtle light background
            },
            tabs: [
              new Tab({
                type: TabStopType.LEFT,
                position: TabStopPosition.MAX,
              }),
            ],
          },
        },
        {
          id: "HyperlinkStyle",
          name: "Hyperlink Style",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: {
            font: "Calibri",
            size: 24, // 12pt
            color: "2980B9", // Enhanced blue for links
            underline: {
              type: "single",
              color: "2980B9",
            },
            bold: true,
          },
          paragraph: {
            spacing: {
              before: 0,
              after: 120,
              line: 320,
            },
            alignment: AlignmentType.CENTER,
            border: {
              left: {
                color: "E8F4FD",
                space: 10,
                style: BorderStyle.SINGLE,
                size: 4,
              },
              right: {
                color: "E8F4FD",
                space: 10,
                style: BorderStyle.SINGLE,
                size: 4,
              },
            },
          },
        },
        {
          id: "Hyperlink",
          name: "Hyperlink",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: {
            font: "Calibri",
            size: 24, // 12pt
            color: "2980B9", // Enhanced blue for links
            underline: {
              type: "single",
              color: "2980B9",
            },
          },
          paragraph: {
            spacing: {
              before: 0,
              after: 0,
              line: 320,
            },
          },
        },
        {
          id: "SectionDivider",
          name: "Section Divider",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: {
            font: "Calibri",
            size: 18,
            color: "BDC3C7",
          },
          paragraph: {
            spacing: {
              before: 200, // Better spacing
              after: 200, // Better spacing
              line: 300,
            },
            alignment: AlignmentType.CENTER,
            border: {
              top: {
                color: "BDC3C7",
                space: 25,
                style: BorderStyle.SINGLE,
                size: 6, // Thicker for better visibility
              },
              bottom: {
                color: "BDC3C7",
                space: 25,
                style: BorderStyle.SINGLE,
                size: 6, // Thicker for better visibility
              },
            },
          },
        },
        {
          id: "HighlightBox",
          name: "Highlight Box",
          basedOn: "BodyText",
          next: "BodyText",
          quickFormat: true,
          run: {
            font: "Calibri",
            size: 24,
            color: "2C3E50",
            bold: true,
          },
          paragraph: {
            spacing: {
              before: 120,
              after: 120,
              line: 300,
            },
            indent: {
              left: 400,
              right: 400,
            },
            alignment: AlignmentType.CENTER,
            border: {
              top: { color: "3498DB", space: 15, style: BorderStyle.SINGLE, size: 6 },
              bottom: { color: "3498DB", space: 15, style: BorderStyle.SINGLE, size: 6 },
              left: { color: "3498DB", space: 15, style: BorderStyle.SINGLE, size: 6 },
              right: { color: "3498DB", space: 15, style: BorderStyle.SINGLE, size: 6 },
            },
            shading: {
              fill: "EBF5FB", // Light blue background
            },
          },
        },
        {
          id: "QuoteText",
          name: "Quote Text",
          basedOn: "BodyText",
          next: "BodyText",
          quickFormat: true,
          run: {
            font: "Calibri",
            size: 26,
            color: "34495E",
            italic: true,
          },
          paragraph: {
            spacing: {
              before: 100,
              after: 100,
              line: 300,
            },
            indent: {
              left: 800,
              right: 400,
            },
            alignment: AlignmentType.JUSTIFIED,
            border: {
              left: {
                color: "E74C3C",
                space: 20,
                style: BorderStyle.SINGLE,
                size: 8,
              },
            },
            shading: {
              fill: "FDFEFE", // Very light gray background
            },
          },
        },
      ],
    },
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 900, // 0.625 inch - more professional
              right: 720, // 0.5 inch
              bottom: 900, // 0.625 inch - more professional
              left: 720, // 0.5 inch
            },
          },
        },
        children: [
          new Paragraph({
            text: `${program?.toUpperCase()} HEMISPHERE HOME PROGRAM`,
            style: "Heading1",
          }),
          // Patient Information Section
          new Paragraph({
            children: [
              new TextRun({ text: "Patient Information", bold: true, size: 32, color: "1B365D" }),
            ],
            spacing: { before: 160, after: 10 },
            alignment: AlignmentType.CENTER,
            border: {
              bottom: {
                color: "1B365D",
                space: 10,
                style: BorderStyle.SINGLE,
                size: 4,
              },
            },
          }),

          // Patient Information - Simple Paragraph Layout
          new Paragraph({
            children: [
              new TextRun({
                text: "Patient: ",
                bold: true,
                size: 26,
                color: "2980B9",
                font: "Calibri",
              }),
              new TextRun({
                text: `${firstName} ${lastName}`,
                size: 26,
                color: "2C3E50",
                font: "Calibri",
              }),
            ],
            alignment: AlignmentType.LEFT,
            spacing: { after: 100 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "Age Group: ",
                bold: true,
                size: 26,
                color: "2980B9",
                font: "Calibri",
              }),
              new TextRun({
                text: `${ageGroup}`,
                size: 26,
                color: "2C3E50",
                font: "Calibri",
              }),
            ],
            alignment: AlignmentType.LEFT,
            spacing: { after: 100 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "Goal Level: ",
                bold: true,
                size: 26,
                color: "2980B9",
                font: "Calibri",
              }),
              new TextRun({
                text: `${(() => {
                  if (age <= 2) return 1;
                  if (age >= 11) return 10;
                  return age - 1;
                })()}`,
                size: 26,
                color: "2C3E50",
                font: "Calibri",
              }),
            ],
            alignment: AlignmentType.LEFT,
            spacing: { after: 200 },
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: `${isReevaluation ? "Re-Evaluation" : "Initial Evaluation"}: `,
                bold: true,
                size: 26,
                color: "2980B9",
                font: "Calibri",
              }),
              new TextRun({
                text: new Date(evaluationDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }),
                size: 26,
                color: "2C3E50",
                font: "Calibri",
              }),
            ],
            alignment: AlignmentType.LEFT,
            spacing: { after: 100 },
          }),

          // Spacing after patient info
          new Paragraph({
            text: "",
            spacing: { after: 60 },
          }),
          new Paragraph({
            text: "Melillo Method 10 Level System",
            style: "Heading2"
          }),
          new Paragraph({
            text: "The Melillo Method program is designed to be dynamic and change as you progress with each part of the home program. Each task has 10 levels which are based on the patient’s age. The overall goal level listed at the top of this program is the ultimate goal for each task in this home program. The evaluation determines the starting level and each task you will see in this program has its own set of criteria that must be met before moving up to the next level. In order to progress to the next level, all criteria for the task must be met for 5 sessions in a row. Once this is achieved, you are encouraged to request the next level and a video will be sent to you with a more advanced version of that task with new criteria. Ideally, you or your child should be able to advance to a new level every 6-8 weeks with the overall goal being to reach their goal level in each task. This 10 level system ensures the program is individualized to each person and is designed to change as you progress through the program.",
            style: "BodyText",
          }),
          ...(sections.primitiveReflexes &&
            Object.keys(sections.primitiveReflexes).length > 0
            ? [
              new Paragraph({
                text: "Primitive Reflexes Exercises/Stimulation",
                style: "Heading2",
              }),
              new Paragraph({
                text: getPrimitiveReflexesText({
                  firstName,
                  program,
                  genderAdjective,
                  ageGroup,
                  isVirtual,
                  isReevaluation,
                  genderSubjectPronoun,
                }),
                style: "BodyText",
              }),
              // Call appropriate functions based on reflex type
              ...(sections.primitiveReflexes.tlr
                ? await Tlr(
                  Number(sections.primitiveReflexes.tlr),
                  firstName,
                  genderSubjectPronoun,
                  genderObjectAdjective,
                  genderAdjective,
                  program
                )
                : []),
              ...(sections.primitiveReflexes.astnr
                ? await Astnr(
                  Number(sections.primitiveReflexes.astnr),
                  firstName,
                  genderSubjectPronoun,
                  genderAdjective,
                  genderObjectAdjective,
                  ageGroup
                )
                : []),
              ...(sections.primitiveReflexes.babinski
                ? await Babinski(
                  Number(sections.primitiveReflexes.babinski),
                  program,
                  genderAdjective,
                  genderObjectAdjective,
                )
                : []),
              ...(sections.primitiveReflexes.rooting
                ? await Rooting(
                  Number(sections.primitiveReflexes.rooting),
                  firstName,
                  genderAdjective,
                  genderObjectAdjective
                )
                : []),
              ...(sections.primitiveReflexes.snout
                ? await Snout(
                  Number(sections.primitiveReflexes.snout),
                  firstName,
                  genderAdjective,
                  genderObjectAdjective,
                  ageGroup
                )
                : []),
              ...(sections.primitiveReflexes.spinalGalant
                ? await SpinalGalant(
                  Number(sections.primitiveReflexes.spinalGalant),
                  firstName,
                  genderObjectAdjective,
                  genderAdjective
                )
                : []),
              ...(sections.primitiveReflexes.stnr
                ? await Stnr(
                  Number(sections.primitiveReflexes.stnr),
                  firstName,
                  genderAdjective,
                  ageGroup,
                  genderObjectAdjective
                )
                : []),
              ...(sections.primitiveReflexes.moro
                ? await Moro(
                  Number(sections.primitiveReflexes.moro),
                  firstName,
                  genderAdjective,
                  genderObjectAdjective
                )
                : []),
              ...(sections.primitiveReflexes.palmerGrasp
                ? await PalmerGrasp(
                  Number(sections.primitiveReflexes.palmerGrasp),
                  firstName,
                  ageGroup,
                  genderAdjective,
                  genderObjectAdjective,
                  program
                )
                : []),
            ]
            : []),
          // Core Stability Exercises
          ...(sections.coreStability?.prone ||
            sections.coreStability?.lateral ||
            sections.coreStability?.supine ||
            sections.coreStability?.curlUps ||
            sections.coreStability?.pushUps ||
            sections.coreStability?.pullUps
            ? [
              new Paragraph({
                text: "Core Stability Exercises",
                style: "Heading2",
              }),
              new Paragraph({
                text: isReevaluation
                  ? `To continue to work on building core strength and increasing stability around the spine, ${firstName} will continue with these updated core exercises as part of the daily home program.`
                  : `These are important for ${firstName} to create spinal stability, and build core strength. The spine carries a lot of information to the brain, so this is important for many reasons. These will be part of ${firstName}'s daily home program also.`,
                style: "BodyText",
              }),
              ...(sections.coreStability?.prone
                ? await CSProne(
                  Number(sections.coreStability.prone),
                  firstName,
                  ageGroup
                )
                : []),
              ...(sections.coreStability?.lateral
                ? await CSLateral(
                  Number(sections.coreStability.lateral)
                )
                : []),
              ...(sections.coreStability?.supine
                ? await CSSupine(
                  Number(sections.coreStability.supine),
                  firstName
                )
                : []),
              ...(sections.coreStability?.curlUps
                ? await CSCurlUps(
                  Number(sections.coreStability.curlUps),
                  ageGroup,
                  firstName
                )
                : []),
              ...(sections.coreStability?.pushUps
                ? await CSPushUps(
                  Number(sections.coreStability.pushUps),
                  firstName,
                  genderSubjectPronoun,
                  genderAdjective,
                  ageGroup
                )
                : []),
              ...(sections.coreStability?.pullUps
                ? await CSPullUps(
                  Number(sections.coreStability.pullUps),
                  firstName,
                  ageGroup,
                  genderSubjectPronoun,
                  genderObjectAdjective,
                  genderAdjective
                )
                : []),
            ]
            : []),

          // Balance Exercises
          ...(sections.balanceExercise && sections.balanceExercise !== "0"
            ? [
              new Paragraph({
                text: "Balance Exercises",
                style: "Heading2",
              }),
              ...(await BalanceExercise(Number(sections.balanceExercise),
                genderAdjective,
                program
              )),
            ]
            : []),

          // Galvanic
          ...(sections.galvanic?.length > 0
            ? [
              new Paragraph({
                text: "Galvanic",
                style: "Heading2",
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `We will also be recommending the `,
                  }),
                  new ExternalHyperlink({
                    children: [
                      new TextRun({
                        text: "Galvanic Device",
                        underline: {},
                        color: "3498DB"
                      }),
                    ],
                    link: "https://dr-robert-melillo.myshopify.com/products/micro-z-ii-mini-unit-for-home-therapy-galvanic-stimulation",
                  }),
                  new TextRun({
                    text: ` to use as part of the home program. This will be used at home while doing the home program including the vestibular activities, balance and Metronome as instructed. `,
                  }),
                  new ExternalHyperlink({
                    children: [
                      new TextRun({
                        text: "The instruction video",
                        underline: {},
                        color: "3498DB"
                      }),
                    ],
                    link: "https://www.dropbox.com/scl/fi/5uir4vyqqay7fdgfftxx0/Melillo-Galvanic-Stimulator-Instructions-V2.mp4?rlkey=rb454jhozex52j4vwiy674vbn&st=9k0q97oe&dl=0",
                  }),
                  new TextRun({
                    text: ` for the unit and written instructions can be used for reference also.`,
                  }),
                ],
                style: "BodyText",
              }),
            ]
            : []),

          // Shokz
          ...(sections.shokz?.length > 0
            ? [
              new Paragraph({
                text: "Bone Conducting Headphones (Shokz)",
                style: "Heading2",
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `I have also recommended the `,
                  }),
                  new ExternalHyperlink({
                    children: [
                      new TextRun({
                        text: "Shokz Bone Conducting Headphones",
                        style: "Hyperlink",
                      }),
                    ],
                    link: "https://a.co/d/8Q3Kv88",
                  }),
                  new TextRun({
                    text: ` to use as part of the home program. These will be used as part of the home program while spinning or doing vestibular activities, balance and metronome activities with the TONER PACER PRO app on a tablet or phone.These can be purchased in our office or on Amazon.`,
                  }),
                ],
                style: "BodyText",
              }),
              new Paragraph({
                children: [
                  new ExternalHyperlink({
                    children: [
                      new TextRun({
                        text: "Bone Conducting Headphones Instruction Video",
                        style: "Hyperlink",
                      }),
                    ],
                    link: "https://www.dropbox.com/scl/fi/iq0nc822mb7wcx6hi7qq2/Melillo-Bone-Conduction-Instruction-V2.mp4?rlkey=7lzfhopqmz16f0o8sv93ixqhm&st=jq6804jx&dl=0",
                  }),
                ],
                style: "HyperlinkStyle",
              }),
            ]
            : []),

          // Vestibular Exercises
          ...(sections.vestibular?.spinning?.length > 0 || sections.vestibular?.level
            ? [
              new Paragraph({
                text: "Vestibular Exercises",
                style: "Heading2",
              }),
              ...(await VestibularSpinning(
                Number(
                  sections.vestibular.level
                    ? sections.vestibular.level
                    : sections.vestibular.spinning.length > 0
                      ? 1
                      : 0
                ),
                firstName,
                genderObjectAdjective,
                genderSubjectPronoun,
                program,
                ageGroup,
                sections
              )),
            ]
            : []),



          // Hemispheric Sensory Stimulus
          new Paragraph({
            text: "Hemispheric Sensory Stimulus",
            style: "Heading2",
          }),
          new Paragraph({
            text: `Recommend that ${firstName} receive regular chiropractic manipulations also as part of the therapy program by a well-trained professional. These will be done to the ${program === "left" ? "right" : "left"
              } side only of the cervical spine and the right and left side of the thoracic and lumbar spine.`,
            style: "BodyText",
          }),

          // Light Stimulation
          ...(sections.eyeLights?.length > 0
            ? [
              new Paragraph({
                text: "Light Stimulation",
                style: "Heading2",
              }),
              ...(await LSEyelights(sections.eyeLights, firstName,
                isReevaluation))
            ]
            : []),

          // Sound Therapy
          ...(sections.soundTherapy?.length > 0
            ? [
              new Paragraph({
                text: "Sound Therapy",
                style: "Heading2",
              }),
              ...sections.soundTherapy.map((value) => SoundTherapy(value, firstName, ageGroup, genderAdjective, genderSubjectPronoun, program)),
            ]
            : []),

          // Interactive Metronome
          ...(sections.interactiveMetronome
            ? [
              new Paragraph({
                text: "Interactive Metronome",
                style: "Heading2",
              }),
              ...(await InteractiveMetronome(firstName, genderSubjectPronoun, isReevaluation)),
            ]
            : []),

          // Occulomotor
          ...(sections.occulomotor
            ? [
              new Paragraph({
                text: "Oculomotor",
                style: "Heading2",
              }),
              ...(["1", "2", "3"].includes(String(sections.occulomotor))
                ? [
                  new Paragraph({
                    children: [
                      new TextRun({
                        text: `We recommend downloading the `
                      }),
                      new TextRun({ text: "OKN App", style: "bold" }),
                      new TextRun({
                        text: " (Google or Apple) on a phone for these activities. "
                      }),
                      new ExternalHyperlink({
                        children: [
                          new TextRun({
                            text: "OKN App Set Up Instructions",
                            underline: {}, color: "3498DB"
                          }),
                        ],
                        link: "https://www.dropbox.com/scl/fi/nj7u0n9zxpp9e9xfgii12/Melillo-OKN-Drum-Instructions.mp4?rlkey=p4u1vlxb5aqmxvg3ewgm6mqrm&st=dlkg1h0s&dl=0",
                      }),
                    ],
                    style: "BodyText",
                  }),
                ]
                : []),
              ...(await Occulomotor(Number(sections.occulomotor), program, ageGroup)),
            ]
            : []),

          // Olfactory
          ...(sections.olfactory
            ? [
              new Paragraph({
                text: "Olfactory",
                style: "Heading2",
              }),
              ...(await Olfactory(
                Number(sections.olfactory),
                genderAdjective,
                genderSubjectPronoun,
                ageGroup,
                program,
                genderObjectAdjective,
                firstName
              )),
            ]
            : []),

          // Auditory Processing
          ...(sections.auditoryProcessing
            ? [
              new Paragraph({
                text: "Auditory Processing",
                style: "Heading2",
              }),
              ...(await AuditoryProcessing(
                program,
                Number(sections.auditoryProcessing),
                ageGroup,
                firstName,
                genderSubjectPronoun
              )),
            ]
            : []),

          // MetroTimer
          ...(sections.metroTimer
            ? [
              new Paragraph({
                text: "MetroTimer",
                style: "Heading2",
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `MetroTimer App `, style: "bold"
                  }),
                  new TextRun({
                    text: `- Recommend downloading and using the Metrotimer App as part of the daily home program. `,
                  }),
                  new ExternalHyperlink({
                    children: [
                      new TextRun({
                        text: "MetroTimer App Instructions to set up",
                        underline: {}, color: "3498DB"
                      }),
                    ],
                    link: "https://www.dropbox.com/scl/fi/1w3angdyqrxuekbmcy8qg/Metro-Timer-Intro.mp4?rlkey=7gypwoxi7txqktthqxe7f5upk&st=oanwmlgi&dl=0",
                  }),
                ],
                style: "BodyText",
              }),
              ...await MetroTimer(Number(sections.metroTimer), firstName, ageGroup, program, genderObjectAdjective),
            ]
            : []),

          // Tactile - TENs Unit
          new Paragraph({
            text: "Tactile - TENS Unit",
            style: "Heading2",
          }),
          new Paragraph({
            children: [
              ...(isReevaluation
                ? [
                  new TextRun({
                    text: `The TENS unit is used to activate nerve fibers for sensory stimulation to the body. ${firstName} is recommended to continue to use the TENS unit for a minimum of`
                  }),
                  new TextRun({ text: `20 minutes twice daily`, bold: true }),
                  new TextRun({ text: ` as part of the home program. The goal will be to increase the time` }),
                  new TextRun({ text: ` every 2 weeks by 10 minutes to the maximum time of 60 minutes twice daily` }), new TextRun({
                    text: ` combined with using the Eyelights and Brain Driver at the same time. The pad will continue to be placed on the ${program === "right" ? "left" : "right"} side of the upper back between the spine and shoulder blade.`
                  }),
                ]
                : [
                  new TextRun({
                    text: `The TENS unit is used to activate nerve fibers for sensory stimulation to the body. This will be recommended to be placed between the spine and shoulder blade starting with `,
                  }),
                  new TextRun({ text: "20 minutes twice daily", bold: true }),
                  new TextRun({
                    text: ` as part of the home program. The goal will be to increase the time `,
                  }),
                  new TextRun({ text: "every 2 weeks by 10 minutes to the maximum of 60 minutes twice daily", bold: true }),
                  new TextRun({
                    text: ` combined with the Eyelights and Brain Driver. The level should be at the patient's tolerance and can be increased as able to be tolerated as well. The pad will be placed on the ${program === "right" ? "left" : "right"} side of the upper back.`
                  }),
                ]),
            ],
            style: "BodyText",
          }),

          ...(await (async () => {
            try {
              const imageResponse = await fetch(
                `/user-forms/doctor-assessment/exercise-images/tens_unit_${program}.jpeg`
              );
              const imageBuffer = await imageResponse.arrayBuffer();
              return [
                new Paragraph({
                  children: [
                    new ImageRun({
                      data: imageBuffer,
                      transformation: {
                        width: 240,
                        height: 130,
                      },
                    }),
                  ],
                  alignment: AlignmentType.CENTER,
                  spacing: { after: 10 },
                }),
              ];
            } catch (e) {
              console.error("Could not fetch TENs Unit image", e);
              return [];
            }
          })()),

          new Paragraph({
            children: [
              new ExternalHyperlink({
                children: [
                  new TextRun({
                    text: `TENS Unit Instructional Video`,
                    style: "Hyperlink",
                  }),
                ],
                link: "https://www.dropbox.com/scl/fi/e9knkb0w19e4onr4x0f9d/Melillo-TENS-unit-Instructions.mp4?rlkey=8mc2cvz66mbku88spef447sh0&st=v018zak9&dl=0",
              }),
            ],
            style: "HyperlinkStyle",
          }),


          // Vibration
          ...(sections.vibration?.length > 0
            ? [
              new Paragraph({
                text: "Vibration",
                style: "Heading2",
              }),
              ...Vibration(sections.vibration, firstName),
            ]
            : []),

          // Brain Driver / NeuroMyst
          ...(sections.brainDriver
            ? [
              new Paragraph({
                text: "Transcranial Unit (Brain Driver)",
                style: "Heading2",
              }),
              !isReevaluation &&
              new Paragraph({
                text: `To use the Brain Driver, you must use a Saline Solution mixture –`,
                bold: true
              }),
              !isReevaluation &&
              new Paragraph({
                children: [new ExternalHyperlink({
                  children: [
                    new TextRun({
                      text: `Brain Driver Solution Instructions`,
                      style: "Hyperlink",
                    }),
                  ],
                  link: "https://www.dropbox.com/scl/fi/7aitvdmtvqfri4c2gzjfc/Melillo-Transcranial-Saline-and-Sponge-Pads.mp4?rlkey=wlbs1cwkvonbdlrhb8738cm2w&st=zcnbg6j5&dl=0",
                })],
                style: "HyperlinkStyle",
                alignment: AlignmentType.LEFT,
                indent: {
                  left: 400,
                  right: 200,
                },
              }),
              !isReevaluation &&
              new Paragraph({
                children: [new ExternalHyperlink({
                  children: [
                    new TextRun({
                      text: `Brain Driver Set up Instruction Video`,
                      style: "Hyperlink",
                    }),
                  ],
                  link: "https://www.dropbox.com/scl/fi/rqrmkwg59yzhh2p91ccu3/Melillo-Transcranial-Direct-Current-Instructional-Brain-Driver.mp4?rlkey=g4ilchburq3d7lgmza72yvorv&st=btfv7zxe&dl=0",
                })],
                style: "HyperlinkStyle",
                alignment: AlignmentType.LEFT,
                indent: {
                  left: 400,
                  right: 200,
                },
              }),
              ...await BrainDriver(sections.brainDriver,
                program, ageGroup, firstName,),
            ]
            : []),

          // Cognitive Activities
          ...(!isReevaluation &&
            (sections.cognitiveActivities?.rightBrain?.length > 0 ||
              sections.cognitiveActivities?.leftBrain?.length > 0)
            ? [
              new Paragraph({
                text: "Cognitive Activities",
                style: "Heading2",
              }),
              ...(sections.cognitiveActivities?.rightBrain?.length > 0
                ? [
                  new Paragraph({
                    text: `Right Brain - ${sections.cognitiveActivities.rightBrain.join(
                      ", "
                    )}. Age level jigsaw puzzles, the game Connect 4 and maze books are right brain activities. A game of ring toss, ladder ball or corn-hole are also right brain fun and can be played outside or inside.`,
                    style: "BodyText",
                  }),
                ]
                : []),
              ...(sections.cognitiveActivities?.leftBrain?.length > 0
                ? [
                  new Paragraph({
                    text: `Left Brain - ${sections.cognitiveActivities.leftBrain.join(
                      ", "
                    )}. Any type of Memory games with Numbers, letters or words are left-brain activities. Games like Scrabble, Cross word puzzles, Boggle and also Tic-Tac-Toe and hangman are also left brain to do for fun.`,
                    style: "BodyText",
                  }),
                ]
                : []),
            ]
            : []),

          // Vitamins
          ...(sections.vitamins.length > 0
            ? [
              new Paragraph({
                text: "Vitamin Recommendations",
                style: "Heading2",
              }),
              ...await Vitamin(sections.vitamins, isReevaluation, firstName, genderSubjectPronoun, genderObjectAdjective),
            ]
            : []),

          // Vibrant Testing
          ...(sections.vibrantTesting.length > 0
            ? [
              new Paragraph({
                text: "Vibrant Testing",
                style: "Heading2",
              }),
              ...sections.vibrantTesting.flatMap(
                (value) =>
                  VibrantTesting(
                    value,
                    firstName,
                    genderSubjectPronoun,
                    genderAdjective,
                    ageGroup
                  )
              ),
            ]
            : []),

          new Paragraph({
            text: "Sallie",
            style: "Heading2"
          }),
          new Paragraph({
            text: "Sallie is an online program designed to help track changes you may see, how often you are able to complete home therapy sessions and as a way to keep in touch with the Melillo Center therapists. The therapists will have access to the information you input into Sallie in order to follow along with home treatment and changes to symptoms. We encourage you to track as often as possible to give the most accurate picture of your home treatment and changes you are seeing. Feel free to use the message feature on Sallie to request the next level of tasks you need or to ask any questions our staff may be able to help you with.",
            style: "BodyText",
          }),
          ...(await (async () => {
            try {
              const imageResponse = await fetch(
                `/user-forms/doctor-assessment/exercise-images/sallie.png`
              );
              const imageBuffer = await imageResponse.arrayBuffer();
              return [
                new Paragraph({
                  children: [
                    new ImageRun({
                      data: imageBuffer,
                      transformation: {
                        width: 120,
                        height: 70,
                      },
                    }),
                  ],
                  alignment: AlignmentType.CENTER,
                  spacing: { after: 10 },
                }),
              ];
            } catch (e) {
              console.error("Could not fetch Sallie image", e);
              return [];
            }
          })()),
          new Paragraph({
            children: [new ExternalHyperlink({
              children: [
                new TextRun({
                  text: `Sallie Power of Patients Website`,
                  style: "Hyperlink",
                }),
              ],
              link: "https://melillomethod.powerofpatients.com",
            })],
            style: "HyperlinkStyle",
          }),

          // Ending / Follow-up text
          // new Paragraph({
          //   text: getFollowUpText({
          //     firstName,
          //     ageGroup,
          //     sections,
          //     isReevaluation,
          //     isVirtual,
          //     genderObjectAdjective,
          //     genderSubjectPronoun,
          //     genderAdjective,
          //   }),
          //   style: "BodyText",
          //   spacing: { before: 400, after: 20 },
          // }),
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${firstName}_${new Date().toISOString().split('T')[0]}.docx`);
};

async function createImageParagraphs(urls, width = 150) {
  const paragraphs = [];

  if (!urls || urls.length === 0) {
    return paragraphs;
  }

  // Helper function to get image dimensions and calculate aspect ratio
  const getImageDimensions = (arrayBuffer) => {
    return new Promise((resolve) => {
      const blob = new Blob([arrayBuffer]);
      const img = new Image();
      img.onload = () => {
        const aspectRatio = img.height / img.width;
        let calculatedHeight = Math.round(width * aspectRatio);

        // If image is vertical (aspect ratio > 1.2) and height is too large, constrain it
        const maxHeight = 200; // Maximum height for vertical images
        if (aspectRatio > 1.2 && calculatedHeight > maxHeight) {
          calculatedHeight = maxHeight;
        }

        resolve({ width, height: calculatedHeight });
      };
      img.src = URL.createObjectURL(blob);
    });
  };

  // Process images in pairs for side-by-side layout
  for (let i = 0; i < urls.length; i += 2) {
    const leftUrl = urls[i];
    const rightUrl = urls[i + 1];

    try {
      // Fetch left image
      const leftResponse = await fetch(leftUrl);
      const leftArrayBuffer = await leftResponse.arrayBuffer();
      const leftDimensions = await getImageDimensions(leftArrayBuffer);

      // If there's only one image, center it
      if (!rightUrl) {
        paragraphs.push(
          new Table({
            rows: [
              new TableRow({
                children: [
                  new TableCell({
                    children: [
                      new Paragraph({
                        children: [
                          new ImageRun({
                            data: leftArrayBuffer,
                            transformation: { width: leftDimensions.width, height: leftDimensions.height },
                          }),
                        ],
                        alignment: AlignmentType.CENTER,
                        spacing: { after: 10 },
                      }),
                    ],
                    width: {
                      size: 100,
                      type: WidthType.PERCENTAGE,
                    },
                    verticalAlign: VerticalAlign.TOP,
                    margins: {
                      top: 40,
                      bottom: 10,
                      left: 10,
                      right: 200,
                    },
                  }),
                ],
              }),
            ],
            width: {
              size: 100,
              type: WidthType.PERCENTAGE,
            },
            borders: {
              top: { style: BorderStyle.NONE },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE },
              insideHorizontal: { style: BorderStyle.NONE },
              insideVertical: { style: BorderStyle.NONE },
            },
          })
        );
      } else {
        // Multiple images - use side-by-side layout
        const cellContents = [
          new TableCell({
            children: [
              new Paragraph({
                children: [
                  new ImageRun({
                    data: leftArrayBuffer,
                    transformation: { width: leftDimensions.width, height: leftDimensions.height },
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 10 },
              }),
            ],
            width: {
              size: 50,
              type: WidthType.PERCENTAGE,
            },
            verticalAlign: VerticalAlign.TOP,
            margins: {
              top: 40,
              bottom: 10,
              left: 10,
              right: 100,
            },
          }),
        ];

        // Fetch and add the second image
        try {
          const rightResponse = await fetch(rightUrl);
          const rightArrayBuffer = await rightResponse.arrayBuffer();
          const rightDimensions = await getImageDimensions(rightArrayBuffer);

          cellContents.push(
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new ImageRun({
                      data: rightArrayBuffer,
                      transformation: { width: rightDimensions.width, height: rightDimensions.height },
                    }),
                  ],
                  alignment: AlignmentType.CENTER,
                  spacing: { after: 10 },
                }),
              ],
              width: {
                size: 50,
                type: WidthType.PERCENTAGE,
              },
              verticalAlign: VerticalAlign.TOP,
              margins: {
                top: 40,
                bottom: 10,
                left: 10,
                right: 100,
              },
            })
          );
        } catch (e) {
          console.error(`❌ Could not fetch right image: ${rightUrl}`, e);
          // Add empty cell to maintain layout
          cellContents.push(
            new TableCell({
              children: [new Paragraph({ text: "" })],
              width: {
                size: 50,
                type: WidthType.PERCENTAGE,
              },
            })
          );
        }

        // Create table with both images
        paragraphs.push(
          new Table({
            rows: [
              new TableRow({
                children: cellContents,
              }),
            ],
            width: {
              size: 100,
              type: WidthType.PERCENTAGE,
            },
            borders: {
              top: { style: BorderStyle.NONE },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE },
              insideHorizontal: { style: BorderStyle.NONE },
              insideVertical: { style: BorderStyle.NONE },
            },
          })
        );
      }

      // Add spacing after table
      paragraphs.push(
        new Paragraph({
          text: "",
          spacing: { after: 10 },
        })
      );

    } catch (e) {
      console.error(`❌ Could not fetch left image: ${leftUrl}`, e);
      // Fallback to single image layout for this pair
      paragraphs.push(
        new Paragraph({
          text: "Image could not be loaded",
          alignment: AlignmentType.CENTER,
          spacing: { after: 10 },
        })
      );
    }
  }

  return paragraphs;
}

const getPrimitiveReflexesText = ({
  firstName,
  program,
  genderAdjective,
  ageGroup,
  isVirtual,
  isReevaluation,
  genderSubjectPronoun,
}) => {
  if (isReevaluation) {
    return `It was great to see ${firstName} for a reevaluation${isVirtual ? "" : " in the office"
      } to re-assess the progress. Based upon the re-evaluation, ${firstName} does still have primitive reflexes present at this time. We have updated the home program and the family will continue to work on these daily.`;
  }
  if (isVirtual) {
    return `Based upon the paperwork completed and my consultation call, I confirmed that ${firstName} has a ${program} hemisphere deficit and most of ${genderAdjective} primitive reflexes are still present. Research has shown that retained primitive reflexes will lead to imbalances in the brain so these will be important to focus on as part of the daily home program. ${ageGroup === "Child" ? "The family" : "You"
      } will do these stimulation exercises ${ageGroup === "Child" ? `with ${firstName}` : ""
      } for 1 - 2 times per day as part of the home program.`;
  }
  return `Based upon the paperwork completed, the physical examination and testing, we confirmed that ${firstName} has a ${program} hemisphere deficit, and most all primitive reflexes are still present. Research has clearly shown that retained Primitive Reflexes will lead to imbalances in the brain, so it is important ${ageGroup === "Child" ? "for the family" : ""
    } to work on these as part of ${firstName}'s home program. These are recommended to be done twice daily${ageGroup === "Child" ? ` with ${firstName}` : ""
    }.`;
};

const getFollowUpText = ({
  firstName,
  ageGroup,
  sections,
  isReevaluation,
  isVirtual,
  genderObjectAdjective,
  genderSubjectPronoun,
  genderAdjective,
}) =>
  isReevaluation
    ? `It was great to see ${firstName}${ageGroup === "Child" ? "’s family" : ""}, and check in on the progress with the program. We have updated the home report based upon the reevaluation and testing and the results will be reviewed with ${ageGroup === "Child" ? "the family" : "you"
    }. ${sections.followUpExam
      ? `I will discuss the ongoing recommendations and recommendation for a reevaluation in ${sections.followUpExam.join(
        " to "
      )} months. `
      : ""
    }${sections.virtualFollowUp
      ? `I will remind them to set up a virtual follow up with the team in about ${sections.virtualFollowUp.join(
        " or "
      )} months so that we can check in on the progress and make updates as needed to the program. `
      : ""
    }We are here to help and support ${ageGroup === "Child" ? "the family" : "you"} for anything needed on the journey.`
    : isVirtual
      ? `It was great to meet ${firstName}${ageGroup === "Child" ? "’s family" : ""}, and I am excited to get ${genderObjectAdjective} started with the program because I believe that ${genderSubjectPronoun} will do very well. ${ageGroup === "Child" ? "The family" : "You"
      } will implement the home program right away and can reach out to us with any questions or assistance that is needed. I recommend that ${ageGroup === "Child" ? "they" : "you"
      } schedule a virtual follow up in about 6-8 weeks so that we can check in on ${ageGroup === "Child" ? firstName + "'s" : "your"
      } progress and make any updates that are needed. Thank you for allowing us to be a part of ${ageGroup === "Child" ? firstName + "'s" : "your"
      } journey and we are here for any questions or additional support that ${ageGroup === "Child" ? "they" : "you"
      } need while implementing the program.`
      : `It was great to meet ${firstName}${ageGroup === "Child" ? " and " + genderAdjective + " family" : ""} and I am excited to get ${genderObjectAdjective} started with the program because I think that ${genderSubjectPronoun} will do very well. The evaluation and testing results were reviewed with ${ageGroup === "Child" ? "the family" : genderObjectAdjective
      } and I discussed my recommendations for therapy and treatment as well. ${ageGroup === "Child" ? "They" : genderSubjectPronoun
      } will implement the home program and reach out to us with any questions. I have also recommended a full reevaluation in 3 to 6 months so that we can check in the progress as well. Thank you for the opportunity to be a part of ${ageGroup === "Child" ? firstName + "'s" : "your"
      } journey and we are excited to follow ${ageGroup === "Child" ? genderAdjective : "your"} results.`;


async function BalanceExercise(
  level,
  genderAdjective,
  program
) {
  const content = [];

  const getDescriptionRuns = (level) => {


    switch (level) {
      case 1:
        return [
          new TextRun({ text: `Romberg with Eyes Open: Shoes and socks off, while standing on a level surface, stand with feet together so they are touching. The goal for this level is to stand in this position, with eyes open for ` }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: ` without losing ${genderAdjective} balance or wobbling before proceeding to the next level.` })
        ];
      case 2:
        return [
          new TextRun({ text: `Romberg with Eyes Closed: Shoes and socks are off, while standing on a level surface, stand with feet together so they are touching and close eyes.  The goal for this level is to stand in this position, with eyes closed for ` }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: ` without losing balance, falling or wobbling consistently for 5 sessions in row before proceeding to the next level.` })
        ];
      case 3:
        return [
          new TextRun({ text: `Mann's Test with Eyes Open: Socks and shoes are off - Stand in a tandem gait position (left foot directly in front of the right foot with the heel of the left foot touching the toe of the right foot), with eyes open. The goal for this level is to maintain this position without losing balance, falling or wobbling for ` }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: ` for 5 sessions in a row before proceeding to the next level.` })
        ];
      case 4:
        return [
          new TextRun({ text: `Mann's Test with Eyes Closed: Socks and shoes are off - Stand in a tandem gait position (left foot directly in front of the right foot with the heel of the left foot touching the toe of the right foot), with eyes closed. The goal for this level is to consistently maintain this position without losing balance, falling or wobbling for ` }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: ` for 5 sessions  in a row before proceeding to the next level.` })
        ];
      case 5:
        return [
          new TextRun({ text: `One Leg Stand with Eyes Open: With socks and shoes off, while standing on a level surface, stand on one leg with eyes open. Stand on just the ${program === "left" ? "right" : "left"} leg.  Do not hold on to anything for support or rest one leg on the other during this exercise. The goal for this level is to maintain balance for ` }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: ` without swaying, wobbling, falling or jumping consistently for 5 sessions in a row before moving to the next level.` })
        ];
      case 6:
        return [
          new TextRun({ text: `One Leg Stand with Eyes Closed: With socks and shoes off, standing on a level surface, stand on one leg with eyes open. Stand on just the ${program === 'left' ? 'right' : 'left'} leg.  Once balanced, close eyes. Do not hold on to anything for support or rest one leg on the other during this exercise. The goal for this level is to maintain balance on one leg for ` }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: ` consistently without swaying, wobbling, falling or jumping for 5 sessions in a row before proceeding to the next level.` })
        ];
      case 7:
        return [
          new TextRun({ text: `Romberg with Eyes Open on Foam Pad: With shoes and socks off, stand on a foam pad with ${genderAdjective} feet together so they are touching and hands at side. The goal for this level is to stand in this position, hands at ${genderAdjective} side, with eyes open for ` }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: ` without losing balance, swaying, falling or wobbling for 5 sessions in a row before proceeding to the next level.` })
        ];
      case 8:
        return [
          new TextRun({ text: `Romberg with Eyes Closed on Foam Pad: With shoes and socks off, stand on a foam pad with their feet together so they are touching and hands at side and eyes closed. The goal for this level is to stand in this position, hands at ${genderAdjective} side, with eyes closed for ` }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: ` without losing balance, swaying, falling or wobbling for 5 sessions in a row before proceeding to the next level.` })
        ];
      case 9:
        return [
          new TextRun({ text: `One Leg Stand with Eyes Closed on Foam Pad: With socks and shoes off, stand on a foam pad. stand on ${program === 'right' ? 'left' : 'right'} leg. Once balanced, close eyes. Do not hold on to anything for support or rest one leg on the other during this exercise. The goal for this level is to maintain balance for ` }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: ` on one leg with eyes closed without wobbling, falling or jumping for 5 sessions in a row before proceeding to the next level.` })
        ];
      case 10:
        return [
          new TextRun({ text: `One Leg Stand with Eyes Closed on Bosu Ball: With socks and shoes off, flip the Bosu Ball over so the inflated side is on the ground, and the flat plastic side is up. Stand on the flat side of the Bosu ball on one leg. Stand on just the ${program === 'right' ? 'left' : 'right'} leg. Once balanced, close eyes. Do not hold on to anything for support or rest one leg on the other during this exercise. The goal for this level is to consistently maintain balance for ` }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: ` on one leg without wobbling, falling or jumping to regain balance with eyes closed.` })
        ];
      default:
        return [];
    }
  };


  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/be/be_1.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/be/be_2.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/be/be_3.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/be/be_4.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/be/be_5.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/be/be_6.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/be/be_7.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/be/be_8.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/be/be_9.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/be/be_10.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/a1khomjkeek4d4vame28f/Melillo-Balance-Assessment-and-Treatment-Level-1-V2.mp4?rlkey=ph7rm98xpe64411uqxvnt9rwx&st=f22d7se3&dl=0",
    2: "https://www.dropbox.com/scl/fi/8ato37var37yc3w0v087a/Melillo-Balance-Assessment-and-Treatment-Level-2-V2.mp4?rlkey=gtwp0xl2uozp0l65ohse7suxe&st=4yk5sfb1&dl=0",
    3: "https://www.dropbox.com/scl/fi/135ijfd10bu9b9eiojabt/Melillo-Balance-Assessment-and-Treatment-Level-3-V2.mp4?rlkey=nqg68iuxrhqspd97sbspdde5x&st=0mmgd26y&dl=0",
    4: "https://www.dropbox.com/scl/fi/q9mt5aike3i7qlvdr2pmh/Melillo-Balance-Assessment-and-Treatment-Level-4-V2.mp4?rlkey=5kuxpc4s649ejp6bstfluzb7i&st=2ipe2kc8&dl=0",
    5: "https://www.dropbox.com/scl/fi/zlq7t3zq0fylf1rn7v8u7/Melillo-Balance-Assessment-and-Treatment-Level-5-V2.mp4?rlkey=2nauq2053hhgztg026ylq9hw4&st=cfhsrduu&dl=0",
    6: "https://www.dropbox.com/scl/fi/bnqfynjlqgdcfc5amatab/Melillo-Balance-Assessment-and-Treatment-Level-6-V2.mp4?rlkey=eh79btyzl9u657ruustiltcvo&st=3dzbb9x5&dl=0",
    7: "https://www.dropbox.com/scl/fi/0x3g9ozdeygxkl3onrnp7/Melillo-Balance-Assessment-and-Treatment-Level-7-V2.mp4?rlkey=bt6hgj4oml0rkmx9ir86d8vnq&st=01xuawvk&dl=0",
    8: "https://www.dropbox.com/scl/fi/c91iwzs5jgm3k7lr8kzea/Melillo-Balance-Assessment-and-Treatment-Level-8-V2.mp4?rlkey=3wdeptrkldcwanzmvtl448gp8&st=r1pr2b5v&dl=0",
    9: "https://www.dropbox.com/scl/fi/jahc3un7yho5ywghmhhqm/Melillo-Balance-Assessment-and-Treatment-Level-9-V2.mp4?rlkey=ht9i9vml3ddp0mgqvvncfui2u&st=djxvzm2h&dl=0",
    10: "https://www.dropbox.com/scl/fi/8cv8zrm4uk3tpq5ppbx0l/Melillo-Balance-Assessment-and-Treatment-Level-10-V2.mp4?rlkey=bxxc23ks825fpeyp1wn27k6wh&st=64fpein2&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      children: [new TextRun({ text: `Level ${level}:`, bold: true })],
      style: "Heading3",
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: getDescriptionRuns(level),
      style: "BodyText",
    })
  );

  if (imageUrls[level]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[level]);
    content.push(...imageParagraphs);
  }

  // Hyperlink
  const linkUrl = links[level];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Instruction Video: Level ${level}`,
          style: "Hyperlink",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
      })
    );
  }

  return content;
}

async function VestibularSpinning(
  level,
  firstName,
  genderObjectAdjective,
  genderSubjectPronoun,
  program,
  ageGroup,
  sections
) {
  const content = [];

  // Helper for direction
  const direction = program === "left" ? "counter-clockwise" : "clockwise";
  const isGalvanicOrShokz = sections.galvanic?.length > 0 || sections.shokz?.length > 0;

  // Image URLs for each level
  const imageUrls = {
    1: "/user-forms/doctor-assessment/exercise-images/vestibular/image3.jpeg",
    2: "/user-forms/doctor-assessment/exercise-images/vestibular/image4.jpeg",
    3: `/user-forms/doctor-assessment/exercise-images/vestibular/image${isGalvanicOrShokz ? '5' : '1'}.jpeg`,
    4: `/user-forms/doctor-assessment/exercise-images/vestibular/image${isGalvanicOrShokz ? '5' : '1'}.jpeg`,
    5: `/user-forms/doctor-assessment/exercise-images/vestibular/image${isGalvanicOrShokz ? '5' : '1'}.jpeg`,
    6: `/user-forms/doctor-assessment/exercise-images/vestibular/image${isGalvanicOrShokz ? '5' : '1'}.jpeg`,
    7: `/user-forms/doctor-assessment/exercise-images/vestibular/image${isGalvanicOrShokz ? '5' : '1'}.jpeg`,
    8: `/user-forms/doctor-assessment/exercise-images/vestibular/image${isGalvanicOrShokz ? '5' : '1'}.jpeg`,
    9: `/user-forms/doctor-assessment/exercise-images/vestibular/image${isGalvanicOrShokz ? '5' : '1'}.jpeg`,
    10: "/user-forms/doctor-assessment/exercise-images/vestibular/image2.jpeg",
  };

  // Video links for each level
  const videoLinks = {
    1: "https://www.dropbox.com/scl/fi/7bxxfvto4u99cmqknjpua/Melillo-Visual-Testing-Vestibular-L1.mp4?rlkey=xopojlylpursaaqcnxce21w89&st=fvwbcskr&dl=0",
    2: "https://www.dropbox.com/scl/fi/q7okktw8nffbe8ftgybs7/Melillo-Visual-Testing-Vestibular-L2.mp4?rlkey=da5j50046m91s2hk5blweuqfg&st=9hvou9ts&dl=0",
    3: "https://www.dropbox.com/scl/fi/ch6f6lkyy83piadn9oxzs/Melillo-Visual-Testing-Vestibular-L3.mp4?rlkey=hmd292yoc1zrfb72jwar8ptsh&st=xs8izqvs&dl=0",
    4: "https://www.dropbox.com/scl/fi/di8rp8i05km5x4j15f6x6/Melillo-Visual-Testing-Vestibular-L4.mp4?rlkey=ji6qjqot078exy8s27hxz4lgk&st=t1aymhzd&dl=0",
    5: "https://www.dropbox.com/scl/fi/esniccvejykd03dnnjzwq/Melillo-Visual-Testing-Vestibular-L5.mp4?rlkey=dcfawgjhatlt3a2k50hm6y5ki&st=2cyrwgn4&dl=0",
    6: "https://www.dropbox.com/scl/fi/vb0gqzs163lnhspstmke4/Melillo-Visual-Testing-Vestibular-L6.mp4?rlkey=2i59s298ghsiwta3wx0n5vrdp&st=czwcushc&dl=0",
    7: "https://www.dropbox.com/scl/fi/e6w7rwudplr28mavl9ofe/Melillo-Visual-Testing-Vestibular-L7.mp4?rlkey=qky89nas5u2fo5b0fycbs7tkk&st=hu9keluv&dl=0",
    8: "https://www.dropbox.com/scl/fi/uzltgufmwxxnte7ygwdta/Melillo-Visual-Testing-Vestibular-L8.mp4?rlkey=sgatsrbfupltyxsjrwpnp0y4h&st=rgw2f229&dl=0",
    9: "https://www.dropbox.com/scl/fi/ah9dhmglhkja7tvaxq3oh/Melillo-Visual-Testing-Vestibular-L9.mp4?rlkey=pnet7h5rbr1qxq780y8i9rlbe&st=eaoj4bht&dl=0",
    10: "https://www.dropbox.com/scl/fi/zzp8yh06rwwilnm92xjv6/Melillo-Visual-Testing-Vestibular-L10.mp4?rlkey=1jhvre0g1efnuafz0h9bqno0v&st=bfhr0i4j&dl=0",
  };

  const descriptions = {
    1: `${ageGroup === "Child" ? "Parents" : "Partner"} will do spinning – ${firstName} will sit in a chair and at this level, may need ${ageGroup === "Child" ? "parent" : "partner"} to assist with this exercise by holding ${genderObjectAdjective}. ${ageGroup === "Child" ? "Parent" : "Partner"} will spin and go Fast to the ${program}, ${direction} for 2 seconds per rotation for as long as ${genderSubjectPronoun} will tolerate and repeat if possible until you can see Nystagmus (beating) of the eyes when stopped.`,

    2: `${ageGroup === "Child" ? "Parents" : "Partner"} will do spinning – ${firstName} will sit in a chair and at this level, preferably with feet crossed. ${ageGroup === "Child" ? "Parent" : "Partner"} will spin and go Fast to the ${program}, ${direction} for 2 seconds per rotation for as long as ${genderSubjectPronoun} will tolerate for 10 – 50 rotations and repeat if needed until you can see Nystagmus (beating) of the eyes when stopped.`,

    3: `${ageGroup === "Child" ? "Parents" : "Partner"} will do spinning – ${firstName} will sit in a chair and ${ageGroup === "Child" ? "parent" : "partner"} will spin fast to the ${program}, ${direction} for 2 seconds per rotation for 10 – 70 rotations and repeat if needed and they can tolerate until you can see Nystagmus (beating) of the eyes when stopped.`,

    4: `${ageGroup === "Child" ? "Parent" : "Partner"} will spin fast to the ${program}, ${direction} for 2 seconds per rotation for 10 – 50 rotations and repeat if needed until you can see Nystagmus (beating) of the eyes when stopped.`,

    5: `${ageGroup === "Child" ? "Parent" : "Partner"} will spin fast to the ${program}, ${direction} for 2 seconds per rotation for 10 – 30 rotations and repeat if needed until you can see Nystagmus (beating) of the eyes when stopped for 7 – 10 seconds.`,

    6: `${ageGroup === "Child" ? "Parent" : "Partner"} will spin fast to the ${program}, ${direction} for 2 seconds per rotation for 10 – 20 rotations and repeat if needed until you can see Nystagmus (beating) of the eyes when stopped for 7 – 10 seconds.`,

    7: `${ageGroup === "Child" ? "Parent" : "Partner"} will spin fast to the ${program}, ${direction} for 2 seconds per rotation for 10 rotations and repeat if needed until you can see Nystagmus (beating) of the eyes when stopped for 10 seconds.`,

    8: `${ageGroup === "Child" ? "Parent" : "Partner"} will spin fast to the ${program}, ${direction} for 2 seconds per rotation for 10 rotations and repeat if needed until you can see Nystagmus (beating) of the eyes when stopped for 5 seconds.`,

    9: `${ageGroup === "Child" ? "Parent" : "Partner"} will spin fast to the ${program}, ${direction} for 2 seconds per rotation for 10 rotations and repeat if needed until you can see Nystagmus (beating) of the eyes when stopped for 10 seconds.`,

    10: `${firstName} will hold hands clapped together in front of ${genderObjectAdjective} and focus eyes on them to suppress VOR - ${ageGroup === "Child" ? "Parent" : "Partner"} will spin fast to the ${program}, ${direction} for 2 seconds per rotation for 10 rotations as shown in the video instructions below.`,
  };

  if (!descriptions[level]) return [];

  // Title
  content.push(
    new Paragraph({
      text: `Vestibular Spinning - Level ${level}`,
      heading: HeadingLevel.HEADING_3,
      spacing: { before: 120, after: 80 },
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: [new TextRun({ text: descriptions[level] })],
      style: "BodyText",
      spacing: { after: 100 },
    })
  );

  // Add image if available
  if (imageUrls[level]) {
    const imageParagraphs = await createImageParagraphs([imageUrls[level]]);
    content.push(...imageParagraphs);
  }

  // Add video link if available
  if (videoLinks[level]) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Level ${level} Spinning Instructions`,
          style: "Hyperlink",
        }),
      ],
      link: videoLinks[level],
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
        spacing: { after: 10 },
      })
    );
  }

  return content;
}

async function CSProne(level, firstName, ageGroup) {
  const content = [];
  const numLevel = Number(level);

  const getDescriptionRuns = (level) => {
    switch (level) {
      case 1:
        return [
          new TextRun({ text: "Starting on the floor and lay face down, lift each limb separately and lift head up, hold it perfectly straight for " }),
          new TextRun({ text: "15 seconds", bold: true }),
          new TextRun({ text: " for each limb." })
        ];
      case 2:
        return [
          new TextRun({ text: "Starting on the floor and lay face down, lift each limb separately and lift head up, hold it perfectly straight for " }),
          new TextRun({ text: "45 seconds", bold: true }),
          new TextRun({ text: " for each limb." })
        ];
      case 3:
        return [
          new TextRun({ text: "Starting on the floor and lay face down, lift up opposite arm and leg, and lift head up also. Hold it perfectly straight, switch sides and do other arm and leg. Goal is " }),
          new TextRun({ text: "15 seconds", bold: true }),
          new TextRun({ text: " for each side." })
        ];
      case 4:
        return [
          new TextRun({ text: "Starting on the floor and lay face down, lift up opposite arm and leg, and lift head up also. Hold it perfectly straight, switch sides and do other arm and leg. Goal is " }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: " for each side." })
        ];
      case 5:
        return [
          new TextRun({ text: "Starting on the floor and lay face down, lift up opposite arm and leg, and lift head up also. Hold it perfectly straight, switch sides and do other arm and leg. Goal is " }),
          new TextRun({ text: "60 seconds", bold: true }),
          new TextRun({ text: " for each side." })
        ];
      case 6:
        return [
          new TextRun({ text: "Lay face down, lift arms, legs and head all at the same time, hold as still and straight. Goal is " }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: "." })
        ];
      case 7:
        return [
          new TextRun({ text: "Lay face down, lift arms, legs and head all at the same time, hold as still and straight. Goal is " }),
          new TextRun({ text: "60 seconds", bold: true }),
          new TextRun({ text: "." })
        ];
      case 8:
        return [
          new TextRun({ text: `Laying on a bench or a bed, ${ageGroup === "Child" ? `parent` : `partner`} will hold feet stable and ${firstName} will lift up to extend back with arms crossed over chest and hold for ` }),
          new TextRun({ text: "1 minute", bold: true }),
          new TextRun({ text: "." })
        ];
      case 9:
        return [
          new TextRun({ text: `Laying on a bench or a bed, ${ageGroup === "Child" ? `parent` : `partner`} will hold feet stable and ${firstName} will lift up to extend back with arms crossed over chest and hold for ` }),
          new TextRun({ text: "2 minutes", bold: true }),
          new TextRun({ text: "." })
        ];
      case 10:
        return [
          new TextRun({ text: `Prior to starting, put on a weighted vest. Laying on a bench or a bed, ${ageGroup === "Child" ? `parent` : `partner`} will hold feet stable, and ${firstName} will lift up to extend back with arms crossed over chest and hold for ` }),
          new TextRun({ text: "1 minute", bold: true }),
          new TextRun({ text: "." })
        ];
      default:
        return [new TextRun({ text: "" })];
    }
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/core/core_pro_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/core/core_pro_2.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/core/core_pro_3.jpeg",
      "/user-forms/doctor-assessment/exercise-images/core/core_pro_4.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/core/core_pro_5.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/core/core_pro_6.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/core/core_pro_7.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/core/core_pro_8.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/core/core_pro_9.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/core/core_pro_10.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/core/core_pro_11.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/core/core_pro_12.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/xjnc6dim1imtith7kowin/Melillo-Core-Exercises-Prone-Bridge-Level-1.mp4?dl=0",
    2: "https://www.dropbox.com/scl/fi/gt7nmg6r9odyv1x600vj1/Melillo-Core-Exercises-Prone-Bridge-Level-2.mp4?dl=0",
    3: "https://www.dropbox.com/scl/fi/c237dkjmg7ibv0h5khb1b/Melillo-Core-Exercises-Prone-Bridge-Level-3.mp4?dl=0",
    4: "https://www.dropbox.com/scl/fi/24mzo24al15ex3f04zao6/Melillo-Core-Exercises-Prone-Bridge-Level-4.mp4?dl=0",
    5: "https://www.dropbox.com/scl/fi/bkipx8y29mtqtlct6o5i5/Melillo-Core-Exercises-Prone-Bridge-Level-5.mp4?dl=0",
    6: "https://www.dropbox.com/scl/fi/a7mm2mqrsv1e1wnfmc9c6/Melillo-Core-Exercises-Prone-Bridge-Level-6.mp4?dl=0",
    7: "https://www.dropbox.com/scl/fi/odlufsym1te79u3uezr83/Melillo-Core-Exercises-Prone-Bridge-Level-7.mp4?dl=0",
    8: "https://www.dropbox.com/scl/fi/yn33sqr3lz1whu9mac588/Melillo-Core-Exercises-Prone-Bridge-Level-8.mp4?dl=0",
    9: "https://www.dropbox.com/scl/fi/t9e6vgk8dxk2ijunyllsq/Melillo-Core-Exercises-Prone-Bridge-Level-9.mp4?dl=0",
    10: "https://www.dropbox.com/scl/fi/i2pcf968u9qqneoe79uu7/Melillo-Core-Exercises-Prone-Bridge-Level-10-V3.mp4?rlkey=ct5wqieznfvotdsmgroq8v1g6&st=lj1wapub&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Prone - Level ${numLevel}: `,
          bold: true,
        }),
      ],
      style: "Heading3",
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: getDescriptionRuns(numLevel),
      style: "BodyText",
    })
  );

  if (imageUrls[numLevel]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[numLevel]);
    content.push(...imageParagraphs);
  }

  // Video link
  const linkUrl = links[numLevel];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Instruction Video: Level ${level}`,
          style: "Hyperlink",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
      })
    );
  }

  return content;
}

async function CSLateral(level) {
  const content = [];
  const numLevel = Number(level);

  const getDescriptionRuns = (level) => {
    switch (level) {
      case 1:
        return [
          new TextRun({ text: "Laying on the side, legs and body should be straight. Both hands on the floor along with bottom arm and elbow. Both feet should be on the ground. Raise the top leg and hold in a stable position. Reverse and do other side. Goal is " }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: " each side." })
        ];
      case 2:
        return [
          new TextRun({ text: "Side plank with bent knees – Both hands on floor along with bottom arm and elbow, knees are bent and lift hips off the ground and hold in a stable position for " }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: " and then switch sides." })
        ];
      case 3:
        return [
          new TextRun({ text: "Legs and body should be straight. Both hands are on the floor along with the bottom arm and elbow. Feet at this level will be with the outside of bottom foot on the floor and inside of top foot also on the floor. Raise hips off the ground to make a lateral bridge, hold in a stable position, reverse and do other side. Goal is " }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: " on each side." })
        ];
      case 4:
        return [
          new TextRun({ text: "Legs and body should be straight. Both hands are on the floor along with the bottom arm and elbow. Feet at this level will be stacked. Raise hips off the ground for a lateral bridge… Hold as long as possible. Reverse and do other side. Goal is " }),
          new TextRun({ text: "60 seconds", bold: true }),
          new TextRun({ text: " each side." })
        ];
      case 5:
        return [
          new TextRun({ text: "Weighted side plank with a weighted vest on: Legs and body should be straight. Both hands are on the floor along with the bottom arm and elbow. Feet at this level will be stacked. Raise hips off the ground for a lateral bridge… Hold as long as possible. Reverse and do other side. Goal is " }),
          new TextRun({ text: "60 seconds", bold: true }),
          new TextRun({ text: " each side." })
        ];
      case 6:
        return [
          new TextRun({ text: "On side, hands on floor along with bottom arm and elbow, feet will start off by being stacked. Then lift the hips and the top leg – the goal is for " }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: ". Then switch sides and do with opposite leg." })
        ];
      case 7:
        return [
          new TextRun({ text: "On side, hands on floor along with bottom arm and elbow, feet will start off by being stacked. Then lift the hips, the top arm and the top leg – the goal is for " }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: ". Then switch sides and do with opposite side." })
        ];
      case 8:
        return [
          new TextRun({ text: "Laying on side with bottom arm extended, lift up and top arm and top leg also extends and hold the position stable for " }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: ", then switch sides." })
        ];
      case 9:
        return [
          new TextRun({ text: "Laying on side with bottom arm extended, lift up and top arm and top leg also extends and hold the position stable for " }),
          new TextRun({ text: "60 seconds", bold: true }),
          new TextRun({ text: ", then switch sides." })
        ];
      case 10:
        return [
          new TextRun({ text: "Weighted Starfish Plank: Prior to starting, put on a weighted vest. Start in the side plank position. From here, come up onto the hand that is closest to the ground. From here, raise top arm straight up to the ceiling and raise the top leg up to make a starfish with their body. Make sure to do whichever side is more challenging first, then match it on the other side to ensure it does not create a muscular imbalance." })
        ];
      default:
        return [new TextRun({ text: "" })];
    }
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/core/core_lat_1.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/core/core_lat_2.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/core/core_lat_3.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/core/core_lat_4.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/core/core_lat_5.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/core/core_lat_6.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/core/core_lat_7.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/core/core_lat_8.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/core/core_lat_9.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/core/core_lat_10.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/jxatk86jrzs8fnhx0nfr4/Melillo-Core-Exercises-Lateral-Side-Plank-Level-1.mp4?rlkey=yjjqdlhmw4cgkevn1ymfevmh1&st=1vpntyrp&dl=0",
    2: "https://www.dropbox.com/scl/fi/iquvc2yiy3roycdvurhyx/Melillo-Core-Exercises-Lateral-Side-Plank-Level-2.mp4?rlkey=3iyajiee3vytdpxv3wdf5t0jc&st=tltf67r6&dl=0",
    3: "https://www.dropbox.com/scl/fi/v9w4v28zz5cvk66hxiphd/Melillo-Core-Exercises-Lateral-Side-Plank-Level-3.mp4?rlkey=s1djrm4hmi7f8bcdr5q4a5v4x&st=xk6nx6dx&dl=0",
    4: "https://www.dropbox.com/scl/fi/sl319je3dntt234d5oqgi/Melillo-Core-Exercises-Lateral-Side-Plank-Level-4.mp4?rlkey=80ej5fq8ccem11vvjygtyqk7n&st=z693ygqd&dl=0",
    5: "https://www.dropbox.com/scl/fi/bzjtwmbm6k6lehz5xgztt/Melillo-Core-Exercises-Lateral-Side-Plank-Level-5-V3.mp4?rlkey=645t5vgc6bjq93qripbisgz3l&st=xlkf41me&dl=0",
    6: "https://www.dropbox.com/scl/fi/wx7z57s8fxv9v6rs2atvt/Melillo-Core-Exercises-Lateral-Side-Plank-Level-6.mp4?rlkey=09cb69u0y8zmpnmtf8mpnqzwg&st=fx3qs5s4&dl=0",
    7: "https://www.dropbox.com/scl/fi/il66ch8ym7q2wmgw3wtep/Melillo-Core-Exercises-Lateral-Side-Plank-Level-7.mp4?rlkey=1oikj8mboqr9hzjcq2s9hb95g&st=ucm1b3qk&dl=0",
    8: "https://www.dropbox.com/scl/fi/tlz3go6x6adfkxy7q4ekq/Melillo-Core-Exercises-Lateral-Side-Plank-Level-8.mp4?rlkey=w5nxmnbq8qt6gy77qk6ix7bpi&st=3z2fofnh&dl=0",
    9: "https://www.dropbox.com/scl/fi/yrlbyzp3krw5tygz1juxn/Melillo-Core-Exercises-Lateral-Side-Plank-Level-9.mp4?rlkey=3ti9ipguptf3xj9s7xu29x6ae&st=8cq7vpd1&dl=0",
    10: "https://www.dropbox.com/scl/fi/wws9igm2d7ch4rit917wr/Melillo-Core-Exercises-Lateral-Side-Plank-Level-10-V3.mp4?rlkey=djlbdj9a5nug0jqr2qw3g3qry&st=7vps1k31&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      children: [
        new TextRun({ text: `Lateral - Level ${numLevel}:`, bold: true }),
      ],
      style: "Heading3",
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: getDescriptionRuns(numLevel),
      style: "BodyText",
    })
  );

  if (imageUrls[numLevel]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[numLevel]);
    content.push(...imageParagraphs);
  }

  // Hyperlink
  const linkUrl = links[numLevel];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Instruction Video: Level ${level}`,
          style: "Hyperlink",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
      })
    );
  }

  return content;
}

async function CSSupine(level, firstName) {
  const content = [];
  const numLevel = Number(level);

  const getDescriptionRuns = (level) => {
    switch (level) {
      case 1:
        return [
          new TextRun({ text: "On the floor face up, feet and arms flat on the floor and knees should be shoulder width apart & arms at the side. Raise back off the floor, hold as long as possible. Goal is " }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: "." })
        ];
      case 2:
        return [
          new TextRun({ text: "On the floor face up, feet and arms flat on the floor and knees should be shoulder width apart & arms at side. Raise back off the floor. Goal is " }),
          new TextRun({ text: "60 seconds", bold: true }),
          new TextRun({ text: "." })
        ];
      case 3:
        return [
          new TextRun({ text: "Starting position is on the floor, laying face up, feet are together, knees are together, and arms are crossed. Raise back off the floor, hold this position. Goal is " }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: "." })
        ];
      case 4:
        return [
          new TextRun({ text: "Starting position is on the floor, laying face up, feet are together, knees are together, and arms are crossed. Raise back off the floor, hold this position. Goal is " }),
          new TextRun({ text: "60 seconds", bold: true }),
          new TextRun({ text: "." })
        ];
      case 5:
        return [
          new TextRun({ text: "Arms at the side, flat on the floor, lift leg (one at a time) and bottom off the ground and hold for " }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: ". Then switch and do on the other side with the other leg." })
        ];
      case 6:
        return [
          new TextRun({ text: "Arms crossed over chest, lift leg (one at a time) and bottom off the ground and hold for " }),
          new TextRun({ text: "60 seconds", bold: true }),
          new TextRun({ text: ". Then switch and do on the other side with the other leg." })
        ];
      case 7:
        return [
          new TextRun({ text: "Laying on the back, arms crossed over chest and feet are on a bosa ball or a box. Lift off the ground to form a bridge and hold for " }),
          new TextRun({ text: "60 seconds", bold: true }),
          new TextRun({ text: "." })
        ];
      case 8:
        return [
          new TextRun({ text: "Laying on the back, arms crossed over chest and feet are on a bosa ball or a box. Lift off the ground to form a bridge & lift leg, hold for 60 seconds & switch to the other leg and hold for " }),
          new TextRun({ text: "60 seconds", bold: true }),
          new TextRun({ text: "." })
        ];
      case 9:
        return [
          new TextRun({ text: "(Supine Bridge with Weight Vest) Laying on back, put a weighted vest on the hip crease/ belt line, then lift up into bridge position and hold for " }),
          new TextRun({ text: "60 seconds", bold: true }),
          new TextRun({ text: "." })
        ];
      case 10:
        return [
          new TextRun({ text: "(Supine Bridge with Weight Vest)Laying on back, put a weighted vest on the hip crease/ belt line, then lift up into bridge position, then lift each leg, one at a time and hold for " }),
          new TextRun({ text: "60 seconds", bold: true }),
          new TextRun({ text: "." })
        ];
      default:
        return [new TextRun({ text: "" })];
    }
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/core/core_sup_1.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/core/core_sup_2.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/core/core_sup_3.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/core/core_sup_4.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/core/core_sup_5.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/core/core_sup_6.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/core/core_sup_7.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/core/core_sup_8.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/core/core_sup_9.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/core/core_sup_10.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/opm6ygcqx07yfav7wcnsp/Melillo-Core-Exercises-Supine-Bridge-Level-1.mp4?rlkey=be862qhbgx56r570vhcex5sr2&st=2pv7a7br&dl=0",
    2: "https://www.dropbox.com/scl/fi/o71cimxoivpn3g0msft9o/Melillo-Core-Exercises-Supine-Bridge-Level-2.mp4?rlkey=tcl3i9mxmwml68f2c52k2frfn&st=40yuro8u&dl=0",
    3: "https://www.dropbox.com/scl/fi/tc67hqmbklkbcibbp6xn9/Melillo-Core-Exercises-Supine-Bridge-Level-3.mp4?rlkey=g28wo7m43iyq9r868f5z9shu7&st=mcdpr9n4&dl=0",
    4: "https://www.dropbox.com/scl/fi/pb1ueo252d1nqh917r6lu/Melillo-Core-Exercises-Supine-Bridge-Level-4.mp4?rlkey=m47yt7zxk1kuzu3jv97hm64as&st=0cftiols&dl=0",
    5: "https://www.dropbox.com/scl/fi/7tffsypmvbx6c4vroane7/Melillo-Core-Exercises-Supine-Bridge-Level-5.mp4?rlkey=660jf9wg94nqqa156u8e23rvd&st=ltyslvhe&dl=0",
    6: "https://www.dropbox.com/scl/fi/eira6rd6tqm1pzbuvkoq3/Melillo-Core-Exercises-Supine-Bridge-Level-6.mp4?rlkey=sddou3e3r6vnuwasn0t3rop7j&st=4d0kky24&dl=0",
    7: "https://www.dropbox.com/scl/fi/0bevpd9fmnxzqisn3zhyc/Melillo-Core-Exercises-Supine-Bridge-Level-7.mp4?rlkey=4jlu8hrhv9uwjtg3fd8hx7bks&st=1h7dp0vf&dl=0",
    8: "https://www.dropbox.com/scl/fi/gza4wrx5wq94zf9zhrra9/Melillo-Core-Exercises-Supine-Bridge-Level-8.mp4?rlkey=5xua04av8lnltkxg6wcszeqh4&st=tv9bmxbz&dl=0",
    9: "https://www.dropbox.com/scl/fi/kfynjvd7d27dy7fau5ea9/Melillo-Core-Exercises-Supine-Bridge-Level-9-V3.mp4?rlkey=w6zhwp29urbzzni49wtkjmyuy&st=bit76s7l&dl=0",
    10: "https://www.dropbox.com/scl/fi/sbxlsm1qbid2k567y29tp/Melillo-Core-Exercises-Supine-Bridge-Level-10-V3.mp4?rlkey=4jmbs9qu86wd05rnsy8otdwif&st=hcpvwm9k&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      children: [new TextRun({ text: `Supine - Level ${numLevel}:`, bold: true })],
      style: "Heading3",
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: getDescriptionRuns(numLevel),
      style: "BodyText",
    })
  );

  if (imageUrls[numLevel]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[numLevel]);
    content.push(...imageParagraphs);
  }

  // Hyperlink
  const linkUrl = links[numLevel];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Instruction Video: Level ${level}`,
          style: "Hyperlink",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
      })
    );
  }

  return content;
}

async function CSCurlUps(level, ageGroup, firstName) {
  const content = [];
  const numLevel = Number(level);

  const getDescriptionRuns = (level) => {
    const parentOrPartner = ageGroup === "Child" ? "parent" : "partner";

    switch (level) {
      case 1:
        return [
          new TextRun({ text: `(with Hands Pull Assist) Laying down and ${parentOrPartner} can sit on legs and child will hold onto ${parentOrPartner}'s thumbs and ${parentOrPartner} can assist in pulling them up to help do a full sit up. ` }),
          new TextRun({ text: "Goal is 30 at a time", bold: true }),
          new TextRun({ text: " as they grow stronger." })
        ];
      case 2:
        return [
          new TextRun({ text: `Laying down and ${parentOrPartner} can sit on legs and child will hold onto ${parentOrPartner}s' thumbs and ${parentOrPartner} can assist in pulling them up to help do a full sit up. ` }),
          new TextRun({ text: "Goal is 50 at a time", bold: true }),
          new TextRun({ text: " to grow stronger and build core strength." })
        ];
      case 3:
        return [
          new TextRun({ text: `Start off with them lying on the back, arms crossed, and knees bent, bring the torso up and touch the arms to the knees and ${parentOrPartner} can assist by helping pull up lightly with the arms, and with control back down to the floor. The goal will be to successfully complete ` }),
          new TextRun({ text: "30 repetitions for 3-5 consecutive sessions", bold: true }),
          new TextRun({ text: " then proceed to the next level." })
        ];
      case 4:
        return [
          new TextRun({ text: `Laying on back, arms are crossed over chest and ${parentOrPartner} can assist in holding feet for stability. Proceed to do a full sit up and back down for ` }),
          new TextRun({ text: "20 times", bold: true }),
          new TextRun({ text: " consistently." })
        ];
      case 5:
        return [
          new TextRun({ text: `Laying on back, arms are crossed over chest and ${parentOrPartner} can assist in holding feet for stability. Proceed to do a full sit up and back down for ` }),
          new TextRun({ text: "40 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 6:
        return [
          new TextRun({ text: "Start in the Sit Up Iso Hold Position, with hands clasped together, rotate through the trunk from side to side. Make sure the hands touch the floor for every rep. The goal is " }),
          new TextRun({ text: "20 reps", bold: true }),
          new TextRun({ text: " for each side consistently before moving onto the next level." })
        ];
      case 7:
        return [
          new TextRun({ text: "Start in the bottom of the sit up position: With arms crossed, have sit all the way up until the arms touch the knees. From there, slowly lower back to the halfway position (about 45 degrees). Maintain the position for as long possible or until the " }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: " is up." })
        ];
      case 8:
        return [
          new TextRun({ text: "Start in the bottom of the sit up position: With arms crossed, sit all the way up until the arms touch the knees. From there, slowly lower back to the halfway position (about 45 degrees). Maintain the position for as long possible or until the " }),
          new TextRun({ text: "60 seconds", bold: true }),
          new TextRun({ text: " is up." })
        ];
      case 9:
        return [
          new TextRun({ text: "Start in the bottom of the sit up position: With arms crossed, sit all the way up until the arms touch the knees. From there, slowly lower back to the halfway position (about 45 degrees). Maintain the position for as long possible or until the " }),
          new TextRun({ text: "2 minutes", bold: true }),
          new TextRun({ text: " is up." })
        ];
      case 10:
        return [
          new TextRun({ text: "(Weighted Iso Hold 1 minute) While wearing a weighted vest, start in the bottom of the sit up position. With arms crossed, sit all the way up until the arms touch the knees. From there, slowly lower back to the halfway position (about 45 degrees). Maintain the position for as long as possible or until 1 minute is up. Once you can successfully complete " }),
          new TextRun({ text: "1 minute without compensation for 5 consecutive sessions", bold: true }),
          new TextRun({ text: ", increase the weight of the vest." })
        ];
      default:
        return [new TextRun({ text: "" })];
    }
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/cu/cu_1.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/cu/cu_2.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/cu/cu_3.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/cu/cu_4.jpeg",
      "/user-forms/doctor-assessment/exercise-images/cu/cu_5.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/cu/cu_6.jpeg",
      "/user-forms/doctor-assessment/exercise-images/cu/cu_7.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/cu/cu_8.jpeg",
      "/user-forms/doctor-assessment/exercise-images/cu/cu_9.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/cu/cu_10.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/cu/cu_11.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/cu/cu_12.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/cu/cu_13.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/anfqe5c8h53lqbi9kkali/Melillo-Core-Exercises-Curl-Ups-Level-1-V2.mp4?rlkey=2xslfkintmr3tgkea76vhvto7&st=j8rlwr0m&dl=0",
    2: "https://www.dropbox.com/scl/fi/anf4jf5qwmn76muxlmp59/Melillo-Core-Exercises-Curl-Ups-Level-2.mp4?rlkey=u3oo4u0np77c5psy7rwzjxzzd&st=qj4zlrg0&dl=0",
    3: "https://www.dropbox.com/scl/fi/ircx6fhwpue7cu6lrsugh/Melillo-Core-Exercises-Curl-Ups-Level-3.mp4?rlkey=jdhvl6v48snu4ztyatw18ycp1&st=yhi54ups&dl=0",
    4: "https://www.dropbox.com/scl/fi/5klc9l7my0kyuose0kz0e/Melillo-Core-Exercises-Curl-Ups-Level-4.mp4?rlkey=mpgvd1xdtn1zau7upxy2x2pdf&st=nvy8ghiw&dl=0",
    5: "https://www.dropbox.com/scl/fi/vwdaxnmyxo79v9iubr2rg/Melillo-Core-Exercises-Curl-Ups-Level-5.mp4?rlkey=xwfesq8dbysxt462b9uz5qcyv&st=m93ccwip&dl=0",
    6: "https://www.dropbox.com/scl/fi/h0m4xrv48xnyv8tugrdy1/Melillo-Core-Exercises-Curl-Ups-Level-6.mp4?rlkey=mcg6ph7iu77oe60plmafrqp71&st=g73wh2ds&dl=0",
    7: "https://www.dropbox.com/scl/fi/jdq3dr58tp4htf78tbg9w/Melillo-Core-Exercises-Curl-Ups-Level-7.mp4?rlkey=acwbhbji3qhbly85ngs7m2km8&st=xsj171hr&dl=0",
    8: "https://www.dropbox.com/scl/fi/z4n5yx4a5yseqbp35mdst/Melillo-Core-Exercises-Curl-Ups-Level-8.mp4?rlkey=dslp24uh860hmq4bw30jhyp8i&st=yz32l4fp&dl=0",
    9: "https://www.dropbox.com/scl/fi/8hrindao2y4u3mlfg1iz2/Melillo-Core-Exercises-Curl-Ups-Level-9.mp4?rlkey=84bfbjr2dlpud8rxqggode4fn&st=a7wchl5h&dl=0",
    10: "https://www.dropbox.com/scl/fi/7q490xsscfdnjmmahsd8z/Melillo-Core-Exercises-Curl-Ups-Level-10-V3.mp4?rlkey=un027hiwaywp0gum21nrm6rqk&st=6jadjecj&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      children: [
        new TextRun({ text: `Curl Ups - Level ${numLevel}:`, bold: true }),
      ],
      style: "Heading3",
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: getDescriptionRuns(numLevel),
      style: "BodyText",
    })
  );

  if (imageUrls[numLevel]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[numLevel]);
    content.push(...imageParagraphs);
  }

  // Hyperlink
  const linkUrl = links[numLevel];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Instruction Video: Level ${level}`,
          style: "Hyperlink",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
      })
    );
  }

  return content;
}

async function CSPushUps(
  level,
  firstName,
  genderSubjectPronoun,
  genderAdjective,
  ageGroup
) {
  const content = [];
  const numLevel = Number(level);

  const getDescriptionRuns = (level) => {
    const parentOrPartner = ageGroup === "Child" ? "Parent" : "Partner";

    switch (level) {
      case 1:
        return [
          new TextRun({ text: `Wheelbarrow Walk supported - ${parentOrPartner} will hold on ankles and lift legs off the ground so there is a straight line in the body. Support the weight of the legs/hips as needed by moving hands closer to the thighs, knees or hips for more support. The goal will be to walk for ` }),
          new TextRun({ text: "20 steps forward", bold: true }),
          new TextRun({ text: " on the hands." })
        ];
      case 2:
        return [
          new TextRun({ text: `Wheelbarrow walk – ${parentOrPartner} will hold on ankles and lift legs off the ground and ${firstName} will walk for ` }),
          new TextRun({ text: "20 steps forward and 20 steps backwards", bold: true }),
          new TextRun({ text: "." })
        ];
      case 3:
        return [
          new TextRun({ text: `Wheelbarrow Plank – ${parentOrPartner} will hold feet up off the ground while ${firstName} is supporting weight on ${genderAdjective} arms. The goal is to hold the position for ` }),
          new TextRun({ text: "60 seconds", bold: true }),
          new TextRun({ text: " stable without falling." })
        ];
      case 4:
        return [
          new TextRun({ text: `Plank in Push Up Position – Hands directly below shoulders and back is straight and hold as long as possible until ${genderSubjectPronoun} becomes unstable daily and tracking the time. Goal will be to hold in ${genderAdjective} position for ` }),
          new TextRun({ text: "60 seconds", bold: true }),
          new TextRun({ text: " maintaining a straight line from head to toe without shaking." })
        ];
      case 5:
        return [
          new TextRun({ text: 'With a box set up in the 20" position, place the hands on the box and the feet on the ground 2-3 steps behind the box. Maintain a straight line in the body from head to toe, from here lower the chest to the box, and then push back up. The goal will be ' }),
          new TextRun({ text: "15 repetitions without compensation for 5 consecutive sessions", bold: true }),
          new TextRun({ text: ", proceed to the next level." })
        ];
      case 6:
        return [
          new TextRun({ text: "Starting in the push up position, bring the knees down to the floor. Maintain a straight line in the body from head to toe. From here lower to touch the chest to the ground, and then press back up to the starting position. The goal will be to successfully complete " }),
          new TextRun({ text: "15 repetitions without compensation for 5 consecutive sessions", bold: true }),
          new TextRun({ text: ", proceed to the next." })
        ];
      case 7:
        return [
          new TextRun({ text: "Hands directly below shoulders and back is straight, lower down to do a full push up. Goal is " }),
          new TextRun({ text: "15 times(without stopping) for 5 sessions", bold: true }),
          new TextRun({ text: " for this level before moving to the next level." })
        ];
      case 8:
        return [
          new TextRun({ text: "Hands directly below shoulders and back is straight, lower down to do a full push up. Goal is " }),
          new TextRun({ text: "25 times", bold: true }),
          new TextRun({ text: " for this level before moving to the next level." })
        ];
      case 9:
        return [
          new TextRun({ text: "Hands directly below shoulders and back is straight, lower down to do a full push up. Goal is " }),
          new TextRun({ text: `${genderSubjectPronoun === "she" ? "30" : "40"} times`, bold: true }),
          new TextRun({ text: " for this level." })
        ];
      case 10:
        return [
          new TextRun({ text: "Weighted Push ups: Put on a weighted vest to get started. From the top of the Push-Up position, lower the chest until it touches the floor and then press back up to the starting position. The goal is to successfully complete " }),
          new TextRun({ text: "40+ repetitions without compensation for 5 consecutive sessions", bold: true }),
          new TextRun({ text: ", increase the weight of the vest." })
        ];
      default:
        return [new TextRun({ text: "" })];
    }
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/psh/psh_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/psh/psh_2.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/psh/psh_3.jpeg",
      "/user-forms/doctor-assessment/exercise-images/psh/psh_4.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/psh/psh_5.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/psh/psh_6.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/psh/psh_7.jpeg",
      "/user-forms/doctor-assessment/exercise-images/psh/psh_8.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/psh/psh_9.jpeg",
      "/user-forms/doctor-assessment/exercise-images/psh/psh_10.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/psh/psh_11.jpeg",
      "/user-forms/doctor-assessment/exercise-images/psh/psh_12.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/psh/psh_13.jpeg",
      "/user-forms/doctor-assessment/exercise-images/psh/psh_14.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/psh/psh_15.jpeg",
      "/user-forms/doctor-assessment/exercise-images/psh/psh_16.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/psh/psh_17.jpeg",
      "/user-forms/doctor-assessment/exercise-images/psh/psh_18.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/ksh98c0hho9on8u32jbab/Melillo-Core-Exercises-Push-Ups-Level-1-V2.mp4?rlkey=4fm9tpuecs09y1hta3yczmct3&st=xboerg8r&dl=0",
    2: "https://www.dropbox.com/scl/fi/vpnoipkf130j0t4kvc5xz/Melillo-Core-Exercises-Push-Ups-Level-2.mp4?rlkey=mjaln09s86eu9hxthss3t2792&st=awq81oaq&dl=0",
    3: "https://www.dropbox.com/scl/fi/1w1z5kc4otllxd7fhil7c/Melillo-Core-Exercises-Push-Ups-Level-3-V2.mp4?rlkey=gh3dx348ch0rcy0kx587nzo5l&st=kdloiq36&dl=0",
    4: "https://www.dropbox.com/scl/fi/alv1g2gun41k6ttazab8h/Melillo-Core-Exercises-Push-Ups-Level-4.mp4?rlkey=k2b34281xjumtja4dao6njaak&st=42dygbe2&dl=0",
    5: "https://www.dropbox.com/scl/fi/q2fd88vmhtmv95axqbzl4/Melillo-Core-Exercises-Push-Ups-Level-5-V3.mp4?rlkey=hxcsnzk5zbtqjhbsn28srnd0j&st=1zf0e2py&dl=0",
    6: "https://www.dropbox.com/scl/fi/2uq30glntepymulbbh0ig/Melillo-Core-Exercises-Push-Ups-Level-6-V2.mp4?rlkey=ojjufxy2henjj7i5otomaeqf2&st=dwcwfflo&dl=0",
    7: "https://www.dropbox.com/scl/fi/k56hp4ugbf8syn7jtv12c/Melillo-Core-Exercises-Push-Ups-Level-7.mp4?rlkey=92n0gpnebi6houb0xtg5vx50i&st=n43rbldq&dl=0",
    8: "https://www.dropbox.com/scl/fi/gzeeg9u9v8xyiu0l9aerf/Melillo-Core-Exercises-Push-Ups-Level-8.mp4?rlkey=tvi3840nb1azb6ut8k32t3r4e&st=09ems4j0&dl=0",
    9: "https://www.dropbox.com/scl/fi/avbi0oyoygjpmn59wyi2h/Melillo-Core-Exercises-Push-Ups-Level-9.mp4?rlkey=gvqjzzqb8rgfupang65d2an4p&st=cvpuwg2h&dl=0",
    10: "https://www.dropbox.com/scl/fi/3g3s8o4h9h3m6ecicqlyw/Melillo-Core-Exercises-Push-Ups-Level-10.mp4?rlkey=2c5b5ytae78l5lseyl4a6vxts&st=sa76xu69&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      children: [
        new TextRun({ text: `Push Ups - Level ${numLevel}:`, bold: true }),
      ],
      style: "Heading3",
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: getDescriptionRuns(numLevel),
      style: "BodyText",
    })
  );

  if (imageUrls[numLevel]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[numLevel]);
    content.push(...imageParagraphs);
  }

  // Hyperlink
  const linkUrl = links[numLevel];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Push Ups: Level ${level} Instruction Video`,
          style: "Hyperlink",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
      })
    );
  }

  return content;
}

async function CSPullUps(
  level,
  firstName,
  ageGroup,
  genderSubjectPronoun,
  genderObjectAdjective,
  genderAdjective
) {
  const content = [];
  const numLevel = Number(level);

  const getDescriptionRuns = (level) => {
    const helpThem = ageGroup === "Child" ? `help ${genderObjectAdjective}` : `${genderSubjectPronoun} will`;
    const parentOrPartner = ageGroup === "Child" ? "parent" : "partner";
    const parentsOrPartners = ageGroup === "Child" ? "parent's" : "partner's";

    switch (level) {
      case 1:
        return [
          new TextRun({ text: `(Supported Bar Hang) Recommend ${ageGroup === "Child" ? "the family" : "you"} purchase a doorway chin up bar for the home program. From the standing position, ${helpThem} reach up and grab the bar with both hands. With arms straight, lift the legs off the ground, maintaining a straight line in the body, and hold that position for ` }),
          new TextRun({ text: "10 seconds", bold: true }),
          new TextRun({ text: `. Support part of ${genderAdjective} body weight by holding the legs.` })
        ];
      case 2:
        return [
          new TextRun({ text: `(Supported Bar Hang) From the standing position, ${helpThem} reach up and grab the bar with both hands. With arms straight, lift the legs off the ground, maintaining a straight line in the body, and hold that position for ` }),
          new TextRun({ text: "10 seconds", bold: true }),
          new TextRun({ text: `. Support part of the body weight by holding the legs. Once ${genderSubjectPronoun} can successfully complete ` }),
          new TextRun({ text: "10 seconds without compensation for 5 consecutive sessions", bold: true }),
          new TextRun({ text: ", proceed to the next level." })
        ];
      case 3:
        return [
          new TextRun({ text: `(Unsupported Bar Hang) From the standing position, ${helpThem} reach up and grab the bar with both hands. With arms straight, lift the legs off the ground, maintaining a straight line in the body, and hold that position for ` }),
          new TextRun({ text: "15 seconds", bold: true }),
          new TextRun({ text: "." })
        ];
      case 4:
        return [
          new TextRun({ text: `(Unsupported Bar Hang) From the standing position, ${helpThem} reach up and grab the bar with both hands. With arms straight, lift the legs off the ground, maintaining a straight line in the body, and hold that position for ` }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: "." })
        ];
      case 5:
        return [
          new TextRun({ text: `(Unsupported Bar Hang) From the standing position, ${helpThem} reach up and grab the bar with both hands. With arms straight, lift the legs off the ground, maintaining a straight line in the body, and hold that position for ` }),
          new TextRun({ text: "60 seconds", bold: true }),
          new TextRun({ text: "." })
        ];
      case 6:
        return [
          new TextRun({ text: `(45 Degree Ring Row) Use home stretch wraps or ring - set up rings so they are at the hip height level. Stand with feet directly under the pull up bar and pull the rings tightly to the chest. With ${parentsOrPartners} support, lower the body down until their arms are fully extended and body is at a ` }),
          new TextRun({ text: "45 degree angle", bold: true }),
          new TextRun({ text: ". Maintain a straight line with the body from head to toe. Pull the body with hands in pronated position until the rings touch the chest, then lower back down with control. The goal is to successfully complete " }),
          new TextRun({ text: "15 reps without compensation for 5 consecutive sessions", bold: true }),
          new TextRun({ text: ", proceed to the next level." })
        ];
      case 7:
        return [
          new TextRun({ text: "(Flexed Arm Hang, 30 Sec) Start by grabbing the bar and jumping or pulling up so the arms are fully flexed and the chin is above the bar. Maintain this position and hang for " }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: ". Be sure not to rest their chin on the bar. The goal for this level is to successfully hang for " }),
          new TextRun({ text: "30 seconds without compensation for 3-5 consecutive sessions", bold: true }),
          new TextRun({ text: ", proceed to the next level." })
        ];
      case 8:
        return [
          new TextRun({ text: `(Supported Pull Ups, 10 Reps) From the standing position, reach up and grab the bar with both hands. If using the TheraBand's, step onto the band with one leg while bringing the other leg in front to secure the band in place. If a ${parentOrPartner} is supporting manually, ${genderSubjectPronoun} will bend their knees so the feet are off the ground and the ${parentOrPartner} should hold their feet and support part of the body weight. Pull body up until their chin is above the bar and then control body weight back down to the hanging position with arms straight.` })
        ];
      case 9:
        return [
          new TextRun({ text: "(Right Angle Pull Ups, 10 Reps) Starting from the hanging position, maintaining a straight line in the body, pull body up until the chin is at or above the bar, then control bodyweight back down until " }),
          new TextRun({ text: "a right angle or 90 degree angle", bold: true }),
          new TextRun({ text: " is attained in the elbow. From here pull back up until the chin is at or over the bar and repeat for " }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 10:
        return [
          new TextRun({ text: "(Pull Ups, 10) Starting from the hanging position, maintaining a straight line in the body, pull body up until the chin is at or above the bar, then control the bodyweight back down to the hanging position." })
        ];
      default:
        return [new TextRun({ text: "" })];
    }
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/pul/pul_1.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/pul/pul_2.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/pul/pul_3.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/pul/pul_4.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/pul/pul_5.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/pul/pul_6.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pul/pul_7.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/pul/pul_8.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/pul/pul_9.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pul/pul_10.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/pul/pul_11.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pul/pul_12.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/pul/pul_13.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pul/pul_14.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/e89nqomvz6vwcnbr7e8hy/Melillo-Core-Exercises-Pull-Ups-Level-1-V2.mp4?rlkey=sulvmu4iiu4umxp9sjtz2irkx&st=0s60zni5&dl=0",
    2: "https://www.dropbox.com/scl/fi/znflkq72k5dgkn2nuamd5/Melillo-Core-Exercises-Pull-Ups-Level-2.mp4?rlkey=7a8gswuc6up9ofomvv93qegpp&st=t1smx7l3&dl=0",
    3: "https://www.dropbox.com/scl/fi/w3wx837zsdpyh6mj47tha/Melillo-Core-Exercises-Pull-Ups-Level-3.mp4?rlkey=txtxg1wehddy4cyfk63j3wmg0&st=94vi0i6f&dl=0",
    4: "https://www.dropbox.com/scl/fi/pd8bxmhlqak0w3lriqwh5/Melillo-Core-Exercises-Pull-Ups-Level-4.mp4?rlkey=e689i8x3gqpn8ikbsouz6vbdw&st=335cas6k&dl=0",
    5: "https://www.dropbox.com/scl/fi/23q8rpfy2znrfudtgybt3/Melillo-Core-Exercises-Pull-Ups-Level-5.mp4?rlkey=iyrywtjk0qjq9nerai905vfbg&st=6afnuh5a&dl=0",
    6: "https://www.dropbox.com/scl/fi/x2px49qsg403wbb5azqpa/Melillo-Core-Exercises-Pull-Ups-Level-6-V2.mp4?rlkey=wokjl73kgku9vknirimla9rgz&st=p1pfcdwh&dl=0",
    7: "https://www.dropbox.com/scl/fi/9f10s2vx5g7183c30gk2j/Melillo-Core-Exercises-Pull-Ups-Level-7.mp4?rlkey=q8ob4z5l22qyixqaxjn0w0hh2&st=sa911yrz&dl=0",
    8: "https://www.dropbox.com/scl/fi/aid0ggrja43so0rwlp6u6/Melillo-Core-Exercises-Pull-Ups-Level-8-V2.mp4?rlkey=dlkavf5plyjv315dnb2g3s83n&st=7vzs9woy&dl=0",
    9: "https://www.dropbox.com/scl/fi/o8c5cjug9w7oolla5hm8g/Melillo-Core-Exercises-Pull-Ups-Level-9-V2.mp4?rlkey=t69kvlwy72wfhj1458663inew&st=k66fyp4i&dl=0",
    10: "https://www.dropbox.com/scl/fi/54no8o0260rocz2uo4t2p/Melillo-Core-Exercises-Pull-Ups-Level-10-V2.mp4?rlkey=37vjmckpyjlf2m9wzvj0x4ku1&st=j6srdcs1&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      children: [
        new TextRun({ text: `Pull Ups - Level ${numLevel}:`, bold: true }),
      ],
      style: "Heading3",
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: getDescriptionRuns(numLevel),
      style: "BodyText",
    })
  );

  if (imageUrls[numLevel]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[numLevel]);
    content.push(...imageParagraphs);
  }

  // Hyperlink
  const linkUrl = links[numLevel];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Instruction Video: Level ${level}`,
          style: "Hyperlink",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
      })
    );
  }

  return content;
}

async function LSEyelights(
  eyelights,
  firstName,
  isReevaluation
) {
  const content = [];

  const getDescriptionRuns = (eyeLight) => {
    if (eyeLight === "Blue") {
      if (isReevaluation) {
        return [
          new TextRun({ text: `${firstName} is recommended to continue to use the Eyelights as part of the daily home program.  These should be worn at least ` }),
          new TextRun({ text: "20 minutes, twice daily but increasing the time every 2 weeks by 10 minutes to the goal of 60 minutes twice daily", bold: true }),
          new TextRun({ text: ` combined with the TENS unit and Brain driver at the same time.` })
        ];
      } else {
        return [
          new TextRun({ text: `Blue Eyelights are used for a right brain deficit to be used as part of the daily home program.  They will be set up to blink in the left half of each eye (when looking out the glasses, the left half of each eye will be blinking on the top and bottom).  They should be worn for ` }),
          new TextRun({ text: "20 minutes twice daily and increase the time every 2 weeks by 10 minutes to the maximum time of 60 minutes, twice daily", bold: true }),
          new TextRun({ text: " combined with the TENS unit and Driver." })
        ];
      }
    } else if (eyeLight === "Red") {
      return [
        new TextRun({ text: `Red Eyelights are used for a left brain deficit to be used as part of the daily home program.  They will be set up to blink in the right half of each eye (when looking out the glasses, the right half of each eye will be blinking on the top and bottom).  They should be worn for ` }),
        new TextRun({ text: "20 minutes twice daily and increase the time every 2 weeks by 10 minutes to the maximum time of 60 minutes, twice daily", bold: true }),
        new TextRun({ text: " combined with the TENS unit and Brain driver." })
      ];
    }
    return [];
  };

  const imageUrls = {
    "Blue": [
      "/user-forms/doctor-assessment/exercise-images/eyelights_blue.jpeg",
    ],
    "Red": [
      "/user-forms/doctor-assessment/exercise-images/eyelights_red.jpeg",
    ],
  };

  const instructionVideoUrl =
    "https://www.dropbox.com/scl/fi/toaezglkx9ldqhn3ffqu9/Melillo-Eyelights-Intructions.mp4?rlkey=qcmxw2sn1jzmihz7wjet8dtgq&st=tlg44xve&dl=0";

  for (let eyeLight of eyelights) {
    const descriptionRuns = getDescriptionRuns(eyeLight);
    if (descriptionRuns.length === 0) continue;

    // Title
    content.push(
      new Paragraph({
        text: `Eyelights (${eyeLight})`,
        style: "Heading3",
      })
    );

    // Description
    content.push(
      new Paragraph({
        children: descriptionRuns,
        style: "BodyText",
        alignment: AlignmentType.LEFT,
      })
    );

    if (imageUrls[eyeLight]) {
      const imageParagraphs = await createImageParagraphs(imageUrls[eyeLight]);
      content.push(...imageParagraphs);
    }

    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Eyelight Instruction Video`,
          style: "Hyperlink",
        }),
      ],
      link: instructionVideoUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
        spacing: { after: 10 },
      })
    );
  }

  return content;
}

function SoundTherapy(
  value,
  firstName,
  ageGroup,
  genderAdjective,
  genderSubjectPronoun,
  program
) {
  const content = [];

  const descriptions = {
    "Brain Balance Music": `${ageGroup === "Child" ? "The parents" : "You"
      } should purchase the Brain Balance music for the ${program} brain online at `,

    "Metro Timer": `${ageGroup === "Child" ? firstName : "You"
      } can also use the Metro Timer app downloaded onto a phone as part of the daily home program. ${ageGroup === "Child" ? genderSubjectPronoun : "You"
      } will be using it at 54 BPM and hold the phone about 6–8 inches away from ${ageGroup === "Child" ? genderAdjective : "your"
      } ${program === "left" ? "right" : "left"
      } ear and allow the sound to play for 30 seconds, 1 to 2 times per day. ${ageGroup === "Child" ? genderSubjectPronoun : "You"
      } can also use the flashing light from this app, having it blink in the outside corner of ${ageGroup === "Child" ? genderAdjective : "your"
      } ${program === "left" ? "right" : "left"
      } eye for 30 seconds, 1 to 2 times per day.`,
  };

  // Skip if no description (e.g., Tonal Therapy)
  if (!descriptions[value]) return [];

  // Title
  content.push(
    new Paragraph({
      text: value,
      style: "Heading3",
    })
  );

  // Description
  if (value === "Brain Balance Music") {
    content.push(
      new Paragraph({
        children: [
          new TextRun({ text: descriptions[value] }),
          new ExternalHyperlink({
            children: [
              new TextRun({
                text: "www.brainbalancemusic.com",
                style: "HyperlinkStyle",
              }),
            ],
            link: "https://www.brainbalancemusic.com",
          }),
          new TextRun({ text: `. ${ageGroup === "Child" ? firstName : "You"} should listen to the music in just the ${program === "left" ? "right" : "left"} ear with just 1 ear bud in or using just one headphone for 30 minutes for 1 to 2 times daily.` }),
        ],
        spacing: { before: 120, after: 120 },
        indent: { left: 400, right: 200 },
        alignment: AlignmentType.JUSTIFIED,
      })
    );
  } else {
    content.push(
      new Paragraph({
        children: [new TextRun({ text: descriptions[value], break: 1 })],
        style: "BodyText",
      })
    );
  }

  // Additional links for Brain Balance Music
  if (value === "Brain Balance Music") {
    // Main site link
    const link1 = new ExternalHyperlink({
      children: [
        new TextRun({
          text: "Brain Balance Music Website",
          style: "Hyperlink",
        }),
      ],
      link: "https://www.brainbalancemusic.com",
    });

    content.push(
      new Paragraph({
        children: [link1],
        spacing: { after: 10 },
      })
    );

    // Spotify playlist
    content.push(
      new Paragraph({
        text: `Additional ${program} brain music recommendations:`,
        heading: HeadingLevel.HEADING_4,
        spacing: { before: 100, after: 50 },
      })
    );

    const link2 = new ExternalHyperlink({
      children: [
        new TextRun({
          text: "Spotify Playlist",
          style: "Hyperlink",
        }),
      ],
      link: "https://open.spotify.com/playlist/0nWjBFv6DiCqN0mPn5VAgr?si=EaVev-aIQLux78uYJ1A-Ug&nd=1&dlsi=b358016764e14dee",
    });

    content.push(
      new Paragraph({
        children: [link2],
        alignment: AlignmentType.CENTER,
        spacing: { after: 10 },
      })
    );
  }

  return content;
}

async function InteractiveMetronome(
  firstName,
  genderSubjectPronoun,
  isReevaluation
) {
  const content = [];

  content.push(
    new Paragraph({
      text: "Interactive Metronome",
      style: "Heading3",
    })
  );

  const descriptionRuns = [
    new TextRun({
      text: `We will be recommending that ${firstName} ${isReevaluation ? "continues to " : ""
        }use the `,
    }),

    new ExternalHyperlink({
      link:
        "https://d.docs.live.net/8816061ae35f8897/Documents/Melillo%20Misc/Home%20IM%20Ordering%20Information.docx",
      children: [
        new TextRun({
          text: "Home Interactive Metronome Unit",
          style: "HyperlinkStyle",
        }),
      ],
    }),

    new TextRun({
      text: ` also as part of the daily home program. This is an excellent piece of equipment that can be used throughout all phases of the home program. ${genderSubjectPronoun} will be using the Melillo Method Home protocols and we can set up and monitor the progress remotely as the progress continues through the program.`,
    }),
  ];

  content.push(
    new Paragraph({
      children: descriptionRuns,
      style: "BodyText",
    })
  );

  const imageUrls = [
    "/user-forms/doctor-assessment/exercise-images/interactive_metronome_1.jpg",
    "/user-forms/doctor-assessment/exercise-images/interactive_metronome_2.jpg",
  ];

  const imageParagraphs = await createImageParagraphs(imageUrls);
  content.push(...imageParagraphs);

  return content;
}

async function Occulomotor(level, program, ageGroup) {
  const content = [];

  const getDescriptionRuns = (level) => {
    const cogBuilderLink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: "CogBuilder",
          underline: {}, color: "3498DB"
        }),
      ],
      link: "https://dr-robert-melillo.myshopify.com/products/cogbuilder-home-therapy-program",
    });

    switch (level) {
      case 1:
        return [
          new TextRun({ text: `Will use the OKN App after downloading: At the speed of 14 and stripes or images (as explained in the instruction video above) turning the phone horizontal for left/right movement and vertical for up/down movement. ${ageGroup === 'Child' ? "Parent" : "Partner"} will observe the nystagmus (beating of the eyes) if they are fixated on the images.  The goal for Level 1 is to focus on the screen long enough for this to happen consistently. For a ${program} brain program the images should be moving to the ${program} and ${program === 'right' ? 'down' : 'up'} for ` }),
          new TextRun({ text: "10 seconds", bold: true }),
          new TextRun({ text: " in each direction. OKN app for " }),
          new TextRun({ text: "10 seconds", bold: true }),
          new TextRun({ text: " in each direction. Also, use a small mirror for convergence eye exercises as explained in the video below. " }),
        ];
      case 2:
        return [
          new TextRun({ text: `Will use the OKN App after downloading: At the speed of 14 and stripes or images (as explained in the instruction video above) turning the phone horizontal for left/right movement and vertical for up/down movement. ${ageGroup === 'Child' ? "Parent" : "Partner"} will observe the nystagmus (beating of the eyes) if they are fixated on the images. For a ${program} brain program the images should be moving to the ${program} and ${program === 'right' ? 'down' : 'up'} for ` }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: ` in each direction.OKN app (` }),
          new TextRun({ text: `30 seconds `, bold: true }),
          new TextRun({ text: " in each direction).pencil push ups (10 reps)." }),
        ];
      case 3:
        return [
          new TextRun({ text: `Will use the OKN App after downloading: At the speed of 14 and stripes or images (as explained in the instruction video above) turning the phone horizontal for left/right movement and vertical for up/down movement. ${ageGroup === 'Child' ? "Parent" : "Partner"} will observe the nystagmus (beating of the eyes) if they are fixated on the images. For a ${program} brain program the images should be moving to the ${program} and ${program === 'right' ? 'down' : 'left'} for ` }),
          new TextRun({ text: "30 seconds", bold: true }),
          new TextRun({ text: ` in each direction. OKN app (` }),
          new TextRun({ text: `30 seconds`, bold: true }),
          new TextRun({ text: ` in each direction). Also, use a string for the Brock String eye exercises as explained in the video below for 10 reps.` })
        ];
      case 4:
        return [
          new TextRun({ text: `This level of the Occulomotor Activities will include a computer program at home called CogBuilder. The cogBuilder program can be purchased in our office or online: ` }),
          cogBuilderLink,
          new TextRun({ text: `. And follow the instructions below – for Level 4, we will be using the following activities as described in the instruction video: (Tennis (level 1-2), line tracing (level 1-2), saccades (level 1-2), randot duction (1min - convergence and divergence)).` })
        ];
      case 5:
        return [
          new TextRun({ text: `This level of the Occulomotor Activities will include a computer program at home called CogBuilder. The cogBuilder program can be purchased in our office or online: ` }),
          cogBuilderLink,
          new TextRun({ text: `. And follow the instructions below – for Level 5, (Tennis (level 3), line tracing (level 3), saccades (level 3-4), randot (2 min - convergence and divergence), reaction time (level 1-2), visual memory (level 1-2)).` })
        ];
      case 6:
        return [
          new TextRun({ text: `This level of the Occulomotor Activities will include a computer program at home called CogBuilder. The cogBuilder program can be purchased online: ` }),
          cogBuilderLink,
          new TextRun({ text: `. And follow the instructions below – for Level 6, (Tennis (level 4), randot (3 min - convergence and divergence), line tracing (level 4), saccades (level 5-6), reaction time (level 3-4), visual memory (level 3)).` })
        ];
      case 7:
        return [
          new TextRun({ text: `Level 7 of the Occuolomotor activities will continue with the CogBuilder Home program for these activities: (Tennis (level 5), randot (4 min - convergence and divergence), line tracing (level 5), saccades (level 7), reaction time (level 5-6), visual memory (level 4).` })
        ];
      case 8:
        return [
          new TextRun({ text: `Level 8 of the Oculomotor Activities will use the CogBuilder Program for these activities: Tennis (level 6-7), randot (5 min - convergence and divergence), saccades (level 8), reaction time (level 7), visual memory (level 5).` })
        ];
      case 9:
        return [
          new TextRun({ text: `Level 9 of the Occuolomotor Activities will continue with the Cog Builder program for these activities: (Randot (5 min - convergence and divergence), reaction time (level 8), visual memory (level 6).` })
        ];
      case 10:
        return [
          new TextRun({ text: `Continue using the CogBuilder Program for these activities: Reaction time (level 9), visual memory (level 7).` })
        ];
      default:
        return [];
    }
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/occ/occ_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_2.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_3.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_4.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/occ/occ_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_2.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_5.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_6.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/occ/occ_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_2.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_7.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_8.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/occ/occ_9.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_10.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_11.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_12.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/occ/occ_13.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_11.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_14.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_15.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_16.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/occ/occ_14.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_17.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_11.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_15.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_18.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/occ/occ_19.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_14.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_20.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_11.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_15.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_21.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/occ/occ_19.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_14.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_11.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_15.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_22.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/occ/occ_14.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_15.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_23.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/occ/occ_15.jpeg",
      "/user-forms/doctor-assessment/exercise-images/occ/occ_24.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/voqeutujbadwiql16zkzn/Melillo-Visual-Testing-Oculomotor-Treatment-L1-V2.mp4?rlkey=o7lj6xernub6jvctp9f0ab0bb&st=ajw4yie7&dl=0",
    2: "https://www.dropbox.com/scl/fi/asdw7f71t0f0xf24eqzc3/Melillo-Visual-Testing-Oculomotor-Treatment-L2-V2.mp4?rlkey=erry25tut0e6ea4211quapzr8&st=7bb78zwc&dl=0",
    3: "https://www.dropbox.com/scl/fi/gsqf6t8ik2786cb28l8qu/Melillo-Visual-Testing-Oculomotor-Treatment-L3-V2.mp4?rlkey=48gnw6sju36lvco60pcrput4p&st=la4sph19&dl=0",
    4: "https://www.dropbox.com/scl/fi/nksr30u56x1ayw7m9z4l6/Melillo-Visual-Testing-Oculomotor-Treatment-L4-V2.mp4?rlkey=azmkudmo1trup6ea20sh55d70&st=n6fhkfia&dl=0",
    5: "https://www.dropbox.com/scl/fi/500yo005m897jwfw6b4pg/Melillo-Visual-Testing-Oculomotor-Treatment-L5-V2.mp4?rlkey=uyfavkr0chrcw6ux6ugjurqhi&st=gva6dao5&dl=0",
    6: "https://www.dropbox.com/scl/fi/8lnbu1iynt6c670w4jdwo/Melillo-Visual-Testing-Oculomotor-Treatment-L6-V2.mp4?rlkey=luxavc8t4jt6iphgtvxk9ll2u&st=ioxkxhlw&dl=0",
    7: "https://www.dropbox.com/scl/fi/1vivrffypoizo04slgi15/Melillo-Visual-Testing-Oculomotor-Treatment-L7-V2.mp4?rlkey=18kn8pkvo9z0y84trzx41x5t1&st=ssuqk69s&dl=0",
    8: "https://www.dropbox.com/scl/fi/yuu4pehhzzapycf0he0yx/Melillo-Visual-Testing-Oculomotor-Treatment-L8-V2.mp4?rlkey=j2dvcb3tbjjepqji43ufz383h&st=2c74ozpy&dl=0",
    9: "https://www.dropbox.com/scl/fi/21ioine03bnbzadnfgyju/Melillo-Visual-Testing-Oculomotor-Treatment-L9-V2.mp4?rlkey=rg4lzrtpg1y391jch0603ibx1&st=d78z2xoz&dl=0",
    10: "https://www.dropbox.com/scl/fi/71ydw63saktzjri2bg0tr/Melillo-Visual-Testing-Oculomotor-Treatment-L10-V2.mp4?rlkey=ekr7cg2myn757h8qas5hhgz9p&st=nk55f4wn&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      children: [new TextRun({ text: `Level ${level}:`, bold: true })],
      style: "Heading3",
    })
  );

  // Description
  const descriptionRuns = getDescriptionRuns(level);
  if (descriptionRuns && descriptionRuns.length > 0) {
    content.push(
      new Paragraph({
        children: descriptionRuns,
        style: "BodyText",
      })
    );
  }

  if (imageUrls[level]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[level]);
    content.push(...imageParagraphs);
  }

  // Hyperlink
  const linkUrl = links[level];
  if (linkUrl) {
    const linkTexts = {
      1: "Level 1 Occulomotor Instructions",
      2: "Level 2 Occulomotor Instruction Videos for OPK and Pencil Push Up Activities",
      3: "Level 3 Occulomotor Instructions",
      4: "Level 4 Occulomotor Instructions",
      5: "Level 5  - Cog Builder Program for Occulomotor Activities",
      6: "Level 6 Occulomotor Instructions",
      7: "Level 7 Occulomotor CogBuilder Instructions",
      8: "Level 8 Occulomotor Instructions",
      9: "Level 9 Occulomotor Instructions",
      10: "Level 10 Occulomotor Instructions",
    };

    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: linkTexts[level] || `Level ${level} Occulomotor Instructions`,
          style: "Hyperlink",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
        spacing: { after: 10 },
      })
    );
  }

  return content;
}


async function Olfactory(level, genderAdjective, genderSubjectPronoun, ageGroup, program, genderObjectAdjective, firstName) {
  const content = [];

  const getDescriptionRuns = (level) => {
    const haveOrWill = ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`;
    const parentOrPartner = ageGroup === "Child" ? "parent's" : "partner's";

    switch (level) {
      case 1:
        return [
          new TextRun({ text: `No Sniff: Present a smell by holding the essential oil in front of ${genderAdjective} nose. At this level, we do not expect ${genderObjectAdjective} to sniff and ${genderSubjectPronoun} may turn away or resist in some way, such as pushing the ${parentOrPartner} hand away. Place a drop of the essential oil on ${genderAdjective} shirt after this exercise. Place the smell on the ${program} side of ${genderAdjective} shirt. Once ${genderSubjectPronoun} can tolerate being exposed to a smell with no resistance ` }),
          new TextRun({ text: "3 sessions in a row", bold: true }),
          new TextRun({ text: `, move to the next level.` })
        ];
      case 2:
        return [
          new TextRun({ text: `Lean in: Present a smell by holding the essential oil in front of ${genderAdjective} nose. Encourage ${genderObjectAdjective} to sniff the smell by providing verbal cues and demonstration. At this level, ${genderSubjectPronoun} may lean in but make no attempt to sniff or attempt to sniff incorrectly (e.g., blowing air out of the nose instead of sniffing in). Place a drop of the essential oil on ${genderAdjective} shirt after this exercise. Place the smell on the ${program} side of ${genderAdjective} shirt. Once ${genderSubjectPronoun} lean in and attempt to smell for ` }),
          new TextRun({ text: "3 sessions in a row", bold: true }),
          new TextRun({ text: `, move to the next level.` })
        ];
      case 3:
        return [
          new TextRun({ text: `Sniff without recognition: Present a smell by holding the essential oil in front of ${genderAdjective} nose. Encourage ${genderObjectAdjective} to sniff the smell by providing verbal cues and demonstration. At this level, ${genderSubjectPronoun} should be able to sniff consistently but may not be able to identify the smell. If ${genderSubjectPronoun} are able to guess the smell, ${genderObjectAdjective} may do so incorrectly 75% of the time. Place a drop of the essential oil on ${genderAdjective} shirt after this exercise. Place the smell on the ${program} side of ${genderAdjective} shirt. Once ${genderSubjectPronoun} is able to consistently sniff when presented with a smell for ` }),
          new TextRun({ text: "3 sessions in a row", bold: true }),
          new TextRun({ text: `, move to the next level.` })
        ];
      case 4:
        return [
          new TextRun({ text: `Sniff with forced choice identification: Present 2 smells by holding the essential oil in front of ${genderAdjective} nose. ${haveOrWill} smell each one and give 2 options of what the smell may be. Ask which of the 2 options the one ${genderSubjectPronoun} just smelled is (e.g., "Is this lemon or mint?"). Be sure to mix up the order in which you give the options to ensure accuracy and reliability in ${genderAdjective} answers as ${genderSubjectPronoun} may repeat the last word ${genderSubjectPronoun} hear. Place a drop of the essential oil on ${genderAdjective} shirt after this exercise. Place the smell on the ${program} side of ${genderAdjective} shirt. Once ${genderSubjectPronoun} is able to correctly identify the smell for ` }),
          new TextRun({ text: "3 trials, 3 sessions in a row", bold: true }),
          new TextRun({ text: `, move to the next level.` })
        ];
      case 5:
        return [
          new TextRun({ text: `Sniff and identity without forced choice (2 smells): Present 2 smells by holding the essential oil in front of ${genderAdjective} nose. ${haveOrWill} smell each one and ask to identify what ${genderSubjectPronoun} smelled without giving any options as to what the smell may be. Place a drop of the essential oil on ${genderAdjective} shirt after this exercise. Place the smell on the ${program} side of ${genderAdjective} shirt. Once ${genderSubjectPronoun} is able to correctly identify ` }),
          new TextRun({ text: "2 smells for 3 sessions in a row", bold: true }),
          new TextRun({ text: `, move to the next level.` })
        ];
      case 6:
        return [
          new TextRun({ text: `Sniff and identity without forced choice (3 smells): Present 3 smells by holding the essential oil in front of ${genderAdjective} nose. ${haveOrWill} smell each one and ask to identify what ${genderSubjectPronoun} smelled without giving ${genderObjectAdjective} any options. Place a drop of the essential oil on ${genderAdjective} shirt after this exercise. Place the smell on the ${program} side of ${genderAdjective} shirt. Once ${genderSubjectPronoun} is able to correctly identify ` }),
          new TextRun({ text: "3 smells for 3 sessions in a row", bold: true }),
          new TextRun({ text: `, move to the next level.` })
        ];
      case 7:
        return [
          new TextRun({ text: `Sniff and identity without forced choice (5 smells): Present 5 smells by holding the essential oil in front of ${genderAdjective} nose. ${haveOrWill} smell each one and ask ${genderObjectAdjective} to identify what ${genderSubjectPronoun} smelled without giving ${genderObjectAdjective} any options. Place a drop of the essential oil on ${genderAdjective} shirt after this exercise. Place the smell on the ${program} side of ${genderAdjective} shirt. Once ${genderSubjectPronoun} is able to correctly identify ` }),
          new TextRun({ text: "5 smells for 3 sessions in a row", bold: true }),
          new TextRun({ text: `, move to the next level.` })
        ];
      case 8:
        return [
          new TextRun({ text: `Sniff and identity smells only on weak side (5 smells): Present 5 smells by holding the essential oil only on the weak side. Present the smell towards the ${program} nostril. ${haveOrWill} smell each one and ask to identify what ${genderSubjectPronoun} smelled without giving ${genderObjectAdjective} any options. Place a drop of the essential oil on ${genderAdjective} shirt after this exercise. Place the smell on the ${program} side of ${genderAdjective} shirt. Once ${genderSubjectPronoun} is able to correctly identify ` }),
          new TextRun({ text: "5 smells for 3 sessions in a row", bold: true }),
          new TextRun({ text: `, move to the next level.` })
        ];
      case 9:
        return [
          new TextRun({ text: `Sniff and identity smells only on weak side (6 smells): Present 6 smells by holding the essential oil only on the weak side. Present the smell towards the ${program} nostril. ${haveOrWill} smell each one and ask to identify what ${genderSubjectPronoun} smelled without giving ${genderObjectAdjective} any options. Place a drop of the essential oil on ${genderAdjective} shirt after this exercise. Place the smell on the ${program} side of ${genderAdjective} shirt. Once ${genderSubjectPronoun} is able to correctly identify ` }),
          new TextRun({ text: "6 smells for 3 sessions in a row", bold: true }),
          new TextRun({ text: `, move to the next level.` })
        ];
      case 10:
        return [
          new TextRun({ text: `Sniff and identity smells only on weak side (7 smells): Present 7 smells by holding the essential oil only on the weak side. Present the smell towards the ${program} nostril. ${haveOrWill} smell each one and ask to identify what ${genderSubjectPronoun} smelled without giving ${genderObjectAdjective} any options. Place a drop of the essential oil on ${genderAdjective} shirt after this exercise. Place the smell on the ${program} side of ${genderAdjective} shirt. Once ${genderSubjectPronoun} is able to correctly identify ` }),
          new TextRun({ text: "7 smells for 3 sessions in a row", bold: true }),
          new TextRun({ text: `, move to the next level.` })
        ];
      default:
        return [];
    }
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/olf/olf_1.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/olf/olf_2.jpeg",
      "/user-forms/doctor-assessment/exercise-images/olf/olf_3.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/olf/olf_4.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/olf/olf_5.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/olf/olf_6.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/olf/olf_7.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/olf/olf_8.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/olf/olf_9.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/olf/olf_10.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/olf/olf_11.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/7umoheloq0tawe95jxkf5/Melillo-Olfactory-Stim-Asssesment-Level-01.mp4?rlkey=uampgwe8ynjodt7lc09237lk5&st=paq65wn8&dl=0",
    2: "https://www.dropbox.com/scl/fi/fyqq0uptftfz4gj7v42j9/Melillo-Olfactory-Stim-Asssesment-Level-02.mp4?rlkey=yi2hezq86ndv3f3bl6b5dmr09&e=1&st=dnx89ahi&dl=0",
    3: "https://www.dropbox.com/scl/fi/srspu6mynz5dktoeg2mdp/Melillo-Olfactory-Stim-Asssesment-Level-03.mp4?rlkey=l1nsp6wq4rx1j6u05e6dxtrqy&e=1&st=is3fr5ds&dl=0",
    4: "https://www.dropbox.com/scl/fi/shw2zjzodm6mx4u4y6ap3/Melillo-Olfactory-Stim-Asssesment-Level-04.mp4?rlkey=18ygyztrny1wci40rf6sudqks&e=1&st=pgd0wq13&dl=0",
    5: "https://www.dropbox.com/scl/fi/bdrt7u2fd3yplx0kv3b08/Melillo-Olfactory-Stim-Asssesment-Level-05.mp4?rlkey=ici8xf05qm2vyzlc22klfllw5&e=1&st=mkodybq6&dl=0",
    6: "https://www.dropbox.com/scl/fi/vpq06wwcnn0pv15ysqfsd/Melillo-Olfactory-Stim-Asssesment-Level-06.mp4?rlkey=4i32xhhv8pm5xjzlyp35s5hhi&e=1&st=6mmucba7&dl=0",
    7: "https://www.dropbox.com/scl/fi/iaxtdondaemnuvps4gf9r/Melillo-Olfactory-Stim-Asssesment-Level-07.mp4?rlkey=qlimgkn0rozzdcod6v2yiop22&e=1&st=ju0x6y6t&dl=0",
    8: "https://www.dropbox.com/scl/fi/n3d8fe7q21e2tys1xhutm/Melillo-Olfactory-Stim-Asssesment-Level-08.mp4?rlkey=ol1enha2hsui7b6isjqr46fy3&e=1&st=6cc9w2v3&dl=0",
    9: "https://www.dropbox.com/scl/fi/33iy63pdevnxjbxa5327k/Melillo-Olfactory-Stim-Asssesment-Level-09.mp4?rlkey=di9p818f1stynlja5ejsourlq&st=t2r2cuu9&dl=0",
    10: "https://www.dropbox.com/scl/fi/0sgbdfjfxqhasl7dlsmou/Melillo-Olfactory-Stim-Asssesment-Level-10.mp4?rlkey=u5unxckvnvdzqq02bqhupxuw5&e=1&st=y0ffxj7z&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      children: [new TextRun({ text: `Level ${level}:`, bold: true })],
      style: "Heading3",
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: getDescriptionRuns(level),
      style: "BodyText",
    })
  );

  if (imageUrls[level]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[level]);
    content.push(...imageParagraphs);
  }

  // Hyperlink
  const linkUrl = links[level];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Level ${level} Smell Detection Exercise Instructions`,
          style: "Hyperlink",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
        spacing: { after: 10 },
      })
    );
  }

  return content;
}

async function AuditoryProcessing(program, level, ageGroup, firstName, genderSubjectPronoun) {
  const content = [];

  const hemisphere = program;
  const ear = program === "left" ? "right" : "left";

  const getDescriptionRuns = (level) => {
    const haveOrWill = ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`;
    const hemisphereCapitalized = hemisphere.charAt(0).toUpperCase() + hemisphere.slice(1);

    switch (level) {
      case 1:
        return [
          new TextRun({ text: `${haveOrWill} listen to the ${hemisphere} hemisphere music in just the ${ear} ear for ` }),
          new TextRun({ text: "10-15 minutes for 1 to 2 times per day", bold: true }),
          new TextRun({ text: `. The goal for Level 1 is to ${ageGroup === "Child" ? `have ${firstName}` : ``} listen to the music without taking the headphones off. ${hemisphereCapitalized} hemisphere music can be purchased and downloaded from ` })
        ];
      case 2:
        return [
          new TextRun({ text: `${haveOrWill} listen to the ${hemisphere} hemisphere music in just the ${ear} ear for ` }),
          new TextRun({ text: "15 - 20 minutes for 1 to 2 times per day", bold: true }),
          new TextRun({ text: `. ${genderSubjectPronoun} can be doing other ${hemisphere} brain stimulation or activities while listening to the music.` })
        ];
      case 3:
        return [
          new TextRun({ text: `${haveOrWill} listen to the ${hemisphere} hemisphere music in just the ${ear} ear for ` }),
          new TextRun({ text: "20-30 minutes for 1 to 2 times per day", bold: true }),
          new TextRun({ text: `. ${genderSubjectPronoun} can be doing other ${hemisphere} brain stimulation exercises at the same time.` })
        ];
      case 4:
        return [
          new TextRun({ text: `${haveOrWill} listen to the ${hemisphere} hemisphere music in just the ${ear} ear for ` }),
          new TextRun({ text: "30 minutes for 1 to 2 times per day", bold: true }),
          new TextRun({ text: `. ${genderSubjectPronoun} can be doing other ${hemisphere} brain stimulation exercises at the same time.` })
        ];
      default:
        return [];
    }
  };

  const descriptions = {
    1: `${ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`
      } listen to the ${hemisphere} hemisphere music in just the ${ear} ear for 10-15 minutes for 1 to 2 times per day. The goal for Level 1 is to ${ageGroup === "Child" ? `have ${firstName}` : ``
      } listen to the music without taking the headphones off. ${hemisphere.charAt(0).toUpperCase() + hemisphere.slice(1)} hemisphere music can be purchased and downloaded from `,
    2: `${ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`
      } listen to the ${hemisphere} hemisphere music in just the ${ear} ear for 15-20 minutes for 1 to 2 times per day. ${genderSubjectPronoun} can be doing other ${hemisphere} brain stimulation or activities while listening to the music.`,
    3: `${ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`
      } listen to the ${hemisphere} hemisphere music in just the ${ear} ear for 20-30 minutes for 1 to 2 times per day. ${genderSubjectPronoun} can be doing other ${hemisphere} brain stimulation exercises at the same time.`,
    4: `${ageGroup === "Child" ? `Have ${firstName}` : `${firstName} will`
      } listen to the ${hemisphere} hemisphere music in just the ${ear} ear for 30 minutes for 1 to 2 times per day. ${genderSubjectPronoun} can be doing other ${hemisphere} brain stimulation exercises at the same time.`,
    5: `At this level, we recommend the program Acoustic Pioneer for the home program. This can be done on a tablet or computer for very specific Auditory Processing Activities. There are 2 games that we recommend: Insane Earplane and Zoo Caper Sky Scraper. Complete the following: Level 1-4 of Insane Earplane, Levels 1-10 of Zoo Caper.`,
    6: `At this level, we recommend the program Acoustic Pioneer for the home program. This can be done on a tablet or computer for very specific Auditory Processing Activities. There are 2 games that we recommend: Insane Earplane and Zoo Caper Sky Scraper. Complete the following: Level 5-6 of Insane Earplane, Levels 11-18 of Zoo Caper.`,
    7: `Level 7 will continue with the Acoustic Pioneer program. Complete the following: Level 7-10 of Insane Earplane, Levels 19-26 of Zoo Caper.`,
    8: `Level 8 will continue with the Acoustic Pioneer program. Complete the following: Level 11-14 of Insane Earplane, Levels 27-35 of Zoo Caper.`,
    9: `Level 9 will continue with the Acoustic Pioneer program. Complete the following: Level 15-17 of Insane Earplane, Levels 36-41 of Zoo Caper.`,
    10: `Level 10 will continue with the Acoustic Pioneer program. Complete the following: Level 18-20 of Insane Earplane, Levels 42-45 of Zoo Caper.`,
  };

  const links = {
    music: "https://www.brainbalancemusic.com",
    spotify: program === "right"
      ? "https://open.spotify.com/playlist/0nWjBFv6DiCqN0mPn5VAgr"
      : "https://open.spotify.com/playlist/4NDLu66uYUVXx0xGXh3ooR",
    acoustic: "https://www.acousticpioneer.com/",
  };

  const imageUrls = {
    5: [
      "/user-forms/doctor-assessment/exercise-images/ap/ap_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/ap/ap_2.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/ap/ap_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/ap/ap_2.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/ap/ap_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/ap/ap_2.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/ap/ap_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/ap/ap_2.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/ap/ap_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/ap/ap_2.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/ap/ap_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/ap/ap_2.jpeg",
    ],
  };

  if (!descriptions[level]) return [];

  // Title
  content.push(
    new Paragraph({
      children: [new TextRun({ text: `Level - ${level}:`, bold: true })],
      style: "Heading3",
    })
  );

  // Description
  if (level === 1) {
    const runs = getDescriptionRuns(1);
    content.push(
      new Paragraph({
        children: [
          ...runs,
          new ExternalHyperlink({
            children: [
              new TextRun({
                text: "www.brainbalancemusic.com",
                style: "HyperlinkStyle",
              }),
            ],
            link: links.music,
          }),
          new TextRun({ text: " or additional music recommendations are available on Spotify." }),
        ],
        spacing: { before: 120, after: 120 },
        indent: { left: 400, right: 200 },
        alignment: AlignmentType.JUSTIFIED,
      })
    );
  } else if (level >= 2 && level <= 4) {
    content.push(
      new Paragraph({
        children: getDescriptionRuns(level),
        style: "BodyText",
      })
    );
  } else {
    content.push(
      new Paragraph({
        children: [new TextRun({ text: descriptions[level] })],
        style: "BodyText",
      })
    );
  }

  if (imageUrls[level]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[level]);
    content.push(...imageParagraphs);
  }

  // Music / Acoustic links
  if (parseInt(level) <= 4) {
    content.push(
      new Paragraph({
        children: [
          new ExternalHyperlink({
            children: [
              new TextRun({ text: "Brain Balance Music", underline: {}, color: "3498DB" }),
            ],
            link: links.music,
          }),
        ],
      })
    );
    content.push(
      new Paragraph({
        children: [
          new ExternalHyperlink({
            children: [
              new TextRun({
                text: "Recommended Spotify Playlist",
                underline: {}, color: "3498DB"
              }),
            ],
            link: links.spotify,
          }),
        ],
        spacing: { after: 10 },
      })
    );
  }
  return content;
}

async function MetroTimer(
  level,
  firstName,
  ageGroup,
  program,
  genderObjectAdjective
) {
  const content = [];

  const getDescriptionRuns = (level) => {
    const leftEar = program === "left" ? "right" : "left";
    const rightEar = program === "right" ? "left" : "right";
    const leftEye = program === "left" ? "right" : "left";
    const rightEye = program === "right" ? "left" : "right";
    const leftHand = program === "left" ? "right" : "left";
    const rightHand = program === "right" ? "left" : "right";
    const leftLeg = program === "left" ? "right" : "left";
    const rightLeg = program === "right" ? "left" : "right";

    switch (level) {
      case 7:
        return [
          new TextRun({ text: `Tap/Clap Independently, 30 seconds: For Level 7, ${firstName} should be able to tap one hand on their leg and clap independently, attempting to stay on beat for ` }),
          new TextRun({ text: "30 seconds with the sound and 30 seconds with sound and light", bold: true }),
          new TextRun({ text: ` for each exercise. They should be accurate about ` }),
          new TextRun({ text: "50% of the time" }),
          new TextRun({ text: ` for each exercise before moving on to the next level. The sound should be played near the ${leftEar} ear with the light flashing into the ${leftEye} corner of the ${leftEye} eye and using the ${leftHand} hand to tap on their ${leftLeg} leg.` })
        ];
      case 8:
        return [
          new TextRun({ text: `Tap/Clap Independently, 1 minute: At this level, they should be able to tap one hand on their leg and clap independently, attempting to stay on beat for ` }),
          new TextRun({ text: "1 minute with the sound and 1 minute with sound and light", bold: true }),
          new TextRun({ text: ` for each exercise. They should be accurate about ` }),
          new TextRun({ text: "50% of the time" }),
          new TextRun({ text: ` before moving on to the next level. The sound should be played near the ${rightEar} ear with the light flashing into the ${rightEye} corner of the ${rightEye} eye, and using the ${rightHand} hand to tap on their ${rightLeg} leg.` })
        ];
      case 9:
        return [
          new TextRun({ text: `Tap/Clap Independently, 1 minute: At this level, they should be able to tap one hand on their leg and clap independently, attempting to stay on beat for ` }),
          new TextRun({ text: "1 minute with the sound and 1 minute with sound and light", bold: true }),
          new TextRun({ text: ` for each exercise. They should be accurate about ` }),
          new TextRun({ text: "75% of the time", bold: true }),
          new TextRun({ text: ` before moving on to the next level. The sound should be played near the ${rightEar} ear with the light flashing into the ${rightEye} corner of the ${rightEye} eye, and using the ${rightHand} hand to tap on their ${rightLeg} leg.` })
        ];
      case 10:
        return [
          new TextRun({ text: `Tap/Clap Independently, 1 minute: At this level, they should be able to tap one hand on their leg and clap independently, attempting to stay on beat for ` }),
          new TextRun({ text: "1 minute with the sound and 1 minute with sound and light", bold: true }),
          new TextRun({ text: ` for each exercise. They should be accurate almost ` }),
          new TextRun({ text: "100% of the time", bold: true }),
          new TextRun({ text: `. The sound should be played near the ${rightEar} ear with the light flashing into the ${rightEye} corner of the ${rightEye} eye, and using the ${rightHand} hand to tap on their ${rightLeg} leg.` })
        ];
      default:
        return [];
    }
  };

  const descriptions = {
    1: `MetroTimer 30 seconds, sound only: For Level 1, the goal will be to simply get used to the metrotimer sound in the ear for 30 seconds without resistance or attempting to push the phone away. The sound should be played near the ${program === "left" ? "right" : "left"
      } ear for 1 - 2 times per day.`,
    2: `MetroTimer 30 seconds, sound and light: At this level, the goal will be to get used to both the metrotimer sound and a flashing light for 30 seconds without resistance or trying to push the phone away. The sound should be played near the ${program === "left" ? "right" : "left"
      } ear with the light flashing into the ${program === "left" ? "right" : "left"
      } corner of the ${program === "left" ? "right" : "left"
      } eye, 1–2 times per day.`,
    3: `Tap one leg with assistance: For this exercise, the ${ageGroup === "Child" ? "parent" : "partner"
      } will assist in tapping one hand on their leg in time with the beat for 30 seconds using only sound, and 30 seconds using sound and light. The sound should be played near the ${program === "left" ? "right" : "left"
      } ear with the light flashing into the ${program === "left" ? "right" : "left"
      } corner of the ${program === "left" ? "right" : "left"
      } eye and using the ${program === "left" ? "right" : "left"
      } hand to tap on their ${program === "left" ? "right" : "left"} leg.`,
    4: `Clap with assistance: For this exercise, the ${ageGroup === "Child" ? "parent" : "partner"
      } should assist in clapping both hands in time with the beat for 30 seconds using only sound, and 30 seconds using sound and light. The sound should be played near the ${program === "left" ? "right" : "left"
      } ear with the light flashing into the ${program === "left" ? "right" : "left"
      } corner of the ${program === "left" ? "right" : "left"} eye.`,
    5: `Tap one hand with assistance and independently: For Level 5, the ${ageGroup === "Child" ? "parent" : "partner"
      } should assist in tapping one hand on their leg in time with the beat for half of the time and then allow them to do it independently for the other half. Do this for 1 minute using only sound, and 1 minute using sound and light. The sound should be played near the ${program === "left" ? "right" : "left"
      } ear with the light flashing into the ${program === "left" ? "right" : "left"
      } corner of the ${program === "left" ? "right" : "left"
      } eye, and using the ${program === "left" ? "right" : "left"
      } hand to tap on their ${program === "left" ? "right" : "left"} leg.`,
    6: `Clap with assistance and independently: At this level, the ${ageGroup === "Child" ? "parent" : "partner"
      } should assist ${genderObjectAdjective} in clapping both hands in time with the beat for half of the time and then allow them to do it independently for the other half. Do this for 1 minute using only sound and 1 minute using sound and light. The sound should be played near the ${program === "left" ? "right" : "left"
      } ear with the light flashing into the ${program === "left" ? "right" : "left"
      } corner of the ${program === "left" ? "right" : "left"} eye.`,
    7: `Tap/Clap Independently, 30 seconds: For Level 7, ${firstName} should be able to tap one hand on their leg and clap independently, attempting to stay on beat for 30 seconds with the sound and 30 seconds with sound and light for each exercise. They should be accurate about 50% of the time for each exercise before moving on to the next level. The sound should be played near the ${program === "left" ? "right" : "left"
      } ear with the light flashing into the ${program === "left" ? "right" : "left"
      } corner of the ${program === "left" ? "right" : "left"
      } eye and using the ${program === "left" ? "right" : "left"
      } hand to tap on their ${program === "left" ? "right" : "left"} leg.`,
    8: `Tap/Clap Independently, 1 minute: At this level, they should be able to tap one hand on their leg and clap independently, attempting to stay on beat for 1 minute with the sound and 1 minute with sound and light for each exercise. They should be accurate about 50% of the time before moving on to the next level. The sound should be played near the ${program === "right" ? "left" : "right"
      } ear with the light flashing into the ${program === "right" ? "left" : "right"
      } corner of the ${program === "right" ? "left" : "right"
      } eye, and using the ${program === "right" ? "left" : "right"
      } hand to tap on their ${program === "right" ? "left" : "right"} leg.`,
    9: `Tap/Clap Independently, 1 minute: At this level, they should be able to tap one hand on their leg and clap independently, attempting to stay on beat for 1 minute with the sound and 1 minute with sound and light for each exercise. They should be accurate about 75% of the time before moving on to the next level. The sound should be played near the ${program === "right" ? "left" : "right"
      } ear with the light flashing into the ${program === "right" ? "left" : "right"
      } corner of the ${program === "right" ? "left" : "right"
      } eye, and using the ${program === "right" ? "left" : "right"
      } hand to tap on their ${program === "right" ? "left" : "right"} leg.`,
    10: `Tap/Clap Independently, 1 minute: At this level, they should be able to tap one hand on their leg and clap independently, attempting to stay on beat for 1 minute with the sound and 1 minute with sound and light for each exercise. They should be accurate almost 100% of the time. The sound should be played near the ${program === "right" ? "left" : "right"
      } ear with the light flashing into the ${program === "right" ? "left" : "right"
      } corner of the ${program === "right" ? "left" : "right"
      } eye, and using the ${program === "right" ? "left" : "right"
      } hand to tap on their ${program === "right" ? "left" : "right"} leg.`,
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/mt/mt_1.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/mt/mt_2.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/mt/mt_3.jpeg",
      "/user-forms/doctor-assessment/exercise-images/mt/mt_4.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/mt/mt_5.jpeg",
      "/user-forms/doctor-assessment/exercise-images/mt/mt_6.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/mt/mt_7.jpeg",
      "/user-forms/doctor-assessment/exercise-images/mt/mt_8.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/mt/mt_9.jpeg",
      "/user-forms/doctor-assessment/exercise-images/mt/mt_10.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/mt/mt_11.jpeg",
      "/user-forms/doctor-assessment/exercise-images/mt/mt_12.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/mt/mt_13.jpeg",
      "/user-forms/doctor-assessment/exercise-images/mt/mt_14.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/mt/mt_15.jpeg",
      "/user-forms/doctor-assessment/exercise-images/mt/mt_16.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/mt/mt_17.jpeg",
      "/user-forms/doctor-assessment/exercise-images/mt/mt_18.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/691lr08o8y534m9xr7kue/Melillo-Visual-Testing-Metro-Timer-L1-V2.mp4?rlkey=64t9pm74w4e3yzdfgo4pborg1&st=3z9tjx9i&dl=0",
    2: "https://www.dropbox.com/scl/fi/cy7styo5e0c5affxwah3y/Melillo-Visual-Testing-Metro-Timer-L2-V2.mp4?rlkey=ukggl0yan6znwf2h3m78p0gx2&st=wmk3nnes&dl=0",
    3: "https://www.dropbox.com/scl/fi/f74sglc5jfnvrtwuexfln/Melillo-Visual-Testing-Metro-Timer-L3-V2.mp4?rlkey=2rk6e7ve3qnshtgwt4q2vz9qc&st=w2y9dola&dl=0",
    4: "https://www.dropbox.com/scl/fi/1bukpjkoozcye6kuo011e/Melillo-Visual-Testing-Metro-Timer-L4-V2.mp4?rlkey=2m0lz7hldmbtfrxw643nhz6jw&st=o10ko4s8&dl=0",
    5: "https://www.dropbox.com/scl/fi/2zfvpuz5usyhzoa9wpjr7/Melillo-Visual-Testing-Metro-Timer-L5-V2.mp4?rlkey=ytjuknrbi048bjeejot3e27yt&st=11y4evaw&dl=0",
    6: "https://www.dropbox.com/scl/fi/xzfe0ncuvvldinkfq53uf/Melillo-Visual-Testing-Metro-Timer-L6-V4.mp4?rlkey=orzl0ds56bml3ltj7g61qx89i&st=3pj786ub&dl=0",
    7: "https://www.dropbox.com/scl/fi/w81ebohy0qepnquldfqze/Melillo-Visual-Testing-Metro-Timer-L7-V3.mp4?rlkey=eqk5xxudfwv8twvrztkcb30h3&st=zu442pf1&dl=0",
    8: "https://www.dropbox.com/scl/fi/z7xjk9861ucr8or5245sp/Melillo-Visual-Testing-Metro-Timer-L8-V2.mp4?rlkey=wzmg1ck6sl35b8vyig1ipa43j&st=qlna0yil&dl=0",
    9: "https://www.dropbox.com/scl/fi/0b10zvjkopykp3tw6y6sy/Melillo-Visual-Testing-Metro-Timer-L9-V2.mp4?rlkey=51zf00329lc89hr6m13yuazft&st=vu5p8gww&dl=0",
    10: "https://www.dropbox.com/scl/fi/adnhh01959y8nt2xk4wof/Melillo-Visual-Testing-Metro-Timer-L10-V2.mp4?rlkey=8157bvo83lfeamxb5demlz6da&st=pwhka05z&dl=0",
  };
  // Title
  content.push(
    new Paragraph({
      children: [new TextRun({ text: `Level ${level}:`, bold: true })],
      style: "Heading3",
    })
  );

  // Description
  if (level >= 7 && level <= 10) {
    content.push(
      new Paragraph({
        children: getDescriptionRuns(level),
        style: "BodyText",
      })
    );
  } else {
    content.push(
      new Paragraph({
        children: [new TextRun({ text: descriptions[level], break: 1 })],
        style: "BodyText",
      })
    );
  }

  if (imageUrls[level]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[level]);
    content.push(...imageParagraphs);
  }

  // Hyperlink
  const linkUrl = links[level];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Instruction Video: Level ${level}`,
          style: "HyperlinkStyle",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
      })
    );
  }

  return content;
}

function Vibration(values, firstName) {
  const content = [];
  // Description paragraph
  content.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `${firstName} can also use a Vibration Plate at home as part of the home therapy program as a sensory modality that stimulates large nerve fibers. LifePro has a home unit available on Amazon (just as an example): `,
        }),

        new ExternalHyperlink({
          children: [
            new TextRun({
              text: "LIFEPRO Vibration Unit",
              underline: {},
              color: "3498DB"
            }),
          ],
          link: "https://a.co/d/eVcRxVy",
        }),

        new TextRun({
          text: `. This can be used at `,
        }),

        new TextRun({
          text: `${values.join(", ")}`,
          bold: true
        }),

        new TextRun({
          text: ` speeds for `
        }),

        new TextRun({
          text: `15 minutes`,
          bold: true
        }),

        new TextRun({
          text: " at a time. This can be used while wearing sensory equipment (TENS unit, Brain Driver and Eyelights) and while doing Right/Left Brain activities at the same time.",
        }),
      ],
      style: "BodyText",
    })
  );

  return content;
}


async function BrainDriver(
  level,
  program,
  ageGroup, firstName
) {
  const content = [];

  const getDescriptionRuns = (level) => {
    switch (level) {
      case "1":
        return [
          new TextRun({ text: `For Level 1, ${firstName} may need support from a ${ageGroup === 'Child' ? 'parent' : 'partner'} to wear the Brain Driver without resisting or attempting to remove the pads. The goal will be to get them used to the Brain Driver – use the device on Broadman Area (BA) 13, 4 and 3, 1 & 2 for ` }),
          new TextRun({ text: "20 minutes, twice daily.", bold: true }),
          new TextRun({ text: ` This is just above and behind the ear on each side as explained in the video instructions. For a ${program} brain program, the red wires will be on the ${program} and black wires on the ${program === 'left' ? 'right' : 'left'}. The goal will be to wear the unit, without resistance and combine while using the TENS unit and Eyelights at the same time.` }),
        ];
      case "2":
        return [
          new TextRun({ text: `This level of the Brain Driver will be recommended to use 2 units at once.  If only using 1 unit, then alternate locations between the front (BA 39,40) for once a day and the back (BA 13) once a day so you are doing both areas.  Use the Brain Driver on Broadman Area (BA) 39,40 and BA 13 for ` }),
          new TextRun({ text: "20 minutes, preferably twice daily.", bold: true }),
          new TextRun({ text: ` Every 2 weeks, try to increase the time up by 10 more minutes for the goal of  ` }),
          new TextRun({ text: "60 minutes, twice daily", bold: true }),
          new TextRun({ text: ` and strength will be based upon tolerance. The are is just above and behind the ear and then in between the midline of the face and hairline above the eyebrow as explained in the video instructions.  For a ${program} brain program, the red wires will always be on the ${program} and black wires on the ${program === 'left' ? 'right' : 'left'}. Combine the Brain Driver with the TENS unit and Eyelights. ` }),
          new TextRun({ text: "60 minutes twice a day", bold: true }),
        ];
      case "3":
        return [
          new TextRun({ text: `For Level 3, ${firstName}  will be recommended to use 2 Brain Drivers at once or use the 1 Brain Driver doing 1 area (BA 13) in the morning and the other area(BA 44, 45, 47) in the evening.  Using the Brain Driver on Broadman Areas (BA) 13 (just above the ear on each side) and BA 44, 45 & 47 (half way on the hairline in the front and half on the forehead) for ` }),
          new TextRun({ text: "20 minutes each", bold: true }),
          new TextRun({ text: ` and increasing time every ` }),
          new TextRun({ text: "2 weeks by 10 minutes", bold: true }),
          new TextRun({ text: ` if possible and use with the other sensory equipment (like the TENS unit and Eyelights) for the goal amount of  ` }),
          new TextRun({ text: "60 minutes, twice daily. ", bold: true }),
          new TextRun({ text: `For a ${program} brain program, the red wires will always be on the ${program} and black wires on the ${program === 'left' ? 'right' : 'left'}.` })
        ];
      case "4":
        return [
          new TextRun({ text: `For Level 4, it is recommended to use 2 Brain Drivers at once, or if using just 1 unit,   alternate front (BA 13) once a day and back area (BA 6, 8) once a day.  The Brain Driver will be used on Broadman Areas (BA) 13 and BA 6, 8 while using the sensory equipment (TENS unit and Eyelights) at the same time starting with ` }),
          new TextRun({ text: "20 minutes", bold: true }),
          new TextRun({ text: ` and increasing time every ` }),
          new TextRun({ text: "2 weeks by 10 more minutes ", bold: true }),
          new TextRun({ text: `to the goal of ` }),
          new TextRun({ text: "60 minutes, twice daily", bold: true }),
          new TextRun({ text: `For a ${program} brain program, the red wires will always be on the ${program} and black wires on the ${program === 'left' ? 'right' : 'left'}.` })
        ];
      case "5":
        return [
          new TextRun({ text: `Use 2 Brain Drivers at once or if only using 1 unit, alternate as 1 area in the morning and 1 in the evening.  Use the brain driver on Broadman areas (BA) 6,8 and BA 44, 45, 47 (on the vertical hairline above the eyebrow). For a ${program} brain program, the red wires will be on the ${program} side and black wires on the ${program === 'left' ? 'right' : 'left'} side. Starting with ` }),
          new TextRun({ text: "20 minutes and increasing time by 10 minutes every 2 weeks to goal of 60 minutes, twice daily ", bold: true }),
          new TextRun({ text: `with the TENS unit and Eyelights at the same time. ` }),
        ];
      case "6":
        return [
          new TextRun({ text: `Use 2 Brain Drivers at once or if only using 1 unit, alternate 1 area in the morning and 1 in the evening.  Use the brain driver on Broadman areas (BA) 9, 46 and BA 10, 11, 12(halfway between the midline of the face and the vertical hairline) as shown in the video below. For a ${program} brain program, the red wires will be on the ${program} side and black wires on the ${program === 'left' ? 'right' : 'left'} side.  Starting with ` }),
          new TextRun({ text: "20 minutes and increasing time by 10 minutes every 2 weeks to goal of 60 minutes, twice daily ", bold: true }),
          new TextRun({ text: `with the TENS unit and Eyelights at the same time.` }),
        ];
      case "7":
        return [
          new TextRun({ text: `Use 2 Brain Drivers at once or if only using 1 unit, alternate 1 area in the morning and 1 in the evening.  Use the brain driver on Broadman areas (BA) 5, 7 and BA 10, 11, 12 (Just above the eyebrow, in between the midline of the face and the vertical hairline) as shown in the video below. For a ${program} brain program, the red wires will be on the ${program} side and black wires on the ${program === 'left' ? 'right' : 'left'} side.  Starting with ` }),
          new TextRun({ text: "20 minutes and increasing time by 10 minutes every 2 weeks to goal of 60 minutes, twice daily", bold: true }),
          new TextRun({ text: ` with the TENS unit and Eyelights at the same time.` }),
        ];
      case "8":
        return [
          new TextRun({ text: `Use 2 Brain Drivers at once or if only using 1 unit, alternate 1 area in the morning and 1 in the evening.  Use the brain driver on Broadman areas (BA) 39, 40 (1” above the ear and 1” behind the ear) and BA 44, 45, 47 (in the middle of the vertical hairline) as shown in the video below. For a ${program} brain program, the red wires will be on the ${program} side and black wires on the ${program === 'left' ? 'right' : 'left'} side. Starting with ` }),
          new TextRun({ text: "20 minutes and increasing time by 10 minutes every 2 weeks to goal of 60 minutes, twice daily", bold: true }),
          new TextRun({ text: ` with the TENS unit and Eyelights at the same time.` }),
        ];
      case "9":
        return [
          new TextRun({ text: `Use 2 Brain Drivers at once or if only using 1 unit, alternate 1 area in the morning and 1 in the evening.  Use the brain driver on Broadman areas (BA) 39, 40 (1” above the ear and 1” behind the ear) and BA 10, 11, 12 (Just above the eyebrow, in between the midline of the face and the vertical hairline) as shown in the video below. For a ${program} brain program, the red wires will be on the ${program} side and black wires on the ${program === 'left' ? 'right' : 'left'} side.  Starting with ` }),
          new TextRun({ text: "20 minutes and increasing time by 10 minutes every 2 weeks to goal of 60 minutes, twice daily", bold: true }),
          new TextRun({ text: ` with the TENS unit and Eyelights at the same time.` }),
        ];
      case "10":
        return [
          new TextRun({ text: `Use 2 Brain Drivers at once or if only using 1 unit, alternate 1 area in the morning and 1 in the evening.  Use the brain driver on Broadman areas (BA) 39, 40 (1” above the ear and 1” behind the ear) and BA 9, 46  as shown in the video below. For a ${program} brain program, the red wires will be on the ${program} side and black wires on the ${program === 'left' ? 'right' : 'left'} side. Starting with ` }),
          new TextRun({ text: "20 minutes and increasing time by 10 minutes every 2 weeks to goal of 60 minutes, twice daily", bold: true }),
          new TextRun({ text: ` with the TENS unit and Eyelights at the same time.` }),
        ];
      default:
        return [];
    }
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/bd/bd_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/bd/bd_2.jpeg",
      "/user-forms/doctor-assessment/exercise-images/bd/bd_3.jpeg",
      "/user-forms/doctor-assessment/exercise-images/bd/bd_4.jpeg",
      "/user-forms/doctor-assessment/exercise-images/bd/bd_5.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/bd/bd_6.jpeg",
      "/user-forms/doctor-assessment/exercise-images/bd/bd_5.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/bd/bd_7.jpeg",
      "/user-forms/doctor-assessment/exercise-images/bd/bd_5.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/bd/bd_8.jpeg",
      "/user-forms/doctor-assessment/exercise-images/bd/bd_5.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/bd/bd_9.jpeg",
      "/user-forms/doctor-assessment/exercise-images/bd/bd_5.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/bd/bd_10.jpeg",
      "/user-forms/doctor-assessment/exercise-images/bd/bd_5.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/bd/bd_11.jpeg",
      "/user-forms/doctor-assessment/exercise-images/bd/bd_5.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/bd/bd_12.jpeg",
      "/user-forms/doctor-assessment/exercise-images/bd/bd_5.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/bd/bd_13.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/bd/bd_14.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/1rebzrlv4psy8njyez9vf/Melillo-Transcranial-Treatment-Level-1.mp4?rlkey=6l6yteubkopluc4511jzpmoiz&st=dm9s0tad&dl=0",
    2: "https://www.dropbox.com/scl/fi/mpeaz7rva8df8okrkfobp/Melillo-Transcranial-Level-2-V4.mp4?rlkey=wdl76ludaml6mjr2xtahqas0t&e=1&st=iqem4o2s&dl=0",
    3: "https://www.dropbox.com/scl/fi/ya6ginpdqjb3py3okhgh5/Melillo-Transcranial-Treatment-Level-3.mp4?rlkey=xpjhi49haelzi8eefpfcvlghu&st=qmhlva94&dl=0",
    4: "https://www.dropbox.com/scl/fi/pdvcuh6800hd7h1y2pd05/Melillo-Transcranial-Treatment-Level-4.mp4?rlkey=po3tjvmj7xtyrvfr526pfvg8y&st=2vtdq0bs&dl=0",
    5: "https://www.dropbox.com/scl/fi/fpye5sngi8uydetbibgs8/Melillo-Transcranial-Treatment-Level-5.mp4?rlkey=25873u1byqzwwrnmiey7uei5f&st=pggtesln&dl=0",
    6: "https://www.dropbox.com/scl/fi/aiptw6j2eej0w7coedj3g/Melillo-Transcranial-Treatment-Level-6.mp4?rlkey=3iz0qdzpv5gbhhps727gnlkiu&st=ktu8yv0z&dl=0",
    7: "https://www.dropbox.com/scl/fi/acpze521dbxoi3ylz102k/Melillo-Transcranial-Treatment-Level-7.mp4?rlkey=uqi8a8ej28cihs2cbnfzrf92j&st=dj7svpp9&dl=0",
    8: "https://www.dropbox.com/scl/fi/97bxux8pm65a2g6m9gzb9/Melillo-Transcranial-Treatment-Level-8.mp4?rlkey=h0s2pvf9swq4fzspe5ihrv1ez&st=e5g4hom5&dl=0",
    9: "https://www.dropbox.com/scl/fi/muy06cjndbmx9er5e7wkj/Melillo-Transcranial-Treatment-Level-9.mp4?rlkey=ckqc3uxe12k8ozj9tb8zv3y4g&st=hjxxixl1&dl=0",
    10: "https://www.dropbox.com/scl/fi/7fd2ta2xvl7lmgu0t37tp/Melillo-Transcranial-Treatment-Level-10.mp4?rlkey=nb3s8wfew9n6rlsvq6fvz0z9b&st=7ske17er&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      text: "Brain Driver",
      style: "Heading2",
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: getDescriptionRuns(level),
      style: "BodyText",
    })
  );

  if (imageUrls[level]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[level]);
    content.push(...imageParagraphs);
  }

  // Hyperlink
  const linkUrl = links[level];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Level ${level} Brain Driver Instructions`,
          style: "HyperlinkStyle",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
      })
    );
  }

  return content;
}

async function Vitamin(vitamins, isReevaluation, firstName, genderSubjectPronoun, genderObjectAdjective) {
  const content = [];

  // Map of all vitamin descriptions with inline links
  const descriptions = {
    "Kid Genius Left - Under 5":
      `${isReevaluation ? `Recommendation for ${genderObjectAdjective} to continue the ` : "Recommendation for the "}Kid Genius Left Brain (Learning and Memory) supplement. This will be 1 capsule, twice daily, preferably with food. The left-brain vitamins provide neurotransmitters for left-brain development.`,
    "Kid Genius Right - Under 5":
      `${isReevaluation ? `Recommendation for ${genderObjectAdjective} to continue the ` : "Recommendation for the "}Kid Genius Right Brain (Attention & Focus) supplement. This will be 1 capsule, twice daily, preferably. We also have a new chewable version for kids as well. The right-brain vitamins provide neurotransmitters for right-brain development which is very important.`,

    "Kid Genius Left - Over 5":
      `${isReevaluation ? `Recommendation for ${genderObjectAdjective} to continue the ` : "Recommendation for the "}Kid Genius Left Brain (Learning and Memory) supplement. This will be 2 capsules, twice daily, preferably with food. The left-brain vitamins provide neurotransmitters for left-brain development.`,
    "Kid Genius Right - Over 5":
      `${isReevaluation ? `Recommendation for ${genderObjectAdjective} to continue the ` : "Recommendation for the "}Kid Genius Right Brain (Attention & Focus) supplement. This will be 2 capsules, twice daily, preferably. We also have a new chewable version for kids as well. The right-brain vitamins provide neurotransmitters for right-brain development which is very important.`,

    "Kid Genius Probiotic - Under 5":
      `${isReevaluation ? `Also recommending that ${genderSubjectPronoun} continues to take the ` : "Also recommending the "}Kid Genius Prebiotic/Probiotic supplement for 1 capsule, twice daily, with food. These digestion vitamins help to provide important digestive enzymes to the body which are essential for proper digestion while we are working with the program.`,

    "Kid Genius Probiotic - Over 5":
      `${isReevaluation ? `Also recommending that ${genderSubjectPronoun} continues to take the ` : "Also recommending the "}Kid Genius Prebiotic/Probiotic supplement for 2 capsules, twice daily, with food. These digestion vitamins help to provide important digestive enzymes to the body which are essential for proper digestion while we are working with the program.`,

    "Vitamin D capsules - Under 5":
      `Vitamin D capsules 2,500 IU Capsules or gummies are also recommended${isReevaluation ? " to be continued daily" : ""}. Vitamin D helps to balance the immune system which will be important while the brain is developing through this stage. We also have a liquid version available.`,
    "Vitamin D capsules - Over 5":
      `Vitamin D capsules 5,000 IU Capsules or gummies are also recommended${isReevaluation ? " to be continued daily" : ""}. Vitamin D helps to balance the immune system which will be important while the brain is developing through this stage. We also have a liquid version available.`,

    "Vitamin D liquid - Under 5":
      `Vitamin D liquid is also recommended${isReevaluation ? " to be continued daily " : " "}at this stage to be used daily. The liquid will be 1.5 drops for 2,500 units per day. Vitamin D helps to balance the immune system which is important while the brain is developing through this stage.`,
    "Vitamin D liquid - Over 5":
      `Vitamin D liquid is also recommended${isReevaluation ? " to be continued daily " : " "}at this stage to be used daily. The liquid will be 2.5 drops for 5,000 units per day. Vitamin D helps to balance the immune system which is important while the brain is developing through this stage.`,

    "Omega 3 capsules - Under 5":
      `Omega 3 soft gel capsules are also an essential recommendation that should ${isReevaluation ? "continue to " : ""}be taken daily for 2,500 mg. Omega is very important for brain function, brain development and reducing inflammation and in controlling and developing the immune system.`,
    "Omega 3 capsules - Over 5":
      `Omega 3 soft gel capsules are also an essential recommendation that should ${isReevaluation ? "continue to " : ""}be taken daily for 5,000 mg. Omega is very important for brain function, brain development and reducing inflammation and in controlling and developing the immune system.`,
    "Omega 3 liquid - Under 5":
      `Omega 3 liquid is also an essential recommendation that should ${isReevaluation ? "continue to " : ""}be taken daily for 2,500 mg. Omega is very important for brain function, brain development and reducing inflammation and in controlling and developing the immune system.`,
    "Omega 3 liquid - Over 5":
      `Omega 3 liquid is also an essential recommendation that should ${isReevaluation ? "continue to " : ""}be taken daily for 5,000 mg. Omega is very important for brain function, brain development and reducing inflammation and in controlling and developing the immune system.`,

    "TH1/TH2 - Under 5":
      `The TH1/TH2 supplements are also recommended to be ${isReevaluation ? "continued " : ""}taken at home. These are used to build the immune system, fight infections and create antibodies. They can be purchased in our office or online at www.pureforyou.com. They will be taken for 1 capsule of each, twice daily.`,
    "TH1/TH2 - Over 5":
      `The TH1/TH2 supplements are also recommended to be ${isReevaluation ? "continued " : ""}taken at home. These are used to build the immune system, fight infections and create antibodies. They can be purchased in our office or online at www.pureforyou.com. They will be taken for 2 capsules of each, twice daily.`,

    "Zeolite/Glutathione Spray - Under 5":
      `The Glutathione and Zeolite Spray is also recommended to be ${isReevaluation ? "continued " : ""}used daily at home while doing the program. Glutathione is an all-natural chemical that is made in the body and used for detoxification and Zeolite helps to excrete the toxins. The sprays are odorless and tasteless and just absorb in the mouth very easily. They can be purchased in our office, online or on amazon and will be used for 3 sprays of each, twice daily.`,
    "Zeolite/Glutathione Spray - Over 5":
      `The Glutathione and Zeolite Spray is also recommended to be ${isReevaluation ? "continued " : ""}used daily at home while doing the program. Glutathione is an all-natural chemical that is made in the body and used for detoxification and Zeolite helps to excrete the toxins. The sprays are odorless and tasteless and just absorb in the mouth very easily. They can be purchased in our office, online or on amazon and will be used for 6 sprays of each, twice daily.`,

    "B12 Spray":
      `B12 is also an essential part of the home recommendations${isReevaluation ? " to be continued daily" : ""}. This spray is odorless and tasteless and will be sprayed directly into the mouth for the body to absorb easily. B12 is essential to promote healthy brain function and development. This spray can be purchased in our office, on our website or also on amazon and will be used 6 sprays, twice daily, every day as part of the home program.`,

    "Perilla - Under 5":
      `Perilla supplement, made by Pure Encapsulations, is also being recommended${isReevaluation ? " to be continued" : ""}. This will help to improve the immune response and Th2 and maintain a healthy modulation of the immune system to environmental factors. This can be purchased in our office or on Amazon and will be taken for 1 capsule, twice daily.`,
    "Perilla - Over 5":
      `Perilla supplement, made by Pure Encapsulations, is also being recommended${isReevaluation ? " to be continued" : ""}. This will help to improve the immune response and Th2 and maintain a healthy modulation of the immune system to environmental factors. This can be purchased in our office or on Amazon and will be taken for 2 capsules, twice daily.`,

    "Histox - Under 5":
      `Histox by Apex Energetic is also recommended${isReevaluation ? " to be continued" : ""} for 1 capsule, twice daily. This supplement will aid in the body's response to foods and other environmental factors and support immune function. It will also aid in gastrointestinal function and provide antioxidants. This can be purchased in our office on also on Amazon.`,
    "Histox - Over 5":
      `Histox by Apex Energetic is also recommended${isReevaluation ? " to be continued" : ""} for 2 capsules, twice daily. This supplement will aid in the body's response to foods and other environmental factors and support immune function. It will also aid in gastrointestinal function and provide antioxidants. This can be purchased in our office on also on Amazon.`,

    "Quercetin - Under 5":
      `Quercetin is also recommended${isReevaluation ? " to be continued" : ""} for 3 sprays, twice daily. This can be purchased in our office or on our website too.`,
    "Quercetin - Over 5":
      `Quercetin is also recommended${isReevaluation ? " to be continued" : ""} for 6 sprays, twice daily. This can be purchased in our office or on our website too.`,

    "Berberine - Under 5":
      `Berberine is also recommended${isReevaluation ? " to be continued" : ""} for 3 sprays, twice daily. This can be purchased in our office or on our website too.`,
    "Berberine - Over 5":
      `Berberine is also recommended${isReevaluation ? " to be continued" : ""} for 6 sprays, twice daily. This can be purchased in our office or on our website too.`,

    "COQ10 - Under 5":
      `COQ10 is also recommended${isReevaluation ? " to be continued" : ""} for 3 sprays, twice daily. This can be purchased in our office or on our website too.`,
    "COQ10 - Over 5":
      `COQ10 is also recommended${isReevaluation ? " to be continued" : ""} for 6 sprays, twice daily. This can be purchased in our office or on our website too.`,

    "Pro Omega 2000 - Over 5":
      `Pro Omega 2000 is also recommended${isReevaluation ? " to be continued" : ""} for 5 capsule, daily. Follow the recommended instructions and this can be purchased in our office or Amazon.`,
  };

  // Map of corresponding video links
  const links = {
    "Kid Genius Left":
      "https://www.dropbox.com/scl/fi/0h272l49b0lls57xday07/learningmemory-concentration-vits.mov?rlkey=yxmtiwo6jtz7mmk55hrta4n7u&dl=0",
    "Kid Genius Right":
      "https://www.dropbox.com/scl/fi/ma8f3qz1paqb8c15yopp6/Attention-Focus-Calm.mp4?rlkey=d2x8ni5csmp7u07p2jwyhq2vq&st=904pecjg&dl=0",
    "Kid Genius Probiotic":
      "https://www.dropbox.com/scl/fi/a0fdbjrsf09a7zyk12xba/Gut-Vitamin.mp4?rlkey=9zpaiu47sw0sjc2jxh972vvk4&st=c0eor913&dl=0",
    "Vitamin D capsules":
      "https://www.dropbox.com/scl/fi/kmry9tlraa8smeul33zom/vitamin-Ds.mov?rlkey=vaalvez7ap8yj3uoad8cdcu6y&dl=0",
    "Vitamin D liquid":
      "https://www.dropbox.com/scl/fi/kmry9tlraa8smeul33zom/vitamin-Ds.mov?rlkey=vaalvez7ap8yj3uoad8cdcu6y&e=1&dl=0",
    "Omega 3 capsules":
      "https://www.dropbox.com/scl/fi/2ty1lxjb6emc9tg6p6qqn/omegas-vits.mov?rlkey=gk7wc12l3aep22k8t5qqsoj0s&dl=0",
    "Omega 3 liquid":
      "https://www.dropbox.com/scl/fi/2ty1lxjb6emc9tg6p6qqn/omegas-vits.mov?rlkey=gk7wc12l3aep22k8t5qqsoj0s&e=1&dl=0",
    "TH1/TH2":
      "dummy_link_set_explicitely",
    "Zeolite/Glutathione Spray":
      "dummy_link_set_explicitely",
    "B12 Spray":
      "https://www.dropbox.com/scl/fi/salnvf0hoi5xfes2qpnv0/B12.mp4?rlkey=kgg96u7240qstthpzuzrtpux8&st=3lwoxvii&dl=0",
    "Perilla": "https://a.co/d/aKE74eD",
    "Histox": "https://a.co/d/gR6eLrm",
    "Quercetin": "https://www.dropbox.com/scl/fi/gxej2twavzbyfx14deod4/Quercetin-Spray.mp4?rlkey=t1qyqe09du2yqsw5staliggsd&st=rjlew04v&dl=0",
    "Berberine": "https://www.dropbox.com/scl/fi/4mysvot0txwnis93qruiw/Berberine.mp4?rlkey=1fei04asyzkv44hxh2ig59ea9&st=zw7qxj66&dl=0",
    "COQ10": "https://www.dropbox.com/scl/fi/1q9gkux135bgq4ii9wj5g/Co-Q10.mp4?rlkey=px1cl73qahlipw6pbec0q0lm8&st=fo4vhj6v&dl=0",
    "Pro Omega 2000": "https://www.dropbox.com/scl/fi/7p17rhp0b8ca6gvlnk403/ProOmega-2000.mp4?rlkey=9t4kw3yu1cx2a9o6qcwa8p7zn&e=1&st=p7aupx6z&dl=0",
  };

  for (const vitamin of vitamins) {

    if (!descriptions[vitamin]) continue;

    // Create paragraph with inline hyperlinks using the generic map
    const baseKey = Object.keys(links).find((key) => vitamin.includes(key));
    if (baseKey) {
      // Split the description text to insert hyperlinks inline
      let descriptionText = descriptions[vitamin];
      let children = [];

      if (vitamin.includes("Kid Genius Left")) {
        const parts = descriptionText.split(
          "Kid Genius Left Brain (Learning and Memory)"
        );
        children = [
          new TextRun({ text: parts[0] }),
          new ExternalHyperlink({
            children: [
              new TextRun({
                text: "Kid Genius Left Brain (Learning and Memory)",
                underline: {}, color: "3498DB"
              }),
            ],
            link: links[baseKey],
          }),
          new TextRun({ text: parts[1] }),
        ];
      } else if (vitamin.includes("Kid Genius Right")) {
        const parts = descriptionText.split(
          "Kid Genius Right Brain (Attention & Focus)"
        );
        children = [
          new TextRun({ text: parts[0] }),
          new ExternalHyperlink({
            children: [
              new TextRun({
                text: "Kid Genius Right Brain (Attention & Focus)",
                underline: {}, color: "3498DB"
              }),
            ],
            link: links[baseKey],
          }),
          new TextRun({ text: parts[1] }),
        ];
      } else if (vitamin.includes("Kid Genius Probiotic")) {
        const parts = descriptionText.split("Kid Genius Prebiotic/Probiotic");
        children = [
          new TextRun({ text: parts[0] }),
          new ExternalHyperlink({
            children: [
              new TextRun({
                text: "Kid Genius Prebiotic/Probiotic",
                underline: {}, color: "3498DB",
              }),
            ],
            link: links[baseKey],
          }),
          new TextRun({ text: parts[1] }),
        ];
      } else if (vitamin.includes("Vitamin D capsules")) {
        const parts = descriptionText.split("Vitamin D capsules");
        children = [
          new ExternalHyperlink({
            children: [
              new TextRun({ text: "Vitamin D capsules", underline: {}, color: "3498DB" }),
            ],
            link: links[baseKey],
          }),
          new TextRun({ text: parts[1] }),
        ];
      } else if (vitamin.includes("Vitamin D liquid")) {
        const parts = descriptionText.split("Vitamin D liquid");
        children = [
          new ExternalHyperlink({
            children: [
              new TextRun({ text: "Vitamin D liquid", underline: {}, color: "3498DB" }),
            ],
            link: links[baseKey],
          }),
          new TextRun({ text: parts[1] }),
        ];
      } else if (vitamin.includes("Omega 3 capsules")) {
        const parts = descriptionText.split("Omega 3 soft gel capsules");
        children = [
          new ExternalHyperlink({
            children: [new TextRun({ text: "Omega 3 soft gel capsules", underline: {}, color: "3498DB" })],
            link: links[baseKey],
          }),
          new TextRun({ text: parts[1] }),
        ];
      } else if (vitamin.includes("Omega 3 liquid")) {
        const parts = descriptionText.split("Omega 3 liquid");
        children = [
          new ExternalHyperlink({
            children: [
              new TextRun({ text: "Omega 3 liquid", underline: {}, color: "3498DB" }),
            ],
            link: links[baseKey],
          }),
          new TextRun({ text: parts[1] }),
        ];
      } else if (vitamin.includes("TH1/TH2")) {
        // Base description text, followed by appended video sentence
        children = [new TextRun({ text: descriptionText })];

        children.push(new TextRun({ text: " " }));
        children.push(
          new ExternalHyperlink({
            children: [
              new TextRun({
                text: "TH1 Supplement Video",
                underline: {},
                color: "3498DB",
              }),
            ],
            link: "https://www.dropbox.com/scl/fi/yjlxy5uqbtxazc638lgdt/TH1-Support.mp4?rlkey=52hljm7i054ctyf5g6vwy8bgz&st=89h8ztnj&dl=0",
          })
        );
        children.push(new TextRun({ text: " and " }));
        children.push(
          new ExternalHyperlink({
            children: [
              new TextRun({
                text: "TH2 Supplement Video",
                underline: {},
                color: "3498DB",
              }),
            ],
            link: "https://www.dropbox.com/scl/fi/vdfe95rb5xqvkogsu91b1/TH2-Modulator.mp4?rlkey=ebqvodh0yzntmecrl97cwprsg&st=s8xzdlbu&dl=0",
          })
        );
        children.push(
          new TextRun({
            text: " explain how to use these properly.",
          })
        );
      } else if (vitamin.includes("Zeolite/Glutathione Spray")) {

        // Start with the base description text, then append a second sentence with the video links
        children = [new TextRun({ text: descriptionText })];

        children.push(
          new TextRun({
            text: " The ",
          })
        );
        children.push(
          new ExternalHyperlink({
            children: [
              new TextRun({
                text: "Glutathione",
                underline: {},
                color: "3498DB",
              }),
            ],
            link: "https://www.dropbox.com/scl/fi/en6fx3r9ec6oflgl4x8yh/Glutathione.mp4?rlkey=3ciqv7o68o2km976ardbrcymo&st=it4pp6se&dl=0",
          })
        );
        children.push(new TextRun({ text: " and " }));
        children.push(
          new ExternalHyperlink({
            children: [
              new TextRun({
                text: "Zeolite Spray",
                underline: {},
                color: "3498DB",
              }),
            ],
            link: "https://www.dropbox.com/scl/fi/gfluijr3klow37szt0vqz/Zeolite-Spray.mp4?rlkey=e46d8gqj3mz4circaizwpxns9&st=9g45eze7&dl=0",
          })
        );
        children.push(
          new TextRun({
            text: " explain how to use these properly.",
          })
        );
      } else if (vitamin.includes("Perilla")) {
        const parts = descriptionText.split("Perilla");
        children = [
          new ExternalHyperlink({
            children: [new TextRun({ text: "Perilla", underline: {}, color: "3498DB" })],
            link: links[baseKey],
          }),
          new TextRun({ text: parts[1] }),
        ];
      } else if (vitamin.includes("Histox")) {
        const parts = descriptionText.split("Histox");
        children = [
          new ExternalHyperlink({
            children: [new TextRun({ text: "Histox", underline: {}, color: "3498DB" })],
            link: links[baseKey],
          }),
          new TextRun({ text: parts[1] }),
        ];
      } else if (vitamin.includes("Quercetin")) {
        const parts = descriptionText.split("Quercetin");
        children = [
          new ExternalHyperlink({
            children: [new TextRun({ text: "Quercetin", underline: {}, color: "3498DB" })],
            link: links[baseKey],
          }),
          new TextRun({ text: parts[1] }),
        ];
      } else if (vitamin.includes("Berberine")) {
        const parts = descriptionText.split("Berberine");
        children = [
          new ExternalHyperlink({
            children: [new TextRun({ text: "Berberine", underline: {}, color: "3498DB" })],
            link: links[baseKey],
          }),
          new TextRun({ text: parts[1] }),
        ];
      } else if (vitamin.includes("COQ10")) {
        const parts = descriptionText.split("COQ10");
        children = [
          new ExternalHyperlink({
            children: [new TextRun({ text: "COQ10", underline: {}, color: "3498DB" })],
            link: links[baseKey],
          }),
          new TextRun({ text: parts[1] }),
        ];
      } else if (vitamin.includes("B12 Spray")) {
        const parts = descriptionText.split("B12");
        children = [
          new TextRun({ text: parts[0] }),
          new ExternalHyperlink({
            children: [new TextRun({ text: "B12", underline: {}, color: "3498DB" })],
            link: links[baseKey],
          }),
          new TextRun({ text: parts[1] }),
        ];
      } else {
        // Fallback to plain text if no hyperlink pattern matches
        children = [new TextRun({ text: descriptionText })];
      }

      content.push(
        new Paragraph({
          children: children,
          style: "BodyText"
        })
      );
    } else {
      // Fallback for vitamins without links
      content.push(
        new Paragraph({
          children: [new TextRun({ text: descriptions[vitamin], break: 1 })],
          style: "BodyText",
        })
      );
    }
  }

  return content;
}

function VibrantTesting(
  value,
  firstName,
  genderSubjectPronoun,
  genderAdjective,
  ageGroup
) {
  const content = [];

  // Text for Food Sensitivity
  if (value === "Food Sensitivity") {
    const text =
      `I have also discussed a Food Sensitivity Lab test for ${firstName}. ` +
      `This is an at home finger-prick test that can help to evaluate any sensitivity that ${genderSubjectPronoun} ` +
      `has to specific foods or additives and how those may affect ${genderAdjective} neurologically. ` +
      `We will do a separate zoom call with ${ageGroup === "Child" ? "the family" : "you"
      } ` +
      `to review these results when they are ready. This can be ordered in our office or online at:`;

    content.push(
      new Paragraph({
        children: [new TextRun({ text })],
        style: "BodyText",
      })
    );

    // Hyperlink
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: "Well ProZ",
          style: "HyperlinkStyle",
        }),
      ],
      link: "https://drrobertmelillo.wellproz.com/patient/home/",
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
        alignment: AlignmentType.LEFT,
        indent: {
          left: 400,
          right: 200,
        },
      })
    );
  }

  // Text for Neural Zoomer Plus
  else if (value === "Neural Zoomer Plus") {
    const text =
      `I have also discussed a Neural Zoomer Lab test for ${firstName}. ` +
      `This is an at home finger-prick test that can help to evaluate ${genderAdjective} immune system, ` +
      `and any inflammation in ${genderAdjective} body that may affect ${genderAdjective} neurologically. ` +
      `We will do a separate zoom call with ${ageGroup === "Child" ? "the family" : "you"
      } ` +
      `to review these results when they are ready. This can be ordered in our office or online at:`;

    content.push(
      new Paragraph({
        children: [new TextRun({ text })],
        style: "BodyText",
      })
    );

    // Hyperlink
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: "Well ProZ",
          style: "HyperlinkStyle",
        }),
      ],
      link: "https://drrobertmelillo.wellproz.com/patient/home/",
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
        alignment: AlignmentType.LEFT,
        indent: {
          left: 400,
          right: 200,
        },
      })
    );
  }

  return content;
}


async function Tlr(
  level,
  firstName,
  genderSubjectPronoun,
  genderObjectAdjective,
  genderAdjective,
  program
) {
  const content = [];

  const getDescriptionRuns = (level) => {
    switch (level) {
      case 1:
        return [
          new TextRun({ text: `${firstName} will start by lying flat on ${genderAdjective} back, with the hands on either side. Grab the sides of the head, then flex the neck all the way up and do a quick drop back, not letting the head hit the ground. Do this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: `. This is similar to the Moro reflex; the difference is that if ${genderSubjectPronoun} has the TLR, as you flex ${genderAdjective} neck, you will see them flex ${genderAdjective} arms and legs as well.` })
        ];
      case 2:
        return [
          new TextRun({ text: `${firstName} will start by lying flat on ${genderAdjective} back, with the hands on either side. Grab the sides of the head, then flex the neck all the way up and do a quick drop back, not letting the head hit the ground. Do this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: `. This is similar to the Moro reflex; the difference is that if ${genderSubjectPronoun} has the TLR, as you flex ${genderAdjective} neck, you will see them flex ${genderAdjective} arms and legs as well.` })
        ];
      case 3:
        return [
          new TextRun({ text: `${firstName} will start by lying flat on ${genderAdjective} back, with the hands on either side. Grab the sides of the head, then flex the neck all the way up and do a quick drop back, not letting the head hit the ground. Instruct ${genderObjectAdjective} to consciously suppress the reflex, by trying not to flex arms and legs. Do this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 4:
        return [
          new TextRun({ text: `${firstName} will start by lying flat on ${genderAdjective} back, with the hands on either side. Grab the sides of the head, then flex the neck all the way up and do a quick drop back, not letting the head hit the ground. Instruct ${genderObjectAdjective} to consciously suppress the reflex, by trying not to flex arms and legs. Do this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 5:
        return [
          new TextRun({ text: `(Cannon ball) In a supine position, hold knees into chest and wrap arms around legs. Bring your head up to your knees so that your shoulders should leave the ground as you curl into a tight ball and roll forward into a sitting position; then roll back while extending the neck. Do this for ` }),
          new TextRun({ text: "10 reps", bold: true }),
          new TextRun({ text: "." })
        ];
      case 6:
        return [
          new TextRun({ text: `Start by laying on your back with legs and arms extended straight. Flex your legs into a crunch position as you bring your head forward. As you extend back out, bring your head back down to the floor. Repeat ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 7:
        return [
          new TextRun({ text: `Starting position should be sitting on the floor with knees bent, arms on the floor at your sides with fingers pointing behind you with the head flexed down. For each repetition, torso should be extended and head tilted back as you extend, and then back down; when flexing back down, backside should not touch the ground. Repeat ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 8:
        return [
          new TextRun({ text: `Stand with feet together and arms at your sides, squat down and flex head forward, as you stand up extend head all the way back. Repeat this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 9:
        return [
          new TextRun({ text: `Stand with feet together and arms at your sides, squat down and flex head forward, then jump up and extend head all the way back. Repeat this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 10:
        return [
          new TextRun({ text: `Stand with feet together and arms at your sides, squat down and flex head forward, then jump up and extend head all the way back. Repeat this ` }),
          new TextRun({ text: "20 times", bold: true }),
          new TextRun({ text: "." })
        ];
      default:
        return [new TextRun({ text: "" })];
    }
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_2.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_2.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_2.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_2.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_3.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_4.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_5.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_6.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_7.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_8.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_9.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_8.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_9.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_8.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_9.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/g4x7ij4vizz1bi223jwqn/Melillo-Primitive-Reflexes-TLR-Level-1-2.mp4?rlkey=mpbpf05pdues60g9pvew0xvok&st=tq36ml8i&dl=0",
    2: "https://www.dropbox.com/scl/fi/g4x7ij4vizz1bi223jwqn/Melillo-Primitive-Reflexes-TLR-Level-1-2.mp4?rlkey=mpbpf05pdues60g9pvew0xvok&st=tq36ml8i&dl=0",
    3: "https://www.dropbox.com/scl/fi/h54c6xa0sj7asgrlqioo2/Melillo-Primitive-Reflexes-TLR-Level-3-4.mp4?rlkey=fkln8bwyxrghpaxhltokb8uyi&st=a90aje96&dl=0",
    4: "https://www.dropbox.com/scl/fi/h54c6xa0sj7asgrlqioo2/Melillo-Primitive-Reflexes-TLR-Level-3-4.mp4?rlkey=fkln8bwyxrghpaxhltokb8uyi&st=a90aje96&dl=0",
    5: "https://www.dropbox.com/scl/fi/gyxb95keiieiahlt1mkwl/Melillo-Primitive-Reflexes-TLR-Level-5.mp4?rlkey=bmrgegct6eq68auztnv0tnu3w&st=h7ixp3as&dl=0",
    6: "https://www.dropbox.com/scl/fi/1nmip1xxrmjnu8sfey3md/Melillo-Primitive-Reflexes-TLR-Level-6.mp4?rlkey=1wctehbk0e7kalvue1nvr2uki&st=m8iid52i&dl=0",
    7: "https://www.dropbox.com/scl/fi/gee9wsq48x27m8koe4h7u/Melillo-Primitive-Reflexes-TLR-Level-7.mp4?rlkey=408i4x2yyb05dolnnng5dmjsf&st=bf8j7rkq&dl=0",
    8: "https://www.dropbox.com/scl/fi/jhqobvxlzxt4purfuro7t/Melillo-Primitive-Reflexes-TLR-Level-8.mp4?rlkey=e4gml4b8luvf8un7d3bf2mch4&st=prj7l7jg&dl=0",
    9: "https://www.dropbox.com/scl/fi/y7q0cqf4ir0l4z3mgy77j/Melillo-Primitive-Reflexes-TLR-Level-9-10.mp4?rlkey=spmbs43bt9setpspji3k87eci&st=bszipguh&dl=0",
    10: "https://www.dropbox.com/scl/fi/y7q0cqf4ir0l4z3mgy77j/Melillo-Primitive-Reflexes-TLR-Level-9-10.mp4?rlkey=spmbs43bt9setpspji3k87eci&st=bszipguh&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `TLR - Level ${level}: `,
        }),
      ],
      style: "Heading3",
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: getDescriptionRuns(level),
      style: "BodyText",
    })
  );

  if (imageUrls[level]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[level]);
    content.push(...imageParagraphs);
  }

  // Video link
  const linkUrl = links[level];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Instruction Video: Level ${level}`,
          underline: {},
          color: "2980B9",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
      })
    );
  }

  return content;
}

// ASTNR Function
async function Astnr(
  level,
  firstName,
  genderSubjectPronoun,
  genderAdjective,
  genderObjectAdjective,
  ageGroup
) {
  const content = [];

  const getDescriptionRuns = (level) => {
    const parentOrPartner = ageGroup === "Child" ? "Parent" : "Partner";
    switch (level) {
      case 1:
        return [
          new TextRun({ text: `${parentOrPartner} will hold ${genderAdjective} head in hands while ${genderSubjectPronoun} is in a tabletop position, they will turn the head to the right and then to the left ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: ` through full range of motion, looking to see elbows bend for the reflex.` })
        ];
      case 2:
        return [
          new TextRun({ text: `${parentOrPartner} will hold ${genderAdjective} head in hands while ${genderSubjectPronoun} is in a tabletop position, they will turn the head to the right and then to the left ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: ` through full range of motion, looking to see elbows bend for the reflex.` })
        ];
      case 3:
        return [
          new TextRun({ text: `${parentOrPartner} will hold ${genderAdjective} head in hands while ${genderSubjectPronoun} is in a tabletop position, they will turn the head to the right and then to the left for ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: ` through full range of motion, looking to see elbows bend for the reflex. Instruct ${genderObjectAdjective} to consciously suppress the reflex and not let ${genderAdjective} arms bend by making them lock ${genderAdjective} elbows.` })
        ];
      case 4:
        return [
          new TextRun({ text: `${parentOrPartner} will hold ${genderAdjective} head in hands while ${genderSubjectPronoun} is in a tabletop position, they will turn the head to the right and then to the left for ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: ` through full range of motion, looking to see elbows bend for the reflex. Instruct ${genderObjectAdjective} to consciously suppress the reflex and not let ${genderAdjective} arms bend by making them lock ${genderAdjective} elbows.` })
        ];
      case 5:
        return [
          new TextRun({ text: `Lying on stomach, head turned to one side, the arm and leg are extended straight on the side the head is turned towards; arm and leg on other side are flexed. Then lift the head turn to the opposite side; at the same time flex the arm and leg that were extended and extend the opposite arm and leg. Then turn head again back to original position and arms and legs go back to starting points. This is one rep. Do this for ` }),
          new TextRun({ text: "10 reps", bold: true }),
          new TextRun({ text: "." })
        ];
      case 6:
        return [
          new TextRun({ text: `Start by laying on your back. When crunching up bring your knee up and touch it to your elbow on the same side; while doing this your head should be turned away from the arm and leg that are up. Alternate side to side, ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 7:
        return [
          new TextRun({ text: `Lunge forward with one knee, touch elbow on the same side to the knee and extend the arm and leg on the opposite side; turn head towards the extended side. Alternate sides, one lunge to the right and one to the left counts as one rep. Repeat ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 8:
        return [
          new TextRun({ text: `Start with feet shoulder width apart and legs slightly bent with arms at your sides. Step back and behind the other foot with one leg bringing your elbow to touch the knee of your front leg. Extend your opposite arm out and turn head towards the extended side. Then jump across and extend the opposite arm and leg and turn your head in the other direction. Repeat this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 9:
        return [
          new TextRun({ text: `Start with feet shoulder width apart and legs slightly bent with arms at your sides. Step back and behind the other foot with one leg, bringing your opposite elbow to touch the knee of your front leg. Extend your opposite arm out and turn head towards the flexed side. Then jump across and extend the opposite arm and leg and turn your head in the other direction. Repeat this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 10:
        return [
          new TextRun({ text: `Start with feet shoulder width apart and legs slightly bent with arms at your sides. Step back and behind the other foot with one leg, bringing your opposite elbow to touch the knee of your front leg. Extend your opposite arm out and turn head towards the flexed side. Then jump across and extend the opposite arm and leg and turn your head in the other direction. Repeat this ` }),
          new TextRun({ text: "20 times", bold: true }),
          new TextRun({ text: "." })
        ];
      default:
        return [new TextRun({ text: "" })];
    }
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_astnr_1.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_astnr_1.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_astnr_1.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_astnr_1.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_astnr_2.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_astnr_3.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_astnr_4.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_astnr_5.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_astnr_6.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_astnr_7.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_astnr_7.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/7hwt5kl0zp0t6h1r0c73g/Melillo-Primitive-Reflexes-ATNR-Level-1-2.mp4?rlkey=sc8stkm6fusgkss9i0jwnbydr&st=80yy9xii&dl=0",
    2: "https://www.dropbox.com/scl/fi/7hwt5kl0zp0t6h1r0c73g/Melillo-Primitive-Reflexes-ATNR-Level-1-2.mp4?rlkey=sc8stkm6fusgkss9i0jwnbydr&st=80yy9xii&dl=0",
    3: "https://www.dropbox.com/scl/fi/x9hlhqh9wwuvlm4xr1aq2/Melillo-Primitive-Reflexes-ATNR-Level-3-4.mp4?rlkey=6tplsb6okbfnur9xz0jc8abh3&st=4d8h9j55&dl=0",
    4: "https://www.dropbox.com/scl/fi/x9hlhqh9wwuvlm4xr1aq2/Melillo-Primitive-Reflexes-ATNR-Level-3-4.mp4?rlkey=6tplsb6okbfnur9xz0jc8abh3&st=4d8h9j55&dl=0",
    5: "https://www.dropbox.com/scl/fi/h73bdc2ma82nhljr21fby/Melillo-Primitive-Reflexes-ATNR-Level-5.mp4?rlkey=mtll9f38kz8r3hbn2dcyug5cf&st=g62qizgb&dl=0",
    6: "https://www.dropbox.com/scl/fi/k3zbpaxa3tfpangzperyi/Melillo-Primitive-Reflexes-ATNR-Level-6.mp4?rlkey=ykxirgmtargd7ng4tdlp4e9g2&st=zphxdo4w&dl=0",
    7: "https://www.dropbox.com/scl/fi/wl3u1lf5sn58sbh8v41w1/Melillo-Primitive-Reflexes-ATNR-Level-7.mp4?rlkey=v03pcocd09xta8qwrm190fvqw&st=1ezhar56&dl=0",
    8: "https://www.dropbox.com/scl/fi/den69dxm066hxwbxexjke/Melillo-Primitive-Reflexes-ATNR-Level-8.mp4?rlkey=6q0oejlzrubbqkwfcobrz9vnv&st=aud60rrj&dl=0",
    9: "https://www.dropbox.com/scl/fi/6rrceealcw8fpau4m4jym/Melillo-Primitive-Reflexes-ATNR-Level-9-10.mp4?rlkey=12xmyibhoyhyv18pkjpwvgh9l&st=49pfa2w4&dl=0",
    10: "https://www.dropbox.com/scl/fi/6rrceealcw8fpau4m4jym/Melillo-Primitive-Reflexes-ATNR-Level-9-10.mp4?rlkey=12xmyibhoyhyv18pkjpwvgh9l&st=49pfa2w4&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `ASTNR - Level ${level}: `,
        }),
      ],
      style: "Heading3",
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: getDescriptionRuns(level),
      style: "BodyText",
    })
  );

  if (imageUrls[level]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[level]);
    content.push(...imageParagraphs);
  }

  // Video link
  const linkUrl = links[level];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Instruction Video: Level ${level}`,
          style: "HyperlinkStyle",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
      })
    );
  }

  return content;
}

// BABINSKI Function
async function Babinski(level, program, genderAdjective, genderObjectAdjective) {
  const content = [];

  const getDescriptionRuns = (level) => {
    const leftFoot = program === "right" ? "left" : "right";
    const rightFoot = program === "right" ? "right" : "left";
    const leftFootForLeft = program === "left" ? "right" : "left";
    const rightFootForLeft = program === "left" ? "left" : "right";

    switch (level) {
      case 1:
        return [
          new TextRun({ text: `Stroke up using the handle end of a paintbrush on the lateral side and bottom of ${genderAdjective} foot. Do it ` }),
          new TextRun({ text: "20 times", bold: true }),
          new TextRun({ text: ` on the ${leftFoot} foot and then ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: ` on the ${rightFoot} foot.` })
        ];
      case 2:
        return [
          new TextRun({ text: `Stroke up using the handle end of a paintbrush on the lateral side and bottom of ${genderAdjective} foot. Do it ` }),
          new TextRun({ text: "20 times", bold: true }),
          new TextRun({ text: ` on the ${leftFoot} foot and then ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: ` on the ${rightFoot} foot.` })
        ];
      case 3:
        return [
          new TextRun({ text: `Stroke up using the handle end of a paintbrush or a Rezzimax on the lateral side and bottom of ${genderAdjective} foot. Instruct ${genderObjectAdjective} to consciously suppress the reflex by asking them to curl their toes forward. For a ${program} brain program do it ` }),
          new TextRun({ text: "20 times", bold: true }),
          new TextRun({ text: ` on the ${leftFootForLeft} foot and then ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: ` on the ${rightFootForLeft} foot.` })
        ];
      case 4:
        return [
          new TextRun({ text: `Stroke up using the handle end of a paintbrush or a Rezzimax on the lateral side and bottom of ${genderAdjective} foot. Instruct ${genderObjectAdjective} to consciously suppress the reflex by asking them to curl their toes forward. For a ${program} brain program do it ` }),
          new TextRun({ text: "20 times", bold: true }),
          new TextRun({ text: ` on the ${leftFootForLeft} foot and then ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: ` on the ${rightFootForLeft} foot.` })
        ];
      case 5:
        return [
          new TextRun({ text: `Using a tennis ball on the bottom of your foot, you will roll the tennis ball back and forth on the outer edge of the foot. Flex the toes down as you roll the ball forward and extend your toes up as you roll the ball back towards the heel. Repeat this exercise for ` }),
          new TextRun({ text: "10 reps", bold: true }),
          new TextRun({ text: ` on the ${program} foot and ` }),
          new TextRun({ text: "20 reps", bold: true }),
          new TextRun({ text: ` on the ${leftFootForLeft} foot for a ${program} brain program.` })
        ];
      case 6:
        return [
          new TextRun({ text: `With bare feet, stand on the floor and raise up onto the balls of your feet and back down; repeat this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 7:
        return [
          new TextRun({ text: `With bare feet, stand on the floor and raise up onto the balls of your feet and back down; as you lower your heels back down to the floor do a squat and then raise back up on your toes on the way up; repeat ` }),
          new TextRun({ text: "20 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 8:
        return [
          new TextRun({ text: `With bare feet, stand on the floor and raise up onto the balls of your feet and back down; as you lower your heels back down to the floor do a squat and then raise back up on your toes on the way up and jump up. Repeat ` }),
          new TextRun({ text: "20 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 9:
        return [
          new TextRun({ text: `Jump rope for ` }),
          new TextRun({ text: "20 repetitions", bold: true }),
          new TextRun({ text: "." })
        ];
      case 10:
        return [
          new TextRun({ text: `Jump rope for ` }),
          new TextRun({ text: "40 repetitions", bold: true }),
          new TextRun({ text: "." })
        ];
      default:
        return [new TextRun({ text: "" })];
    }
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_babinski_1.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_babinski_1.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_babinski_1.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_babinski_1.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_babinski_2.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_babinski_3.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_babinski_4.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_babinski_5.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_babinski_6.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_babinski_7.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_babinski_8.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_babinski_9.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_babinski_10.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_babinski_10.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/mlgbwtyzvpfwkm63f1c8l/Melillo-Primitive-Reflexes-Babinski-Level-1-2.mp4?rlkey=8pzj19qdyyzt20919m14bo0sv&st=mln99uzx&dl=0",
    2: "https://www.dropbox.com/scl/fi/mlgbwtyzvpfwkm63f1c8l/Melillo-Primitive-Reflexes-Babinski-Level-1-2.mp4?rlkey=8pzj19qdyyzt20919m14bo0sv&st=mln99uzx&dl=0",
    3: "https://www.dropbox.com/scl/fi/sgr0uay272cnitof1h5zz/Melillo-Primitive-Reflexes-Babinski-Level-3-4.mp4?rlkey=qa3d3ns95wu575o4wdb6t4sfq&st=xnty9lj2&dl=0",
    4: "https://www.dropbox.com/scl/fi/sgr0uay272cnitof1h5zz/Melillo-Primitive-Reflexes-Babinski-Level-3-4.mp4?rlkey=qa3d3ns95wu575o4wdb6t4sfq&st=xnty9lj2&dl=0",
    5: "https://www.dropbox.com/scl/fi/vi21ep2q03yvc5q2po8f6/Melillo-Primitive-Reflexes-Babinski-Level-5.mp4?rlkey=5i22o1ne3e2gme2efhdxgsm13&st=p6uc0dmg&dl=0",
    6: "https://www.dropbox.com/scl/fi/in371t5bcjzen6gkj26q7/Melillo-Primitive-Reflexes-Babinski-Level-6.mp4?rlkey=zo0usivdrdj2b7cra2n16z1om&st=6rtx8fia&dl=0",
    7: "https://www.dropbox.com/scl/fi/jjwt4v0oufkye1jcvu936/Melillo-Primitive-Reflexes-Babinski-Level-7.mp4?rlkey=9mykc57nwf1p5nn6gglqvkcbf&st=wlgrqovj&dl=0",
    8: "https://www.dropbox.com/scl/fi/116jus3c40h6brthye3gw/Melillo-Primitive-Reflexes-Babinski-Level-8.mp4?rlkey=cwhu979xoky80xwnatewwmxcx&st=b89896u5&dl=0",
    9: "https://www.dropbox.com/scl/fi/177wx5m7pigtzs6ktxb5k/Melillo-Primitive-Reflexes-Babinski-Level-9-10.mp4?rlkey=9gk7ua46rdev2iiw2mcr5pl11&st=dje2q5f5&dl=0",
    10: "https://www.dropbox.com/scl/fi/177wx5m7pigtzs6ktxb5k/Melillo-Primitive-Reflexes-Babinski-Level-9-10.mp4?rlkey=9gk7ua46rdev2iiw2mcr5pl11&st=dje2q5f5&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Babinski - Level ${level}: `,
        }),
      ],
      style: "Heading3",
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: getDescriptionRuns(level),
      style: "BodyText",
    })
  );

  if (imageUrls[level]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[level]);
    content.push(...imageParagraphs);
  }

  // Video link
  const linkUrl = links[level];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Instruction Video: Level ${level}`,
          style: "HyperlinkStyle",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
      })
    );
  }

  return content;
}

// ROOTING Function
async function Rooting(level, firstName, genderAdjective, genderObjectAdjective) {
  const content = [];

  const getDescriptionRuns = (level) => {
    switch (level) {
      case 1:
        return [
          new TextRun({ text: `Again, with a paint brush (small), stroke from cheek down to corner of mouth and then across both lips ` }),
          new TextRun({ text: "5 x's", bold: true }),
          new TextRun({ text: `; then from Chin toward corner of mouth and then across both lips again ` }),
          new TextRun({ text: "5 x's", bold: true }),
          new TextRun({ text: `; repeat this on the other side of face.` })
        ];
      case 2:
        return [
          new TextRun({ text: `Again, with a paint brush (small), stroke from cheek down to corner of mouth and then across both lips ` }),
          new TextRun({ text: "5 x's", bold: true }),
          new TextRun({ text: `; then from Chin toward corner of mouth and then across both lips again ` }),
          new TextRun({ text: "5 x's", bold: true }),
          new TextRun({ text: `; repeat this on the other side of face.` })
        ];
      case 3:
        return [
          new TextRun({ text: `Again, with a paint brush (small) or a Rezzimax, take a brush stroke from cheek down to corner of mouth and then across both lips ` }),
          new TextRun({ text: "5 x's", bold: true }),
          new TextRun({ text: `; then from Chin toward corner of mouth and across lips again ` }),
          new TextRun({ text: "5 x's", bold: true }),
          new TextRun({ text: `; repeat this on the other side of face. Instruct ${genderObjectAdjective} to consciously suppress the reflex by trying not to let ${genderAdjective} lips or mouth contract in any way.` })
        ];
      case 4:
        return [
          new TextRun({ text: `Again, with a paint brush (small) or a Rezzimax, take a brush stroke from cheek down to corner of mouth and then across both lips ` }),
          new TextRun({ text: "5 x's", bold: true }),
          new TextRun({ text: `; then from Chin toward corner of mouth and across lips again ` }),
          new TextRun({ text: "5 x's", bold: true }),
          new TextRun({ text: `; repeat this on the other side of face. Instruct ${genderObjectAdjective} to consciously suppress the reflex by trying not to let ${genderAdjective} lips or mouth contract in any way.` })
        ];
      case 5:
        return [
          new TextRun({ text: `${firstName} will use a brush to independently stimulate the rooting reflex as done in levels 1-4. Take a brush and stroke from cheek down to corner of mouth and then across both lips ` }),
          new TextRun({ text: "5 x's", bold: true }),
          new TextRun({ text: `; then from Chin toward corner of mouth and across lips again ` }),
          new TextRun({ text: "5 x's", bold: true }),
          new TextRun({ text: `; repeat this on the other side of face.` })
        ];
      case 6:
        return [
          new TextRun({ text: `Using a vibrating toothbrush or Rezzimax, repeat the same motion as the rooting reflex stimulation. Stroke from cheek down to corner of mouth and then across both lips ` }),
          new TextRun({ text: "5 x's", bold: true }),
          new TextRun({ text: `; then from Chin toward corner of mouth and across lips again ` }),
          new TextRun({ text: "5 x's", bold: true }),
          new TextRun({ text: `; repeat this on the other side of face.` })
        ];
      case 7:
        return [
          new TextRun({ text: `Suck lips all the way in and then blow out making a popping noise. Repeat ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 8:
        return [
          new TextRun({ text: `Put thumb in mouth and blow out hard so cheeks puff out, then take thumb out of mouth and open mouth wide while contracting all the muscles around the mouth and upper neck (platysma); repeat this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 9:
        return [
          new TextRun({ text: `Blow up a balloon ` }),
          new TextRun({ text: "5 times", bold: true }),
          new TextRun({ text: `, blowing out as long as you can and keeping a seal on the balloon with your mouth.` })
        ];
      case 10:
        return [
          new TextRun({ text: `Blow up a balloon ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: `, blowing out as long as you can and keeping a seal on the balloon with your mouth.` })
        ];
      default:
        return [new TextRun({ text: "" })];
    }
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_2.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_3.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_4.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_2.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_3.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_4.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_2.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_3.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_4.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_2.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_3.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_4.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_5.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_6.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_rooting_7.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_snout_4.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_snout_5.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_snout_6.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_snout_7.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_snout_8.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_snout_8.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/jy560xcbqllcoxbxt511s/Melillo-Primitive-Reflexes-Rooting-Level-1-2.mp4?rlkey=lwggnfpubhwv17gcpnu2sqdvc&st=ef8jh7d6&dl=0",
    2: "https://www.dropbox.com/scl/fi/jy560xcbqllcoxbxt511s/Melillo-Primitive-Reflexes-Rooting-Level-1-2.mp4?rlkey=lwggnfpubhwv17gcpnu2sqdvc&st=ef8jh7d6&dl=0",
    3: "https://www.dropbox.com/scl/fi/nuap2uojzngf4fxrpsrew/Melillo-Primitive-Reflexes-Rooting-Level-3-4.mp4?rlkey=60b8hris61g6xeuyix8dlnzik&st=dm18mw7i&dl=0",
    4: "https://www.dropbox.com/scl/fi/nuap2uojzngf4fxrpsrew/Melillo-Primitive-Reflexes-Rooting-Level-3-4.mp4?rlkey=60b8hris61g6xeuyix8dlnzik&st=dm18mw7i&dl=0",
    5: "https://www.dropbox.com/scl/fi/kyu34u7nbvaee3oncumjx/Melillo-Primitive-Reflexes-Rooting-Level-5.mp4?rlkey=hpaahn6bjstiu4efizokbu0yh&st=yd39f6vl&dl=0",
    6: "https://www.dropbox.com/scl/fi/4qj4hmy9hlssxz0o54dz6/Melillo-Primitive-Reflexes-Rooting-Level-6.mp4?rlkey=bpjqiog08x1ic5s0o2s4yi674&st=ffn2ln9c&dl=0",
    7: "https://www.dropbox.com/scl/fi/skq1npvn6vy2j26wypin6/Melillo-Primitive-Reflexes-Rooting-Snout-Level-7.mp4?rlkey=ari5ohpsthvdt0nvgl0siq84h&st=hu34k2gy&dl=0",
    8: "https://www.dropbox.com/scl/fi/d40bizkcay5c6rh0p70kt/Melillo-Primitive-Reflexes-Rooting-Snout-Level-8.mp4?rlkey=xht0394x4e5rlnz6ahljqzoi0&st=9e5d4k8w&dl=0",
    9: "https://www.dropbox.com/scl/fi/d1mm5os8rhkn1jptdnwi7/Melillo-Primitive-Reflexes-Rooting-Snout-Level-9-10_v3.mp4?rlkey=ck11ogbi4pqnts06f2x0jtlb4&st=rgxswh9n&dl=0",
    10: "https://www.dropbox.com/scl/fi/d1mm5os8rhkn1jptdnwi7/Melillo-Primitive-Reflexes-Rooting-Snout-Level-9-10_v3.mp4?rlkey=ck11ogbi4pqnts06f2x0jtlb4&st=rgxswh9n&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Rooting - Level ${level}: `,
        }),
      ],
      style: "Heading3",
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: getDescriptionRuns(level),
      style: "BodyText",
    })
  );

  if (imageUrls[level]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[level]);
    content.push(...imageParagraphs);
  }

  // Video link
  const linkUrl = links[level];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Instruction Video: Level ${level}`,
          style: "HyperlinkStyle",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
      })
    );
  }

  return content;
}

// SNOUT Function
async function Snout(level, firstName, genderAdjective, genderObjectAdjective, ageGroup) {
  const content = [];

  const getDescriptionRuns = (level) => {
    const parentOrPartner = ageGroup === "Child" ? "Parent" : "Partner";
    switch (level) {
      case 1:
        return [
          new TextRun({ text: `${parentOrPartner} will press on the Philtrum using the handle of a paint brush (space between the nose and upper lip) for ` }),
          new TextRun({ text: "10 times", bold: true })
        ];
      case 2:
        return [
          new TextRun({ text: `${parentOrPartner} will press on the Philtrum using the handle of a paint brush (space between the nose and upper lip) for ` }),
          new TextRun({ text: "10 times", bold: true })
        ];
      case 3:
        return [
          new TextRun({ text: `${parentOrPartner} will press on the Philtrum using the handle of a paint brush or a Rezzimax (space between the nose and upper lip) for ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: `. Instruct ${genderObjectAdjective} to consciously suppress the reflex by not letting ${genderAdjective} lips contract.` })
        ];
      case 4:
        return [
          new TextRun({ text: `${parentOrPartner} will press on the Philtrum using the handle of a paint brush or a Rezzimax (space between the nose and upper lip) for ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: `. Instruct ${genderObjectAdjective} to consciously suppress the reflex by not letting ${genderAdjective} lips contract.` })
        ];
      case 5:
        return [
          new TextRun({ text: `Press the brush in the center of both lips; ${firstName} should be able to complete this independently for ` }),
          new TextRun({ text: "10 reps", bold: true }),
          new TextRun({ text: "." })
        ];
      case 6:
        return [
          new TextRun({ text: `Using a vibrating toothbrush or Rezzimax, press the vibration tool to the center of the lips in the same fashion as previous levels. Repeat this for ` }),
          new TextRun({ text: "10 reps", bold: true }),
          new TextRun({ text: "." })
        ];
      case 7:
        return [
          new TextRun({ text: `Suck lips all the way in and then blow out making a popping noise. Repeat ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 8:
        return [
          new TextRun({ text: `Put thumb in mouth and blow out hard so cheeks puff out, then take thumb out of mouth and open mouth wide while contracting all the muscles around the mouth and upper neck (platysma); repeat this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 9:
        return [
          new TextRun({ text: `Blow up a balloon ` }),
          new TextRun({ text: "5 times", bold: true }),
          new TextRun({ text: `, blowing out as long as you can and keeping a seal on the balloon with your mouth.` })
        ];
      case 10:
        return [
          new TextRun({ text: `Blow up a balloon ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: `, blowing out as long as you can and keeping a seal on the balloon with your mouth.` })
        ];
      default:
        return [new TextRun({ text: "" })];
    }
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_snout_1.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_snout_1.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_snout_1.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_snout_1.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_snout_2.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_snout_3.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_snout_4.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_snout_5.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_snout_6.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_snout_7.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_snout_8.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_snout_8.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/6w1u4o8o340bl44nymqkt/Melillo-Primitive-Reflexes-Snout-Level-1-2.mp4?rlkey=a9o2mtxy000jec83bs8p18jy6&st=oyws8npw&dl=0",
    2: "https://www.dropbox.com/scl/fi/6w1u4o8o340bl44nymqkt/Melillo-Primitive-Reflexes-Snout-Level-1-2.mp4?rlkey=a9o2mtxy000jec83bs8p18jy6&st=oyws8npw&dl=0",
    3: "https://www.dropbox.com/scl/fi/sol2ddrcdaq0lips4nj94/Melillo-Primitive-Reflexes-Snout-Level-3-4.mp4?rlkey=9qe5haucvgjws507w50mfdk3l&st=ekhvfi5p&dl=0",
    4: "https://www.dropbox.com/scl/fi/sol2ddrcdaq0lips4nj94/Melillo-Primitive-Reflexes-Snout-Level-3-4.mp4?rlkey=9qe5haucvgjws507w50mfdk3l&st=ekhvfi5p&dl=0",
    5: "https://www.dropbox.com/scl/fi/drizxsdsfvx6w1lc8thgt/Melillo-Primitive-Reflexes-Snout-Level-5.mp4?rlkey=kkjyxo0ce42fuhb35h9lq3obv&st=kse5pb5m&dl=0",
    6: "https://www.dropbox.com/scl/fi/qwnvhvthi7yg1q4ymmrmh/Melillo-Primitive-Reflexes-Snout-Level-6.mp4?rlkey=qffbg9irx01l27awnbxm0j2cc&st=oyh7t88e&dl=0",
    7: "https://www.dropbox.com/scl/fi/skq1npvn6vy2j26wypin6/Melillo-Primitive-Reflexes-Rooting-Snout-Level-7.mp4?rlkey=ari5ohpsthvdt0nvgl0siq84h&st=hu34k2gy&dl=0",
    8: "https://www.dropbox.com/scl/fi/d40bizkcay5c6rh0p70kt/Melillo-Primitive-Reflexes-Rooting-Snout-Level-8.mp4?rlkey=xht0394x4e5rlnz6ahljqzoi0&st=9e5d4k8w&dl=0",
    9: "https://www.dropbox.com/scl/fi/d1mm5os8rhkn1jptdnwi7/Melillo-Primitive-Reflexes-Rooting-Snout-Level-9-10_v3.mp4?rlkey=ck11ogbi4pqnts06f2x0jtlb4&st=rgxswh9n&dl=0",
    10: "https://www.dropbox.com/scl/fi/d1mm5os8rhkn1jptdnwi7/Melillo-Primitive-Reflexes-Rooting-Snout-Level-9-10_v3.mp4?rlkey=ck11ogbi4pqnts06f2x0jtlb4&st=rgxswh9n&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Snout - Level ${level}: `,
        }),
      ],
      style: "Heading3",
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: getDescriptionRuns(level),
      style: "BodyText",
    })
  );

  if (imageUrls[level]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[level]);
    content.push(...imageParagraphs);
  }

  // Video link
  const linkUrl = links[level];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Instruction Video: Level ${level}`,
          style: "HyperlinkStyle",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
      })
    );
  }

  return content;
}

// SPINAL_GALANT Function
async function SpinalGalant(level, firstName, genderObjectAdjective, genderAdjective) {
  const content = [];

  const getDescriptionRuns = (level) => {
    switch (level) {
      case 1:
        return [
          new TextRun({ text: `${firstName} will start on hands and knees in a tabletop position. Take a thinner brush and stroke from mid-back down on either side of the spine. Start closer to the spine, working your way out lateral to the sides, completing ` }),
          new TextRun({ text: "10 reps", bold: true }),
          new TextRun({ text: `. Doing it once for each side is considered 1 rep.` })
        ];
      case 2:
        return [
          new TextRun({ text: `${firstName} will start on hands and knees in a tabletop position. Take a thinner brush and stroke from mid-back down on either side of the spine. Start closer to the spine, working your way out lateral to the sides, completing ` }),
          new TextRun({ text: "10 reps", bold: true }),
          new TextRun({ text: `. Doing it once for each side is considered 1 rep.` })
        ];
      case 3:
        return [
          new TextRun({ text: `${firstName} will start on hands and knees in a tabletop position. Take a thinner brush or a Rezzimax and stroke from mid-back down on either side of the spine. Start closer to the spine, working your way out lateral to the sides, completing ` }),
          new TextRun({ text: "10 reps", bold: true }),
          new TextRun({ text: `. Doing it once for each side is considered 1 rep. Instruct ${genderObjectAdjective} to consciously suppress the reflex by not having ${genderAdjective} back twitch or flex.` })
        ];
      case 4:
        return [
          new TextRun({ text: `${firstName} will start on hands and knees in a tabletop position. Take a thinner brush or a Rezzimax and stroke from mid-back down on either side of the spine. Start closer to the spine, working your way out lateral to the sides, completing ` }),
          new TextRun({ text: "10 reps", bold: true }),
          new TextRun({ text: `. Doing it once for each side is considered 1 rep. Instruct ${genderObjectAdjective} to consciously suppress the reflex by not having ${genderAdjective} back twitch or flex.` })
        ];
      case 5:
        return [
          new TextRun({ text: `(Angels in Snow) Lying on the back, start with arms at the side and legs together. Then, opening arms and legs together until hands touch at the top and legs are spread as far as possible apart at the same time. Then go back to original position with hands next to body and legs together, that is 1 rep. Do this for ` }),
          new TextRun({ text: "10 reps", bold: true }),
          new TextRun({ text: "." })
        ];
      case 6:
        return [
          new TextRun({ text: `In a side plank position with both arms and both legs on the ground, raise core up and back down. Repeat ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: ` on both sides.` })
        ];
      case 7:
        return [
          new TextRun({ text: `Starting position is a side plank with feet stacked on top of one another with both hands on the ground. As you raise your torso off the ground, raise the top leg off the other. Repeat ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: ` on each side.` })
        ];
      case 8:
        return [
          new TextRun({ text: `In a standing position, do side crunches, alternating side to side, bringing the knee up to touch your elbow. Repeat this ` }),
          new TextRun({ text: "20 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 9:
        return [
          new TextRun({ text: `Do ` }),
          new TextRun({ text: "20 jumping jacks", bold: true }),
          new TextRun({ text: `; be sure to move your arms and legs at the same pace.` })
        ];
      case 10:
        return [
          new TextRun({ text: `Do ` }),
          new TextRun({ text: "40 jumping jacks", bold: true }),
          new TextRun({ text: `; be sure to move your arms and legs at the same pace.` })
        ];
      default:
        return [new TextRun({ text: "" })];
    }
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_spinal_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_spinal_2.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_spinal_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_spinal_2.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_spinal_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_spinal_2.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_spinal_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_spinal_2.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_spinal_3.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_spinal_4.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_spinal_5.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_spinal_6.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_spinal_7.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_spinal_8.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_spinal_9.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_spinal_10.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_spinal_9.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_spinal_10.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/kibe28cid6mr1gij9y9am/Melillo-Primitive-Reflexes-Spinal-Galant-Level-1-2.mp4?rlkey=hhy6nt79l2le62m8cv9p8uapw&st=9jvvspay&dl=0",
    2: "https://www.dropbox.com/scl/fi/kibe28cid6mr1gij9y9am/Melillo-Primitive-Reflexes-Spinal-Galant-Level-1-2.mp4?rlkey=hhy6nt79l2le62m8cv9p8uapw&st=9jvvspay&dl=0",
    3: "https://www.dropbox.com/scl/fi/pynp54lhp59822mq1s3ui/Melillo-Primitive-Reflexes-Spinal-Galant-Level-3-4.mp4?rlkey=lphh0bblah5b2f35k9obhxoyy&st=2r437bly&dl=0",
    4: "https://www.dropbox.com/scl/fi/pynp54lhp59822mq1s3ui/Melillo-Primitive-Reflexes-Spinal-Galant-Level-3-4.mp4?rlkey=lphh0bblah5b2f35k9obhxoyy&st=2r437bly&dl=0",
    5: "https://www.dropbox.com/scl/fi/cz0untwa641ecsiu4gx7c/Melillo-Primitive-Reflexes-Spinal-Galant-Level-5.mp4?rlkey=t5v1pcyoe7dop5boxo43fjv5d&st=i091g6ql&dl=0",
    6: "https://www.dropbox.com/scl/fi/q5nqo6mdsvenxa2ivm418/Melillo-Primitive-Reflexes-Spinal-Galant-Level-6.mp4?rlkey=f6go6nr8ypyjcsax97rvbnkgz&st=g4fx13ew&dl=0",
    7: "https://www.dropbox.com/scl/fi/1vvcq7ay8tp0wdv5flh3g/Melillo-Primitive-Reflexes-Spinal-Galant-Level-7.mp4?rlkey=u5strcsp0xae8pqw3kcuw8jwu&st=kydjpxkk&dl=0",
    8: "https://www.dropbox.com/scl/fi/u37k9wl24h33buupjwqph/Melillo-Primitive-Reflexes-Spinal-Galant-Level-8.mp4?rlkey=1dbpti2v5viz6zuw1xitth9tx&st=y00mam3f&dl=0",
    9: "https://www.dropbox.com/scl/fi/9brkhntyuej4puvqiv1fl/Melillo-Primitive-Reflexes-Spinal-Galant-Level-9-10.mp4?rlkey=o6gmopur8a496uccg0f3tqq68&st=dh6yopz1&dl=0",
    10: "https://www.dropbox.com/scl/fi/9brkhntyuej4puvqiv1fl/Melillo-Primitive-Reflexes-Spinal-Galant-Level-9-10.mp4?rlkey=o6gmopur8a496uccg0f3tqq68&st=dh6yopz1&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Spinal Galant - Level ${level}: `,
        }),
      ],
      style: "Heading3",
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: getDescriptionRuns(level),
      style: "BodyText",
    })
  );

  if (imageUrls[level]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[level]);
    content.push(...imageParagraphs);
  }

  // Video link
  const linkUrl = links[level];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Instruction Video: Level ${level}`,
          style: "HyperlinkStyle",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
      })
    );
  }

  return content;
}

// STNR Function
async function Stnr(
  level,
  firstName,
  genderAdjective,
  ageGroup,
  genderObjectAdjective
) {
  const content = [];

  const getDescriptionRuns = (level) => {
    const parentOrPartner = ageGroup === "Child" ? "Parent" : "Partner";
    switch (level) {
      case 1:
        return [
          new TextRun({ text: `${firstName} will be on ${genderAdjective} hands and knees and ${parentOrPartner} will hold ${genderObjectAdjective} under the chin and on the crown of the head and then move ${genderAdjective} head up and down quickly through full range ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 2:
        return [
          new TextRun({ text: `${firstName} will be on ${genderAdjective} hands and knees and ${parentOrPartner} will hold ${genderObjectAdjective} under the chin and on the crown of the head and then move ${genderAdjective} head up and down quickly through full range ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 3:
        return [
          new TextRun({ text: `${firstName} will be on ${genderAdjective} hands and knees and ${parentOrPartner} will hold ${genderObjectAdjective} under the chin and on the crown of the head, and then move ${genderAdjective} head up and down quickly through full range ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: `. Instruct ${genderObjectAdjective} to consciously suppress the reflex by not letting ${genderAdjective} arms bend as you bring ${genderAdjective} head down, or shift backwards as you bring ${genderAdjective} head up.` })
        ];
      case 4:
        return [
          new TextRun({ text: `${firstName} will be on ${genderAdjective} hands and knees and ${parentOrPartner} will hold ${genderObjectAdjective} under the chin and on the crown of the head, and then move ${genderAdjective} head up and down quickly through full range ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: `. Instruct ${genderObjectAdjective} to consciously suppress the reflex by not letting ${genderAdjective} arms bend as you bring ${genderAdjective} head down, or shift backwards as you bring ${genderAdjective} head up.` })
        ];
      case 5:
        return [
          new TextRun({ text: `(Cat Stretch) On hands and knees in a tabletop position, move the head all of the way up until neck is fully extended while arching the lower back down at the same time; then flex the neck down with the chin close to chest and eyes looking at knees while arching the back up like a cat. Complete through the full range up and down for ` }),
          new TextRun({ text: "10 reps", bold: true }),
          new TextRun({ text: "." })
        ];
      case 6:
        return [
          new TextRun({ text: `Start by laying on the floor with legs flexed into a cannonball position, hold knees in flexion with both arms. Roll back and forth. Move the head up when rolling forward and down when rolling back; repeat this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 7:
        return [
          new TextRun({ text: `Starting position is crouched down with knees bent and hands on the floor in front of you with head flexed down. Jump out into a plank position and extend the head at the same time. Then jump back into the starting position and flex the head at the same time. Repeat ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 8:
        return [
          new TextRun({ text: `Start in a standing position, squat down so hands are flat on the ground and flex head forward(down), then jump out into a plank position and extend head back(up); repeat this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 9:
        return [
          new TextRun({ text: `Start in a standing position, squat down so hands are flat on the ground and flex head forward, then jump out into a plank position and extend head back, while in this position do a push up, then jump back into a crouch position and jump up, extending the head back as you do this. Repeat this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 10:
        return [
          new TextRun({ text: `Start in a standing position, squat down so hands are flat on the ground and flex head forward, then jump out into a plank position and extend head back, while in this position do a push up, then jump back into a crouch position and jump up, extending the head back as you do this. Repeat this ` }),
          new TextRun({ text: "20 times", bold: true }),
          new TextRun({ text: "." })
        ];
      default:
        return [new TextRun({ text: "" })];
    }
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_2.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_2.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_2.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_2.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_3.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_4.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_5.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_6.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_7.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_8.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_9.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_10.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_11.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_12.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_13.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_14.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_11.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_12.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_13.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_stnr_14.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/1q64c72oatvqsh978q4lp/Melillo-Primitive-Reflexes-STNR-Level-1-2.mp4?rlkey=eu82xo7asp0pe3r71mfgv4an0&st=rc3xlzln&dl=0",
    2: "https://www.dropbox.com/scl/fi/1q64c72oatvqsh978q4lp/Melillo-Primitive-Reflexes-STNR-Level-1-2.mp4?rlkey=eu82xo7asp0pe3r71mfgv4an0&st=rc3xlzln&dl=0",
    3: "https://www.dropbox.com/scl/fi/i2d09rnwn8yn4wj374aba/Melillo-Primitive-Reflexes-STNR-Level-3-4.mp4?rlkey=ir71y9xi4buhbtoqauusd2529&st=bk222wnw&dl=0",
    4: "https://www.dropbox.com/scl/fi/i2d09rnwn8yn4wj374aba/Melillo-Primitive-Reflexes-STNR-Level-3-4.mp4?rlkey=ir71y9xi4buhbtoqauusd2529&st=bk222wnw&dl=0",
    5: "https://www.dropbox.com/scl/fi/j5ql4w9cq1lvrb3yjf8pz/Melillo-Primitive-Reflexes-STNR-Level-5.mp4?rlkey=qnca348ai4q413erd4rvjqed5&st=jwc1goji&dl=0",
    6: "https://www.dropbox.com/scl/fi/40hvv9imnqbqtuf0u7o46/Melillo-Primitive-Reflexes-STNR-Level-6.mp4?rlkey=ob0gurprm1gk5jmizfuosn4cw&st=k7dvx8xy&dl=0",
    7: "https://www.dropbox.com/scl/fi/b9f39uckmnrbavtsali02/Melillo-Primitive-Reflexes-STNR-Level-7.mp4?rlkey=tukbzi12bm4ywfbdwgw102yn4&st=u88hmxuf&dl=0",
    8: "https://www.dropbox.com/scl/fi/qpjuc3wa4vxz03rs75ex8/Melillo-Primitive-Reflexes-STNR-Level-8_v3.mp4?rlkey=tmxr5kgfisabr03r3apsil8e6&st=8frhbm7s&dl=0",
    9: "https://www.dropbox.com/scl/fi/wle1nsvmgj966116r5x46/Melillo-Primitive-Reflexes-STNR-Level-9-10.mp4?rlkey=dq9wp5qhyloapjf7k2xhr5j74&st=28bfs5fg&dl=0",
    10: "https://www.dropbox.com/scl/fi/wle1nsvmgj966116r5x46/Melillo-Primitive-Reflexes-STNR-Level-9-10.mp4?rlkey=dq9wp5qhyloapjf7k2xhr5j74&st=28bfs5fg&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Symmetric Tonic Neck - Level ${level}: `,
        }),
      ],
      style: "Heading3",
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: getDescriptionRuns(level),
      style: "BodyText",
    })
  );

  if (imageUrls[level]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[level]);
    content.push(...imageParagraphs);
  }

  // Video link
  const linkUrl = links[level];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Instruction Video: Level ${level}`,
          style: "HyperlinkStyle",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
      })
    );
  }

  return content;
}

// PALMER_GRASP Function
async function PalmerGrasp(
  level,
  firstName,
  ageGroup,
  genderAdjective,
  genderObjectAdjective,
  program
) {
  const content = [];

  const getDescriptionRuns = (level) => {
    const parentOrPartner = ageGroup === "Child" ? "Parent" : "Partner";
    const leftHand = program === "right" ? "left" : "right";
    const rightHand = program === "right" ? "right" : "left";
    const leftHandForLeft = program === "left" ? "right" : "left";
    const rightHandForLeft = program === "left" ? "left" : "right";

    switch (level) {
      case 1:
        return [
          new TextRun({ text: `Using the handle of a paint brush, ${parentOrPartner} will stimulate the reflex by drawing a "X" on the palm of ${genderAdjective} hand. Do ` }),
          new TextRun({ text: "20 times", bold: true }),
          new TextRun({ text: ` on the ${leftHand} hand and then ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: ` on ${genderAdjective} ${rightHand} hand.` })
        ];
      case 2:
        return [
          new TextRun({ text: `Using the handle of a paint brush, ${parentOrPartner} will stimulate the reflex by drawing a "X" on the palm of ${genderAdjective} hand. Do ` }),
          new TextRun({ text: "20 times", bold: true }),
          new TextRun({ text: ` on the ${leftHand} hand and then ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: ` on ${genderAdjective} ${rightHand} hand.` })
        ];
      case 3:
        return [
          new TextRun({ text: `Using a Rezzimax or a handle of a paint brush, ${parentOrPartner} will stimulate the reflex by drawing a "X" on the palm of the hand. Instruct ${genderObjectAdjective} to consciously suppress the reflex by not letting ${genderAdjective} hands flex. For a ${program} brain program, this will be ` }),
          new TextRun({ text: "20 times", bold: true }),
          new TextRun({ text: ` on the ${leftHandForLeft} hand and then ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: ` on the ${rightHandForLeft} hand.` })
        ];
      case 4:
        return [
          new TextRun({ text: `Using a Rezzimax or a handle of a paint brush, ${parentOrPartner} will stimulate the reflex by drawing a "X" on the palm of the hand. Instruct ${genderObjectAdjective} to consciously suppress the reflex by not letting ${genderAdjective} hands flex. For a ${program} brain program, this will be ` }),
          new TextRun({ text: "20 times", bold: true }),
          new TextRun({ text: ` on the ${leftHandForLeft} hand and then ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: ` on the ${rightHandForLeft} hand.` })
        ];
      case 5:
        return [
          new TextRun({ text: `Using a tennis ball, you will squeeze the ball in your hand. For a ${program} brain program, you will do this for ` }),
          new TextRun({ text: "20 times", bold: true }),
          new TextRun({ text: ` in the ${leftHandForLeft} hand and ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: ` with the ${rightHandForLeft} hand.` })
        ];
      case 6:
        return [
          new TextRun({ text: `Squeeze a hand gripper; once hand is completely flexed hold and resist the gripper from opening back up; repeat ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 7:
        return [
          new TextRun({ text: `Using a rubber band or rubber band extensors, extend fingers and thumb until hand is completely open, the rubber band will create resistance to this motion. Repeat ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: ` on each hand.` })
        ];
      case 8:
        return [
          new TextRun({ text: `Start in a standing position, lean forward and touch the floor so your palms are flat on the ground, walk forward on your hands into a plank position, then walk back and stand back up. Repeat this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 9:
        return [
          new TextRun({ text: `Start in a standing position, lean forward and touch the floor so your palms are flat on the ground, walk forward on your hands into a plank position, do a push up, then walk back and stand back up. Repeat this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 10:
        return [
          new TextRun({ text: `Start in a standing position, lean forward and touch the floor so your palms are flat on the ground, walk forward on your hands into a plank position, do a push up, then walk back and stand back up. Repeat this ` }),
          new TextRun({ text: "20 times", bold: true }),
          new TextRun({ text: "." })
        ];
      default:
        return [new TextRun({ text: "" })];
    }
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_palmer_1.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_palmer_1.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_palmer_1.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_palmer_1.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_palmer_2.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_palmer_3.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_palmer_4.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_palmer_5.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_palmer_6.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_palmer_5.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_palmer_6.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_palmer_7.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_palmer_5.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_palmer_6.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_palmer_7.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/sbe167z5ohnxscz9pay8n/Melillo-Primitive-Reflexes-Palmer-Grasp-Level-1-2.mp4?rlkey=ef64mkve4u6tbpnw9fy5hxdmu&st=bsaj3kdy&dl=0",
    2: "https://www.dropbox.com/scl/fi/sbe167z5ohnxscz9pay8n/Melillo-Primitive-Reflexes-Palmer-Grasp-Level-1-2.mp4?rlkey=ef64mkve4u6tbpnw9fy5hxdmu&st=bsaj3kdy&dl=0",
    3: "https://www.dropbox.com/scl/fi/tci0zpzhbyxuufbyd0lkh/Melillo-Primitive-Reflexes-Palmer-Grasp-Level-3-4.mp4?rlkey=q5n24ergr4mt73cfcpwf9hfsf&st=qwdazpk7&dl=0",
    4: "https://www.dropbox.com/scl/fi/tci0zpzhbyxuufbyd0lkh/Melillo-Primitive-Reflexes-Palmer-Grasp-Level-3-4.mp4?rlkey=q5n24ergr4mt73cfcpwf9hfsf&st=qwdazpk7&dl=0",
    5: "https://www.dropbox.com/scl/fi/dmovdk7cj9dtkfiaqacay/Melillo-Primitive-Reflexes-Palmer-Grasp-Level-5.mp4?rlkey=7swe749f8qr67x7a1xxmrtz92&st=spnqb303&dl=0",
    6: "https://www.dropbox.com/scl/fi/bhq5wc9sloe358r0xzypy/Melillo-Primitive-Reflexes-Palmer-Grasp-Level-6.mp4?rlkey=lygp7srunmfmmhcspez30t5nd&st=oi23qcsd&dl=0",
    7: "https://www.dropbox.com/scl/fi/wm6nz13al7ryy6z3slg75/Melillo-Primitive-Reflexes-Palmer-Grasp-Level-7.mp4?rlkey=387yz7tmxk31uk5u00u2vulgn&st=nd310unc&dl=0",
    8: "https://www.dropbox.com/scl/fi/26oz7g4ivevz9lbtindkb/Melillo-Primitive-Reflexes-Palmer-Grasp-Level-8.mp4?rlkey=2qh9jvxad1ul55qttfq1mbqm7&st=45aptgtq&dl=0",
    9: "https://www.dropbox.com/scl/fi/fclznlbsugak0uujnyenz/Melillo-Primitive-Reflexes-Palmer-Grasp-Level-9-10.mp4?rlkey=7wz0dlczhzqlecanmb87wl9s9&st=j4cg9l5s&dl=0",
    10: "https://www.dropbox.com/scl/fi/fclznlbsugak0uujnyenz/Melillo-Primitive-Reflexes-Palmer-Grasp-Level-9-10.mp4?rlkey=7wz0dlczhzqlecanmb87wl9s9&st=j4cg9l5s&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Palmer Grasp - Level ${level}: `,
        }),
      ],
      style: "Heading3",
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: getDescriptionRuns(level),
      style: "BodyText",
    })
  );

  if (imageUrls[level]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[level]);
    content.push(...imageParagraphs);
  }

  // Video link
  const linkUrl = links[level];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Instruction Video: Level ${level}`,
          style: "HyperlinkStyle",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
      })
    );
  }

  return content;
}

// MORO Function
async function Moro(level, firstName, genderAdjective, genderObjectAdjective) {
  const content = [];

  const getDescriptionRuns = (level) => {
    switch (level) {
      case 1:
        return [
          new TextRun({ text: `${firstName} will start by lying flat on ${genderAdjective} back, with the hands on either side. Grab the sides of the head, then flex the neck all the way up and do a quick drop back, not letting the head hit the ground. Do this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 2:
        return [
          new TextRun({ text: `${firstName} will start by lying flat on ${genderAdjective} back, with the hands on either side. Grab the sides of the head, then flex the neck all the way up and do a quick drop back, not letting the head hit the ground. Do this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 3:
        return [
          new TextRun({ text: `${firstName} will be lying flat on back, with the hands on either side. Kneel or sit behind and hold the sides of the head.  Flex the neck all the way up and do a quick drop back, not letting the head hit the ground. Instruct ${genderObjectAdjective} to consciously suppress the reflex by not letting ${genderAdjective} arms and legs twitch. Do this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 4:
        return [
          new TextRun({ text: `${firstName} will be lying flat on back, with the hands on either side. Kneel or sit behind and hold the sides of the head.  Flex the neck all the way up and do a quick drop back, not letting the head hit the ground. Instruct ${genderObjectAdjective} to consciously suppress the reflex by not letting ${genderAdjective} arms and legs twitch. Do this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 5:
        return [
          new TextRun({ text: `(Starfish) Sitting on chair, start from fetal position, the right wrist over left wrist and the right ankle over left (crisscrossed). Open all the way up, extending head all the way back with arms, hands and legs stretched out to full extension, then return to the starting position but this time crossing left wrist over right and left ankle over right. Doing this on each side is considered 1 rep. Repeat and do this for a total of ` }),
          new TextRun({ text: "10 reps", bold: true }),
          new TextRun({ text: "." })
        ];
      case 6:
        return [
          new TextRun({ text: `Start laying on your back in a crunch position with arms and legs crossed on the same side. As you move into extension from a crunched position, extend both arms and legs out in a starfish position. When flexing back into a crunched position, cross your arms and legs in the opposite direction. Repeat this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: `, alternating the direction your arms and legs are crossed each time.` })
        ];
      case 7:
        return [
          new TextRun({ text: `Start laying on your back in a crunch position with arms and legs crossed on the same side. As you move into extension from a crunched position, extend both arms and legs out in a starfish position. Once fully extended, roll onto your stomach and then back onto your back before flexing back into a crunch position. When flexing back into a crunched position, cross your arms and legs in the opposite direction that they started in. Repeat this ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: ` on each side, alternating the direction your arms and legs are crossed and the side you are rolling to.` })
        ];
      case 8:
        return [
          new TextRun({ text: `Squat down with arms crossed and fists touching the ground, head flexed. As you stand up, extend arms out and head back, then squat back down, cross arms in front of you and flex your head forward. Repeat ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 9:
        return [
          new TextRun({ text: `Squat down with arms crossed and fists touching the ground, head flexed. Then jump up, extend arms out and head back, then squat back down, cross arms in front of you and flex your head forward. Repeat ` }),
          new TextRun({ text: "10 times", bold: true }),
          new TextRun({ text: "." })
        ];
      case 10:
        return [
          new TextRun({ text: `Squat down with arms crossed and fists touching the ground, head flexed. Then jump up, extend arms out and head back, then squat back down, cross arms in front of you and flex your head forward. Repeat ` }),
          new TextRun({ text: "20 times", bold: true }),
          new TextRun({ text: "." })
        ];
      default:
        return [new TextRun({ text: "" })];
    }
  };

  const imageUrls = {
    1: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_2.jpeg",
    ],
    2: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_2.jpeg",
    ],
    3: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_2.jpeg",
    ],
    4: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_tlr_2.jpeg",
    ],
    5: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_moro_1.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_moro_2.jpeg",
    ],
    6: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_moro_4.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_moro_5.jpeg",
    ],
    7: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_moro_6.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_moro_7.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_moro_8.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_moro_9.jpeg",
    ],
    8: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_palmer_5.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_palmer_6.jpeg",
    ],
    9: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_moro_10.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_moro_11.jpeg",
    ],
    10: [
      "/user-forms/doctor-assessment/exercise-images/pr/pr_moro_12.jpeg",
      "/user-forms/doctor-assessment/exercise-images/pr/pr_moro_13.jpeg",
    ],
  };

  const links = {
    1: "https://www.dropbox.com/scl/fi/uf3b9k962k4ojfl0a7zue/Melillo-Primitive-Reflexes-Moro-Level-1-2.mp4?rlkey=op54oa8fm341m09zhyoskejww&st=l4xoy8pg&dl=0",
    2: "https://www.dropbox.com/scl/fi/uf3b9k962k4ojfl0a7zue/Melillo-Primitive-Reflexes-Moro-Level-1-2.mp4?rlkey=op54oa8fm341m09zhyoskejww&st=l4xoy8pg&dl=0",
    3: "https://www.dropbox.com/scl/fi/uzmidakp3zssgnl9lnpcp/Melillo-Primitive-Reflexes-Moro-Level-3-4_v3.mp4?rlkey=bkztxfvnqxzn5apap98s8jejz&st=77bz4w5y&dl=0",
    4: "https://www.dropbox.com/scl/fi/uzmidakp3zssgnl9lnpcp/Melillo-Primitive-Reflexes-Moro-Level-3-4_v3.mp4?rlkey=bkztxfvnqxzn5apap98s8jejz&st=77bz4w5y&dl=0",
    5: "https://www.dropbox.com/scl/fi/wlulyez2r47mdr5bpsyl6/Melillo-Primitive-Reflexes-Moro-Level-5_v3.mp4?rlkey=v5w4wl86ppifk0qbo3th5858a&st=emqdathh&dl=0",
    6: "https://www.dropbox.com/scl/fi/v3w6p23x8oq8o0x5lg444/Melillo-Primitive-Reflexes-Moro-Level-6_v3.mp4?rlkey=wmrk3rx84u8w8jp3jy4pfnfko&st=7lkqnllh&dl=0",
    7: "https://www.dropbox.com/scl/fi/nrkxihpo2iyb8weh4hzgw/Melillo-Primitive-Reflexes-Moro-Level-7_v3.mp4?rlkey=a8eogzporjm4viqv7z6pd3y0b&st=h6op25s3&dl=0",
    8: "https://www.dropbox.com/scl/fi/qytvcms0gd2xyctfgukre/Melillo-Primitive-Reflexes-Moro-Level-8_v3.mp4?rlkey=fajo8lc5jh4328r6wj8zi3cqc&st=3h63mfak&dl=0",
    9: "https://www.dropbox.com/scl/fi/0kus4ri62wpt0dscmjxqw/Melillo-Primitive-Reflexes-Moro-Level-9-10_v3.mp4?rlkey=cnhgjf3uv4lv7vj27ky5htchg&st=fxfg5ssk&dl=0",
    10: "https://www.dropbox.com/scl/fi/0kus4ri62wpt0dscmjxqw/Melillo-Primitive-Reflexes-Moro-Level-9-10_v3.mp4?rlkey=cnhgjf3uv4lv7vj27ky5htchg&st=fxfg5ssk&dl=0",
  };

  // Title
  content.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Moro - Level ${level}: `,
        }),
      ],
      style: "Heading3",
    })
  );

  // Description
  content.push(
    new Paragraph({
      children: getDescriptionRuns(level),
      style: "BodyText",
    })
  );

  if (imageUrls[level]) {
    const imageParagraphs = await createImageParagraphs(imageUrls[level]);
    content.push(...imageParagraphs);
  }

  // Video link
  const linkUrl = links[level];
  if (linkUrl) {
    const hyperlink = new ExternalHyperlink({
      children: [
        new TextRun({
          text: `Instruction Video: Level ${level}`,
          style: "HyperlinkStyle",
        }),
      ],
      link: linkUrl,
    });

    content.push(
      new Paragraph({
        children: [hyperlink],
        style: "HyperlinkStyle",
      })
    );
  }

  return content;
}
export default StimFormReportDocx;